import {
  init,
  dispose,
  ActionType,
  DomPosition,
  FormatDateType,
  utils,
  type Chart,
  type Crosshair,
  type Indicator,
  type IndicatorCreate,
  type IndicatorStyle,
  type OverlayEvent,
  type Coordinate,
} from 'klinecharts'
import type { DataFeed } from '../types/datafeed'
import { completeCalcParams } from '@edge/charts-indicators'
import { withAlphaOrKeep } from '../utils/color'
import { computeRubberBandZoom, MIN_DRAG_PX } from '../utils/rubberBandZoom'

/** klinecharts fill-style block, shared by the `rect`/`polygon`/`circle` keys. */
interface OverlayFillStyle {
  /** Absent on the empty fill that clears a shape's turned-off Background. */
  style?: string
  color: string
  borderColor?: string
}

/** Vertical offset for a duplicated drawing, as a fraction of the visible
 *  price span. Small enough to stay near the original, large enough to read as
 *  a separate object at any zoom. */
const DUPLICATE_DROP_FRACTION = 0.04

/** zLevel for the session/range background shading. Any negative zLevel makes
 *  klinecharts composite the indicator with `destination-over` (behind the
 *  candles). But among the negative-zLevel indicators the compositing is
 *  INVERTED: whatever draws LATER lands FURTHER BACK, and klinecharts draws them
 *  in ascending zLevel order — so the indicator with the HIGHEST (closest-to-0)
 *  negative zLevel ends up at the very back. The shadings are pure background, so
 *  they must sit behind every other -1 indicator (volume profiles, TPO, HTF, VOL
 *  overlay). Keeping them at -1 left the stacking order dependent on indicator
 *  insertion order, which is why the volume profile could fall behind the shading.
 *  -0.5 is > -1 yet still < 0: drawn last among the negatives → deterministically
 *  the backmost layer. Do NOT "fix" this to -2 — that would draw it FIRST and pull
 *  the shading in FRONT of the profiles, re-breaking the bug. */
const BACKGROUND_SHADING_Z_LEVEL = -0.5
import type {
  EdgeSymbol,
  EdgeBar,
  EdgeChartApi,
  OverlayModeName,
  EdgeTheme,
  CrosshairSyncData,
  ViewportSyncData,
} from '../types/chart'
import { snapValueToBar } from '../utils/magnetSnap'
import type { ChartSettings } from '../components/SettingsModal'
import type { Period, PeriodKey } from '../utils/resolution'
import { PERIODS, periodKeyOf, alignTimestamp, isIntradayPeriod } from '../utils/resolution'
import { ZOOM_LEVEL_MULTIPLIER, zoomCategoryOf, type ResetZoomPolicy } from '../types/resetZoom'
import { resolveResetRange } from '../utils/resetZoomRange'
import type { OverlaySyncEvent } from '../utils/overlaySync'
import type { OverlayStyleOverride } from '../types/persistence'
import { resolveOverlayDefault } from '../utils/overlayUserDefaults'
import { EventBus, LAYOUT_CHANGED_EVENT } from './EventBus'
import { DataLayer, pageMsFromBars, type ScrollBackExhaustedReason } from './DataLayer'
import { ReplayController } from './ReplayController'
import type { MultiPeriodCache } from './MultiPeriodCache'
import { getSharedMultiPeriodCache } from './sharedMultiPeriodCache'
import { aggregateBars } from '../utils/aggregator'
import {
  computeAnchorWindow,
  resolveAnchorVisibleRange,
  deriveSessionDateKeys,
  anchorLookbackMs,
  anchorForwardMs,
  windowFromRange,
  DEFAULT_ANCHOR_WINDOW,
  type AnchorVisibleWindow,
} from '../utils/anchorWindow'
import {
  curatePatterns,
  detectPatterns,
  type CurateOptions,
  type Pattern,
  type TouchInput,
} from '../utils/patternDetection'
import {
  PATTERN_OVERLAY_NAME,
  type PatternExtendData,
  bandColorForPattern,
  colorForPattern,
  lineWidthForPattern,
  patternLabel,
} from '../utils/patternOverlay'
import { autoFibLookbackBars, autoFibPoints, pickAutoFibSwing } from '../utils/autoFib'
import { AUTO_FIB_OVERLAY_NAME } from '../utils/fibonacciOverlay'
import { curateTrendlines, detectTrendlines } from '../utils/trendlineDetection'
import { applyTheme } from '../themes/base'
import { VOL_OVERLAY_NAME, VOL_DEFAULT_CALC_PARAMS } from '../utils/volumeIndicator'
import {
  EXTENDED_SESSION_COLORS,
  EXTENDED_SESSION_PRICE_MARK_NAME,
  registerExtendedSessionPriceMarkOverlay,
  type ExtendedSessionLabel,
} from '../utils/premarketDailyBar'
import {
  HISTORY_START_MARKER_LEFT_ROOM,
  HISTORY_START_MARKER_NAME,
  registerHistoryStartMarkerOverlay,
} from '../utils/historyStartMarkerOverlay'
import {
  REFERENCE_PRICE_LINE_NAME,
  registerReferencePriceLineOverlay,
} from '../utils/referencePriceLine'
import {
  BAR_COUNTDOWN_PILL_NAME,
  BAR_COUNTDOWN_Z_LEVEL,
  barCountdownPillState,
  registerBarCountdownPillOverlay,
} from '../utils/barCountdownPill'
import { formatDayBreakLabel } from '../utils/overlayDateFormat'
import { DILUTION_PRICE_LINE_NAME } from '../utils/dilutionPriceLine'
import {
  ALERT_LABEL_AXIS_EXTRA,
  PRICE_ALERT_LINE_NAME,
  PRICE_ALERT_Z_LEVEL,
  alertHitAt,
  countdownPillBand,
  estimateLabelWidth,
  registerPriceAlertLineOverlay,
  type PriceAlertLine,
  type PriceAlertLineExtend,
} from '../utils/priceAlertLine'
import { alertPrice } from '../utils/crosshairPriceAction'
import { CHART_COLORS } from '../utils/chartColors'
import { DEFAULT_FILL_OPACITY, FILLABLE_SHAPES } from '../utils/shapeOverlays'
import {
  registerLabelVisibilityResolver,
  positionDefaultGeometry,
} from '../utils/riskRewardOverlay'
import { hoverCursor, isPointDrag, type PositionCursor } from '../utils/drawingCursor'
import { registerMeasureVolumeResolver } from '../utils/measureOverlay'
import { REPLAY_LOOP_RANGE_NAME } from '../utils/replayLoopOverlay'
import { BAR_HIGHLIGHT_NAME, registerBarHighlightOverlay } from '../utils/barHighlightOverlay'
import {
  VERTICAL_LINE_NAME,
  VERTICAL_LINE_SPAN_NAME,
  registerVerticalLineSpanOverlay,
} from '../utils/verticalLineOverlay'
import { SESSION_HIGHLIGHT_NAME, type SessionHighlightConfig } from '../utils/sessionHighlight'
import { RANGE_HIGHLIGHT_NAME, type RangeHighlightConfig } from '../utils/rangeHighlight'
import { CANDLE_PANE_ID } from '../types/chart'
import { SESSION_BREAKS_NAME } from '../utils/sessionBreaks'
import { PREVIOUS_CLOSE_NAME } from '../utils/previousClose'
import { EARNINGS_MARKERS_NAME } from '../utils/earningsMarkersIndicator'
import { SPLIT_MARKERS_NAME } from '../utils/splitMarkersIndicator'
import { manualLevelsFromOverlays, MANUAL_LEVEL_OVERLAY_NAMES } from '../utils/positionSizing'
import {
  POSITION_SIZE_NAME,
  type PositionSizeExtendData,
  type PositionSizeReadoutPayload,
} from '../utils/positionSizeIndicator'
import {
  POSITION_PLAN_NAME,
  appendPlanEntry,
  entryDisplayNumbers,
  entryPointIndices,
  parsePlanConfig,
  registerPlanSelectionResolver,
  removePlanEntry,
} from '../utils/positionPlanOverlay'

// System "chrome" indicators — session highlighting (pre/after-hours),
// session breaks, the previous-close line and the earnings markers. These are
// reference visuals, not user studies, so the eye / `s` show-hide toggle never
// touches them.
/**
 * Overlays that are DATA, not user drawings. The drawing-bar eye hides with an
 * id-less blanket override (klinecharts has no "user drawings only"), so these
 * are shown again right after it (dilution levels, the Today line, price alert
 * lines). Named rather than tracked by id: the hooks that create them own their
 * ids.
 */
const DATA_LINE_OVERLAY_NAMES = [
  DILUTION_PRICE_LINE_NAME,
  REFERENCE_PRICE_LINE_NAME,
  PRICE_ALERT_LINE_NAME,
]

/** Overlays that are chrome or derived data, never a drawing the user placed. */
/** A double click cancels the alert pressed on when it lands this near (klinecharts pairs
 *  mouse-ups within 5 px, KC:12266-12274) and this soon after that press. */
const AXIS_ALERT_PRESS_MAX_PX = 5
const AXIS_ALERT_PRESS_MAX_MS = 800

const NON_DRAWING_OVERLAY_NAMES = new Set<string>([
  ...DATA_LINE_OVERLAY_NAMES,
  BAR_COUNTDOWN_PILL_NAME,
  HISTORY_START_MARKER_NAME,
  PATTERN_OVERLAY_NAME,
  AUTO_FIB_OVERLAY_NAME,
  BAR_HIGHLIGHT_NAME,
  EXTENDED_SESSION_PRICE_MARK_NAME,
  VERTICAL_LINE_SPAN_NAME,
])

const SYSTEM_INDICATOR_NAMES = new Set<string>([
  SESSION_HIGHLIGHT_NAME,
  SESSION_BREAKS_NAME,
  PREVIOUS_CLOSE_NAME,
  EARNINGS_MARKERS_NAME,
  SPLIT_MARKERS_NAME,
])

// Solid yellow for the vertical crosshair line while Shift-hold history-mode is
// armed on a daily chart — the cue that a Shift+click drills into that day.
const HISTORY_CROSSHAIR_COLOR = 'rgb(250, 204, 21)'
import {
  isDuplicableIndicator,
  allocateInstanceName,
  ensureInstanceRegistered,
  baseIndicatorName,
} from '../utils/maInstances'
import { hubAdd } from './globalListenerHub'
import { HtfSeriesTracker } from './htfSeriesStore'
import {
  dailyRowsFromBars,
  isSessionTimeframe,
  sessionRowCount,
  weeklyRows,
  type SessionRow,
  type SessionTimeframe,
} from '../utils/mtfSession'
import type { MtfExtendData } from '../utils/mtfCalc'
import { subPeriodFor } from '../utils/replaySubBars'
import {
  browserFrameClock,
  easeToward,
  MotionLoop,
  PRICE_GLIDE_MS,
  PRICE_SETTLE_RATIO,
  priceWheelFactor,
  SCROLL_GLIDE_MS,
  wheelDeltaPx,
  wheelZoomFactor,
  ZOOM_GLIDE_MS,
  ZOOM_SETTLE_RATIO,
  type FrameClock,
} from './smoothMotion'

let _syncIdCounter = 0
/** klinecharts' default `modeSensitivity` — the weak magnet's grab radius. */
const MAGNET_SENSITIVITY_PX = 8

function generateSyncId(): string {
  return `osync_${Date.now()}_${++_syncIdCounter}`
}

/* ------------------------------------------------------------------ */
/*  klinecharts internal y-axis API (not on the public `Chart` type)    */
/* ------------------------------------------------------------------ */
// Wheel-over-price-axis scaling drives the same pane y-axis range the built-in
// y-axis DRAG uses (see patches/klinecharts@9.8.12.patch). Those methods live on
// the runtime ChartImp / pane / axis objects but aren't declared on the public
// `Chart` interface, so we reach them through a narrow, guarded cast.
interface YAxisRange {
  from: number
  to: number
  range: number
  realFrom: number
  realTo: number
  realRange: number
}
interface YAxisComponent {
  getRange: () => YAxisRange | null
  setRange: (range: YAxisRange) => void
  convertToRealValue: (value: number) => number
  getScrollZoomEnabled?: () => boolean
  setAutoCalcTickFlag?: (flag: boolean) => void
  /** klinecharts' real auto-fit state: true = auto-calculating ticks (fitting
   *  data), false = a manual range is pinned (set by setRange — the native
   *  y-axis drag, wheel zoom, etc.). The source of truth for the "Auto" menu. */
  getAutoCalcTickFlag?: () => boolean
  /** The auto-fit range for the current view, computed without applying it. */
  calcRange?: () => YAxisRange
}
interface ChartInternals {
  getChartStore?: () => {
    getTimeScaleStore?: () => {
      getLastBarRightSideDiffBarCount?: () => number
      getInitialOffsetRightDistance?: () => number
    }
  }
  getDrawPaneById?: (paneId: string) => { getAxisComponent: () => YAxisComponent } | null
  adjustPaneViewport?: (
    shouldMeasureHeight: boolean,
    shouldMeasureWidth: boolean,
    shouldUpdate: boolean,
    shouldAdjustYAxis: boolean,
  ) => void
}

/**
 * Right-edge breathing room shown to the right of the latest bar on load.
 * Expressed in bars (not pixels or a width ratio): the offset auto-scales with
 * zoom so it always looks like the same amount of empty room regardless of
 * panel size. Thin-history auto-fit (Task 5b) reuses this so stretched bars
 * leave the same trailing gap as a normal chart.
 * Spec: docs/superpowers/specs/2026-05-07-chart-thin-history-ux-design.md
 */
/** Default empty bars kept clear of the right edge — see `rightMarginBars`. */
const RIGHT_OFFSET_BARS = 5
const MIN_BAR_SPACE = 4
const MAX_BAR_SPACE = 30
/**
 * klinecharts' OWN native bar-space clamp (its internal, unexported
 * `BarSpaceLimitConstants` — verified against
 * node_modules/klinecharts/dist/index.esm.js). Deliberately NOT the same as
 * `MIN_BAR_SPACE`/`MAX_BAR_SPACE` above, which are a narrower, feature-specific
 * range for thin-history auto-fit only. Wheel-zoom has no explicit app-level
 * clamp beyond klinecharts' native one, so rubber-band zoom uses the same
 * native range to keep the two gestures' zoom limits consistent.
 *
 * Both bounds are widened from stock via the vendored patch
 * (patches/klinecharts@9.8.12.patch): MAX 50 → 500 (2026-08-01, zoom-in was
 * stopping at a 50px bar) and MIN 1 → 0.5 (2026-08-02, zoom-out was capped
 * at ~1 bar per pixel; max visible bars = pane px / MIN — 0.25 was tried
 * and rolled back the same day, 0.5 is the chosen floor). These mirrors MUST match the patched values or
 * rubber-band requests get silently rejected (klinecharts REJECTS
 * out-of-range setBarSpace rather than clamping).
 */
const NATIVE_MIN_BAR_SPACE = 0.5
const NATIVE_MAX_BAR_SPACE = 500
const PAN_GUARD_RESET_DISTANCE = Number.MAX_SAFE_INTEGER
/** Container dimensions below this are mid-drag slivers (mosaic collapse), not
 *  real chart sizes — `resize()` skips layout for them so the y-axis is never
 *  computed from a collapsed viewport. */
const MIN_LAYOUT_SIZE_PX = 40

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

// ─── Debug logger (toggle from console: window.__chartDebug = true) ───
const _win = typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>) : null
function chartDebug(...args: unknown[]) {
  if (_win?.__chartDebug) console.log('[ChartManager]', ...args)
}

export interface InitialIndicator {
  name: string
  calcParams?: unknown[]
  /** Only meaningful for VOL: 'main' overlays it on the price pane,
   *  'sub' (default) gives it its own bottom pane. */
  placement?: 'main' | 'sub'
  /** Higher timeframe the indicator computes from (MTF). Forces the instance
   *  through an app-owned clone — the built-in template's calc cannot honour
   *  a timeframe. See maInstances / mtfCalc. */
  sourceTimeframe?: string
}

export interface ChartManagerOptions {
  container: HTMLDivElement
  symbol: EdgeSymbol
  period: Period
  datafeed: DataFeed
  theme?: EdgeTheme
  timezone?: string
  extendedHours?: boolean
  /** Initial split-adjusted setting. `true` (default) requests adjusted OHLCV;
   *  `false` requests unadjusted (raw, as-traded). Threaded into the first
   *  history fetch and replayed by `setAdjusted` thereafter. */
  adjusted?: boolean
  mainIndicators?: (string | InitialIndicator)[]
  subIndicators?: (string | InitialIndicator)[]
  /** When set, loads historical data ending at this timestamp instead of today */
  initialFocusTimestamp?: number | null
  /** Visible window anchored charts zoom to (see {@link AnchorVisibleWindow}).
   *  Omitted ⇒ the full 4:00–20:00 ET session ({@link DEFAULT_ANCHOR_WINDOW}). */
  anchorVisibleWindow?: AnchorVisibleWindow
  /** Restore previous bar space (zoom level) */
  initialBarSpace?: number
  /** Chart canvas background colour, used as the hollow fill of drawing grab
   *  handles so they read as a clean donut over the line (see
   *  `overlayStyleToKline`). Defaults to the dark canvas when unset. */
  background?: string
  /** Whether to background-prefetch the coarse preload period(s) — see
   *  PREFETCH_TARGETS. Defaults to true. Grid / scanner / history cells pass
   *  false: a per-cell speculative prefetch across many different tickers is
   *  mostly wasted server load. The single chart and drill-downs (where the
   *  user actually flips timeframes) keep it. */
  enablePreload?: boolean
}

export class ChartManager implements EdgeChartApi {
  private chart: Chart
  private container: HTMLDivElement
  private dataLayer: DataLayer
  private eventBus: EventBus
  private symbol: EdgeSymbol
  private period: Period
  /** Visible window anchored charts zoom to; parameterizes `zoomToDate`. */
  private anchorVisibleWindow: AnchorVisibleWindow = DEFAULT_ANCHOR_WINDOW
  /** Bumped per `setAnchorVisibleWindow`; guards the async reload-then-zoom. */
  private anchorReloadToken = 0
  /** Lazily created bar-replay engine (see `getReplay`). Null until first use. */
  private replay: ReplayController | null = null
  /** The loop-range highlight overlay (see `toggleReplayLoop`), null when off. */
  private replayLoopOverlayId: string | null = null
  private subPaneIds = new Map<string, string>()
  private subPaneRatios = new Map<string, number>()
  // Active pre/after-hours shading config, or null when off. Tracked so the
  // shading can be (re)applied to EVERY pane — it's an indicator, and an
  // indicator lives on one pane, so spanning the sub-panes below means one
  // instance per pane, kept in sync as sub-panes come and go. See
  // `setSessionHighlight` / `addSessionHighlightToPane`.
  private sessionHighlightConfig: SessionHighlightConfig | null = null
  private rangeHighlightConfig: RangeHighlightConfig | null = null
  // Indicator instance names the CURRENT timeframe rule hides (e.g. VWAP on a
  // daily chart). The global show/hide ("S" hotkey / eye button) must NOT
  // re-show these — the toggle is orthogonal to the per-timeframe visibility
  // setting. EdgeChart keeps this in sync via `setTimeframeSuppressed`.
  private timeframeSuppressed = new Set<string>()
  // Chart background — the hollow fill of drawing grab handles (see
  // `overlayStyleToKline`). Kept in sync with the chart's canvas background.
  private overlayHandleFill = '#131722'
  // Sync-subscription bookkeeping: map each caller callback to the wrapped
  // klinecharts action handler we actually registered, so unsubscribe can look
  // it up. WeakMaps (not a property stashed on the callback) so a callback reused
  // across subscriptions can't clobber another's handler, and entries GC with the
  // callback.
  private crosshairHandlers = new WeakMap<object, (raw: Crosshair) => void>()
  private viewportHandlers = new WeakMap<object, (range: { from: number; to: number }) => void>()
  // klinecharts instance names assigned to the initial main-pane indicators, in
  // prop order. Duplicable (MA-family) duplicates get a unique alias here; the
  // EdgeChart legend seed reads this so its rows reference the same instances.
  /** Empty bars kept clear of the right edge (Canvas → Margins setting). */
  private rightMarginBars = RIGHT_OFFSET_BARS
  private mainInstanceNames: string[] = []
  private subInstanceNames: Array<string | undefined> = []
  private axisWidthTier = 0
  /** A symbol/anchor swap kept stale bars on screen (no clearData) and its
   *  replacement load hasn't settled yet. Gates the data:error fallback that
   *  clears those bars so a failed swap can't leave the OLD ticker's candles
   *  under the NEW ticker's name. */
  private swapPending = false
  private hideAxisScale = false
  /** Daily series behind 1D/1W indicators (MTF Phase B); see htfSeriesStore. */
  private htfSeries!: HtfSeriesTracker
  /** Instances computing from a session timeframe: name -> pane + timeframe.
   *  Recomputed when the daily series lands; drives when it is fetched. */
  private mtfSessionInstances = new Map<string, { paneId: string; timeframe: SessionTimeframe }>()
  /** Ticker of the bars on screen. During an in-place symbol swap the old
   *  ticker's bars stay up until the new ones land, and must not be drawn
   *  against the new ticker's daily series. */
  private mtfBarsTicker: string | null = null

  /* Overlay sync state */
  private overlaySyncCallback: ((event: OverlaySyncEvent) => void) | null = null
  private isSyncing = false
  private localToSyncId = new Map<string, string>()
  /**
   * The drawing-bar eye, remembered. `setOverlaysVisible(false)` is a one-off
   * blanket override, but saved drawings arrive later (a fetch, a ticker swap,
   * a peer cell). Without the flag they were created visible under a closed
   * eye. A trash-all clears it: nothing is left to hide.
   */
  private overlaysHidden = false
  /** Same, for the eye's indicator side: data-driven price-pane indicators
   *  (dilution levels) are created after their fetch lands. */
  private indicatorsHidden = false
  private syncToLocalId = new Map<string, string>()
  // Per-tool default style pending on a freshly-created overlay, keyed by its
  // local id. Consumed by `persistOverlayCreate` so the default is persisted +
  // synced with the drawing (not just painted). Empty for legacy/unstyled creates.
  private pendingCreateStyles = new Map<string, OverlayStyleOverride>()
  /**
   * Live app-level style per overlay id. `pendingCreateStyles` is consumed and
   * deleted at persist time, so after that nothing knew a drawing's style —
   * which is why a duplicate came back unstyled after reload. This survives for
   * the overlay's lifetime and is the source of truth for copying style.
   */
  private overlayStyleById = new Map<string, OverlayStyleOverride>()

  // Coalesces `syncVerticalLineSpans` to one run per microtask. Overlay
  // removal fires `onRemoved` BEFORE klinecharts drops the instance from its
  // store, so a synchronous reconcile would still see the deleted primary;
  // deferring to a microtask lets the store settle first (and batches the
  // rapid-fire calls during a drag).
  private verticalSpanSyncScheduled = false

  // Coalesces `syncPositionSizeLevels` the same way: overlay events during a
  // drag fire per pointer move, and `onRemoved` fires before the store drops
  // the instance, so the snapshot defers to a microtask.
  private positionSizeSyncScheduled = false

  /* Overlay selection + keyboard delete */
  private selectedOverlayId: string | null = null
  /** The drawing remembered by ⌘/Ctrl-C, so ⌘/Ctrl-V can duplicate it even
   *  after the selection is cleared (klinecharts deselects on an outside click). */
  private copiedOverlayId: string | null = null
  /** Detaches the ephemeral measure-overlay teardown listeners, if armed. */
  private measureTeardownCleanup: (() => void) | null = null
  /**
   * Notifies the UI that a drawing tool is no longer armed — fired when any
   * drawing completes (its draw ends) or a half-drawn/one-shot overlay is
   * removed, so the toolbar can drop the active-tool highlight.
   */
  private toolDeactivatedCallback: (() => void) | null = null
  private drawProgressCallback:
    | ((points: Array<{ timestamp?: number; value?: number }>) => void)
    | null = null
  /** Last committed-point set broadcast, so mouse-move repeats are suppressed. */
  private lastDrawProgress: string | null = null
  /** Overlay id of the Pre/Post price mark (live daily charts, outside RTH). */
  private extendedMarkId: string | null = null
  /** Pre/Post price the host supplies (quotes API: pm_lp / ah_lp). */
  private extendedHostMark: { label: ExtendedSessionLabel; price: number } | null = null
  /** Live premarket price from the chart's own stream (DataLayer's forming
   *  daily bar). Fresher than the host's polled value, so it wins while set. */
  private streamPrePrice: number | null = null
  /** Settings eye (Labels → Pre/post-market price). */
  private extendedMarkVisible = true
  /** Overlay id of the rocket glyph under the first bar of a thin symbol
   *  (see `syncHistoryStartMarker`). Null when not shown. */
  private historyStartMarkerId: string | null = null
  /** Timestamp the marker currently sits on, to skip no-op overrides. */
  private historyStartMarkerTs: number | null = null
  /** Overlay id of the programmatic reference price line (see
   *  `setReferencePriceLine`). Null when no line is shown. */
  private referencePriceLineId: string | null = null
  /** Overlay id of the canvas last-price + countdown pill (see
   *  `setBarCountdownVisible`). Null when the pill is off. */
  private barCountdownId: string | null = null
  /** The pill is wanted; survives an id-less removeOverlay() wipe. */
  private barCountdownOn = false
  /** Countdown text last painted, so a tick repaints only when it changes. */
  private barCountdownText: string | null = null
  /** Active price alerts the host wants drawn (setPriceAlertLines). Null until the first
   *  call; nullable because test harnesses skip field initializers. */
  private priceAlertLines: readonly PriceAlertLine[] | null = null
  /** Alert id to overlay id for the lines on the chart now. */
  private priceAlertOverlayIds: Map<string, string> | null = null
  /** The host's cancel (EdgeChart's onCancelPriceAlert); null leaves the alerts without clicks. */
  private priceAlertCancel: ((alertId: string) => void) | null = null
  /** Read by the pill overlay at paint time (never pushed per tick). */
  private readonly readBarCountdown = (now: number) =>
    barCountdownPillState(this.getBarCountdownAnchor(), now, this.hideAxisScale)
  /** Last `candle.priceMark.last.show` value we asserted. Tracked so setTheme
   *  (whose theme bundle carries `show: true`) can re-assert a hidden mark. */
  private lastPriceMarkShow = true
  /**
   * Reference line requested before the first `data:loaded`. The quote often
   * resolves before the candles, and a lone dashed line on an empty canvas
   * reads as broken — so the line is deferred until bars exist, exactly like
   * the native price mark (which can't render without a last bar).
   */
  private pendingReferencePriceLine: { price: number; label?: string } | null = null
  /** True once the first `data:loaded` has fired for this chart instance. */
  private hasLoadedBars = false
  /** Overlay ids of the read-only pattern annotations (S/R zones, etc.). */
  private patternOverlayIds: string[] = []
  /**
   * Pattern-detection config (design §6). OPT-IN / dark by default — nothing is
   * drawn until `setPatternDetection({ enabled: true })`. `minConfidence` is the
   * sensitivity slider; `maxN` is the per-group curation budget.
   */
  private patternConfig: { enabled: boolean } & CurateOptions = {
    enabled: false,
    minConfidence: 0.4,
    maxN: 10,
  }
  /** Overlay ids of the read-only auto Fibonacci (Beta). Own array: the
   *  pattern sync clears only `patternOverlayIds`. */
  private autoFibOverlayIds: string[] = []
  /** Auto Fib config — opt-in, nothing draws until `enabled`. */
  private autoFibConfig: { enabled: boolean; swingMult: number } = {
    enabled: false,
    swingMult: 3,
  }
  /** Auto trendlines (Beta) — the "Trends" group of design §6, drawn through
   *  the same pattern-annotation sync as S/R with its own curation budget. */
  private trendlineConfig: { enabled: boolean; maxPerDirection: number } = {
    enabled: false,
    maxPerDirection: 3,
  }
  /** Wicks or candle bodies as the touch series for every detector above. */
  private touchInput: TouchInput = 'wicks'
  /** Debounce for the new-bar auto-analysis refresh (one recompute per bar
   *  close, not per tick — detection runs over the full loaded history). */
  private autoAnalysisTimer: ReturnType<typeof setTimeout> | null = null
  private lastAutoAnalysisBarTs: number | null = null
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null
  /** Overlay id of a drawing whose points are still being placed (tool armed
   *  from the drawing bar or a hotkey, draw not yet completed). Escape cancels
   *  it; cleared on draw-end / removal. */
  private _pendingDrawId: string | null = null
  private get pendingDrawId(): string | null {
    return this._pendingDrawId
  }
  private set pendingDrawId(id: string | null) {
    this._pendingDrawId = id
    // Stamp read by the host's letter-to-ticker layer: a typed letter must
    // not swap symbols out from under an armed or in-progress drawing - the
    // pending overlay dies unsaved with the swap. Escape / draw-end clears it.
    if (id !== null) this.container.setAttribute('data-drawing-pending', 'true')
    else this.container.removeAttribute('data-drawing-pending')
  }
  /** Magnet mode applied to every overlay — see `setOverlayMode`. */
  private overlayMode: OverlayModeName = 'normal'
  private escapeDrawCancelHandler: ((e: KeyboardEvent) => void) | null = null

  /* Drawing-tool hotkeys (h = horizontal, v = vertical, r = ray). Gated on real
   * local hover so only the chart the pointer is physically over responds —
   * peers in a crosshair-sync group receive a synthetic crosshair via
   * `executeCrosshair` and must NOT also place overlays. */
  private hotkeyHandler: ((e: KeyboardEvent) => void) | null = null
  private pointerEnterHandler: (() => void) | null = null
  private pointerLeaveHandler: (() => void) | null = null
  private cursorTrackHandler: ((raw: Crosshair) => void) | null = null
  private altClickHandler: ((e: MouseEvent) => void) | null = null
  private outsideDeselectHandler: ((e: MouseEvent) => void) | null = null
  /* Unregister fns for every document/window listener this instance routed
   * through the shared globalListenerHub (one real listener per event type
   * app-wide, instead of N per chart). Drained in destroy(). Nullable because
   * test harnesses build managers via Object.create (no field initializers). */
  private globalListenerUnsubs: Array<() => void> | null = null
  private isPointerOver = false
  private lastCursor: {
    timestamp: number
    value: number
    paneId: string
    high?: number
    low?: number
    open?: number
    close?: number
  } | null = null

  /* Alt(Option)-hold history mode: while Alt is held on a daily chart, the candle
   * under the cursor is painted yellow and the vertical crosshair turns yellow,
   * cueing that an Alt+click drills into that day. Alt state is tracked globally
   * (keydown fires regardless of hover) but the highlight only applies while the
   * pointer is over this chart. Shift is a separate hotkey that arms the measure
   * (ruler) tool. */
  private altKeyDownHandler: ((e: KeyboardEvent) => void) | null = null
  private altKeyUpHandler: ((e: KeyboardEvent) => void) | null = null
  private altBlurHandler: (() => void) | null = null
  private altHeld = false
  /** True once an Option chord fired during this Alt hold: the bar highlight
   *  stays cleared until Alt is released and pressed again, so drawing chords
   *  never leave a stray highlight. */
  private altChordConsumed = false
  // Alt-hold day-drill (yellow candle highlight + Alt+click → intradayDrill).
  // Only armed when the host wired an `onIntradayDrill` handler — otherwise the
  // highlight is a cue for a gesture that does nothing (e.g. chart-scanner grid
  // cells). Driven by EdgeChart via `setIntradayDrillEnabled`; defaults on for
  // any consumer that never sets it.
  private intradayDrillEnabled = true
  private shiftMeasureHandler: ((e: KeyboardEvent) => void) | null = null
  private shiftMeasureUpHandler: ((e: KeyboardEvent) => void) | null = null
  private measureTypingCancelHandler: ((e: KeyboardEvent) => void) | null = null
  private shiftHeld = false
  private barHighlightId: string | null = null
  /** Theme default vertical-crosshair color, saved while it's overridden yellow. */
  private savedCrosshairColor: string | null = null

  /* Tears down the position-tool label-visibility resolver on destroy */
  private cursorMoveHandler: (() => void) | null = null
  private cursorUpHandler: (() => void) | null = null
  private appliedCursor: PositionCursor = null
  private unregisterLabelResolver: (() => void) | null = null
  private unregisterPlanSelectionResolver: (() => void) | null = null
  private unregisterMeasureVolumeResolver: (() => void) | null = null

  /**
   * Wheel-gesture policy. `wheelHandler` is a SINGLE capture-phase listener
   * that owns all wheel behavior — present only while interception is
   * actually needed (`zoomAnchorState === 'lastBar'` and/or
   * `wheelActionState === 'pan'`); absent otherwise, so a plain
   * zoom-anchored-at-cursor + zoom-on-wheel chart (the klinecharts default)
   * never pays for an extra listener. `zoomAnchorState`/`wheelActionState`
   * are read live inside the handler, so changing either setting while
   * already intercepting needs no listener churn — only the install/remove
   * transition does.
   */
  private wheelHandler: ((e: WheelEvent) => void) | null = null
  private zoomAnchorState: 'cursor' | 'lastBar' = 'cursor'
  private wheelActionState: 'zoom' | 'pan' = 'zoom'

  /**
   * Smooth motion (`setSmoothMotion`): the wheel zoom glides toward its target
   * instead of jumping, Go to latest glides back instead of jumping, and while
   * either runs the price scale eases to its new fit instead of snapping.
   * Off in the manager; the app turns it on through the interaction settings.
   * One `motionLoop` drives all three; it only runs while something is moving.
   *
   * A drag gets no momentum: it stops exactly where the pointer lets go, so a
   * scrub back and forth lands where it was aimed. Any press stops whatever is
   * gliding and hands eased price scales back to auto-fit before klinecharts
   * sees the press (pointerdown fires before its mousedown), so a drag never
   * starts on a pane klinecharts would treat as manually scaled.
   */
  private smoothMotion = false
  /** Test seam: a hand-stepped clock. Absent means the browser's frames. */
  private motionClock?: FrameClock
  private motionLoop: MotionLoop | null = null
  /** In-flight wheel-zoom glide: bar-space target, the pixel it pins, and
   *  whether no frame has run since a wheel event last set it (`fresh`). */
  private zoomGlide: { target: number; anchorX: number; fresh: boolean } | null = null
  /** In-flight Go to latest glide: scroll distance still to cover (px, the
   *  sign `scrollByDistance` uses: negative = forward in time). */
  private scrollGlide: { remaining: number } | null = null
  /** Panes whose price scale is being eased. klinecharts sees them as manually
   *  scaled (setRange) until the ease settles and they go back to auto-fit;
   *  `isPriceAxisAuto` still reports them as auto. */
  private priceGlides: Set<string> | null = null
  private motionPressHandler: ((e: PointerEvent) => void) | null = null
  /** When true, a symbol swap (live or anchored) resets the horizontal zoom
   *  instead of carrying the previous ticker's zoom over. Off by default, see setSymbol. */
  private resetZoomOnSymbolChange = false
  /** Armed by a symbol swap while `resetZoomOnSymbolChange` is on, consumed by
   *  the load that lands the new ticker's bars (`loadInner`). Waiting for those
   *  bars is what lets it survive a load that supersedes the swap's own (a
   *  History row click moves the focus date in the same commit). */
  private pendingSymbolZoomReset = false
  /** Per-timeframe-class reset-zoom rules; an absent row keeps today's fixed reset. */
  private resetZoomPolicy: ResetZoomPolicy = {}

  /**
   * Drag-gesture policy (spec D7). `dragMouseDownHandler` is a capture-phase
   * listener present only while `dragActionState === 'zoomRegion'`; a press
   * that passes `dragStartBlocked`'s guards then arms `dragMouseMoveHandler` /
   * `dragMouseUpHandler` / `dragEscapeHandler` on `window`/`document` for the
   * duration of that ONE press (installed at mousedown, torn down at
   * mouseup/Escape/dispose/setting-flip) — cheaper than always-on listeners
   * and keeps a stray move/up from a DIFFERENT press from ever reaching us.
   *
   * `dragPhase` tracks the press itself:
   *   'idle'      — no press in progress.
   *   'armed'     — mousedown passed the guards; swallowing has ALREADY begun
   *                 (see the mousedown handler for why — klinecharts starts
   *                 panning on the very first post-mousedown move, so we can't
   *                 wait for the 5px threshold before intercepting without a
   *                 visible pan-then-select jitter). No selection div yet.
   *   'selecting' — horizontal travel crossed MIN_DRAG_PX; the selection div
   *                 is visible and tracks every further move.
   *
   * Because we intercept via `stopImmediatePropagation()` only — never
   * `preventDefault()` — a sub-threshold press/move/release still produces the
   * browser's own native mousedown→mouseup→click sequence afterward
   * (preventDefault on mousedown is what suppresses that synthesis, and we
   * never call it), so klinecharts' click-driven behavior (crosshair
   * placement, etc.) survives an accidental sub-5px "drag" for free — no
   * synthetic event construction needed.
   */
  private dragActionState: 'pan' | 'zoomRegion' = 'pan'
  private dragMouseDownHandler: ((e: MouseEvent) => void) | null = null
  private dragMouseMoveHandler: ((e: MouseEvent) => void) | null = null
  private dragMouseUpHandler: ((e: MouseEvent) => void) | null = null
  private dragEscapeHandler: ((e: KeyboardEvent) => void) | null = null
  private dragPhase: 'idle' | 'armed' | 'selecting' = 'idle'
  private dragStartX = 0
  private dragSelectionEl: HTMLDivElement | null = null

  /* Default zoom (bar space) captured at construction; a time-axis double-click
   * resets to it. */
  private defaultBarSpace = 6
  /* True when this chart was constructed with an explicit `initialBarSpace`: a
   * zoom level restored across a template remount (see chart-panel's
   * `_savedBarSpace`). Tickers now swap in place, so this no longer serves a
   * ticker switch; the user-zoom fields below do. Only the first anchored
   * `zoomToDate` that has bars to frame uses it, then it clears, so Visible hours
   * and a later reset can fit again. Fresh panels have no restore and fit. */
  private hasRestoredBarSpace = false
  /* The bar space the chart last picked for itself (a session fit, a thin-ticker
   * stretch, a reset). A live width that differs means the user zoomed. Null when
   * built with a restored zoom, which counts as the user's. */
  private chosenBarSpace: number | null = null
  /* Armed by a ticker or date click on a zoomed anchored chart, consumed by the
   * load that follows. `window` is the time of day in view (null on daily and
   * coarser charts, which have no time of day to keep). */
  private pendingKeptZoom: { window: AnchorVisibleWindow | null } | null = null
  private dblclickHandler: ((e: MouseEvent) => void) | null = null
  private axisAlertPressHandler: ((e: MouseEvent) => void) | null = null
  /** The alert label under the last left press on the candle pane's price axis, judged
   *  against the range on screen at that moment (read in dblclickHandler). */
  private axisAlertPress: { id: string; x: number; y: number; at: number } | null = null
  /** True for one tick after a drawing was double-clicked (opens its settings),
   *  so the body double-click chart-reset doesn't also fire for the same click. */
  private overlayEditFromDblClick = false
  /** Scroll-wheel over a pane's price (y) axis scales that axis. Always on. */
  private yAxisWheelHandler: ((e: WheelEvent) => void) | null = null

  /**
   * Per-(ticker, period) preload cache for `1D` / `1W` / `1M`. Populated by
   * `prefetchDailyAndCoarser()` on chart open and on `setSymbol`. Consumed by
   * `setPeriod()` to render those timeframes instantly without a network
   * round-trip. Spec:
   * docs/superpowers/specs/2026-05-06-chart-multi-period-preload-design.md
   */
  // Assigned in the constructor from the datafeed's SHARED cache. NOT
  // initialized inline: a per-instance `new MultiPeriodCache()` here would
  // defeat cross-chart / cross-remount reuse.
  private cache!: MultiPeriodCache

  /** Exposed for tests + dev tools. Production code should not reach in. */
  getMultiPeriodCache(): MultiPeriodCache {
    return this.cache
  }
  private datafeed: DataFeed
  // Background preload targets. DAILY only: it's the common coarse view users
  // flip to from an intraday chart, so pre-warming it keeps that switch instant.
  // Weekly/monthly are deliberately NOT prefetched — they're uncommon, and on a
  // multi-chart grid every extra period is one more background request per cell.
  // They still load correctly on demand (and then cache) the first time a user
  // opens them. Re-add periods here if a coarse view later warrants pre-warming.
  private static readonly PREFETCH_TARGETS: PeriodKey[] = ['1D']
  /** When false, skip the background preload entirely (grid / scanner cells). */
  private enablePreload = true

  constructor(options: ChartManagerOptions) {
    const {
      container,
      symbol,
      period,
      datafeed,
      theme,
      timezone,
      extendedHours,
      adjusted,
      mainIndicators,
      subIndicators,
      background,
      enablePreload,
      anchorVisibleWindow,
    } = options

    this.eventBus = new EventBus()
    this.enablePreload = enablePreload ?? true
    this.symbol = symbol
    this.period = period
    this.container = container
    if (anchorVisibleWindow) this.anchorVisibleWindow = anchorVisibleWindow
    if (background) this.overlayHandleFill = background

    // Suppress klinecharts "Welcome" console log during init
    const origLog = console.log
    console.log = (...args: unknown[]) => {
      if (typeof args[0] === 'string' && args[0].includes('Welcome to klinecharts')) return
      origLog.apply(console, args)
    }
    this.chart = init(container, { styles: applyTheme(theme) })!
    console.log = origLog
    this.writeEdgePeriod()

    // Position-tool labels track the same hover/click state that klinecharts uses
    // to show the grab handles. We read that live store state at render time
    // (see riskRewardOverlay.createPointFigures) rather than round-tripping events.
    // getChartStore/getOverlayStore are internal APIs absent from mocked charts in
    // tests, so guard before wiring the resolver.
    type HoverClick = {
      instance: { id: string; name?: string } | null
      figureType: number
      figureKey?: string
      figureIndex?: number
    }
    const overlayStore = (
      this.chart as unknown as {
        getChartStore?: () => {
          getOverlayStore?: () => {
            getHoverInstanceInfo: () => HoverClick
            getClickInstanceInfo: () => HoverClick
            getPressedInstanceInfo: () => HoverClick
          }
        }
      }
    )
      .getChartStore?.()
      ?.getOverlayStore?.()
    if (overlayStore) {
      this.unregisterLabelResolver = registerLabelVisibilityResolver((id) => {
        const hover = overlayStore.getHoverInstanceInfo()
        const click = overlayStore.getClickInstanceInfo()
        // figureType 0 = EventOverlayInfoFigureType.None (cursor not over a figure)
        return (
          (hover.instance?.id === id && hover.figureType !== 0) ||
          (click.instance?.id === id && click.figureType !== 0)
        )
      })
      // Position-plan drag semantics are SELECTION-gated (click only, never
      // hover): selected → any drag translates the whole plan; unselected →
      // each line drags alone. Hover in this resolver would hijack every
      // per-line drag into a whole-plan move.
      this.unregisterPlanSelectionResolver = registerPlanSelectionResolver(
        (id) => overlayStore.getClickInstanceInfo().instance?.id === id,
      )
    }

    // Cursor feedback on drawing hover, driven GLOBALLY off the overlay store's
    // hover state rather than per-template `onMouseEnter`. Most drawings are
    // klinecharts BUILT-INS (straightLine, rayLine, rect, …) whose templates we
    // don't own, so a per-template hook only ever worked for our own overlays.
    //
    // The chart's resting cursor is a crosshair (it drives the price/time
    // readout), so the override applies only while genuinely over a figure and
    // is released the moment hover ends.
    if (overlayStore) {
      this.cursorMoveHandler = () => {
        const hover = overlayStore.getHoverInstanceInfo()
        // While a drawing tool is armed (pendingDrawId set, e.g. Shift arming
        // the measure) or a drawing's point is being dragged, show the standard
        // arrow so its tip marks exactly where the point lands.
        const armed = this.pendingDrawId !== null
        const pressed = overlayStore.getPressedInstanceInfo()
        const draggingPoint =
          pressed.instance !== null && isPointDrag(pressed.figureKey, pressed.figureType)
        const next = hoverCursor(
          armed,
          hover.instance?.name,
          hover.figureKey,
          hover.figureType,
          draggingPoint,
        )
        if (next !== this.appliedCursor) {
          this.appliedCursor = next
          this.applyDrawingCursor(next)
        }
      }
      this.container.addEventListener('mousemove', this.cursorMoveHandler)
      this.container.addEventListener('mouseleave', this.cursorMoveHandler)
      // A drag can end without a further move; re-read once klinecharts has
      // cleared its pressed state so the arrow does not linger.
      this.cursorUpHandler = () => requestAnimationFrame(() => this.cursorMoveHandler?.())
      this.container.addEventListener('mouseup', this.cursorUpHandler)
    }

    // Volume for the measure/ruler overlay: sum this chart's bar volume across
    // the measured range. Only respond for overlays that belong to this chart.
    this.unregisterMeasureVolumeResolver = registerMeasureVolumeResolver((id, from, to) => {
      if (!this.chart.getOverlayById(id)) return null
      const data = this.chart.getDataList()
      if (data.length === 0) return null
      const lo = Math.max(0, Math.min(from, to))
      const hi = Math.min(data.length - 1, Math.max(from, to))
      let sum = 0
      for (let i = lo; i <= hi; i++) sum += (data[i] as { volume?: number })?.volume ?? 0
      return sum
    })

    // The loop-range highlight lives only inside a replay session: whatever
    // ends the session (exit chip, symbol switch, workspace date reset) takes
    // the highlight with it. eventBus.clear() in destroy() detaches this.
    this.eventBus.on('replay:ended', () => this.removeReplayLoopOverlay())

    // Seed the y-axis at the smallest tier used by updateAxisWidth() so the
    // empty/loading state matches the loaded state for typical sub-$100
    // prices. Without this, klinecharts falls back to its 'auto' sizing
    // which produces a different width than the tier set after data loads,
    // causing a visible horizontal shift of the y-axis labels on first load.
    this.axisWidthTier = 40
    this.chart.setStyles({ yAxis: { size: 40 } } as Parameters<typeof this.chart.setStyles>[0])

    // Failure fallback for the clearData-less swap (see setSymbol): if the
    // replacement load errors while stale bars are still up, clear them — an
    // empty chart is honest; the old ticker's candles under the new name lie.
    this.eventBus.on('data:error', () => {
      if (!this.swapPending) return
      this.swapPending = false
      this.chart.clearData()
    })

    // klinecharts' default bar space (before any restore/user zoom) × 0.75 is
    // our default zoom. Captured so a time-axis double-click can reset to it.
    this.defaultBarSpace = this.chart.getBarSpace() * 0.75
    this.hasRestoredBarSpace = options.initialBarSpace != null
    const barSpace = options.initialBarSpace ?? this.defaultBarSpace
    this.chart.setBarSpace(barSpace)
    // Read back rather than assume: the chart may clamp what we asked for.
    this.chosenBarSpace = options.initialBarSpace != null ? null : this.chart.getBarSpace()
    this.chart.setOffsetRightDistance(this.rightMarginBars * barSpace)

    if (timezone) {
      this.chart.setTimezone(timezone)
    }

    this.applyDateFormatter()

    this.datafeed = datafeed
    // Use the SHARED multi-period cache for this datafeed so every chart on the
    // feed reuses each other's prefetched D/W/M bars and keeps them across a
    // remount (drill in/out, layout change, key-driven remount).
    this.cache = getSharedMultiPeriodCache(datafeed)
    this.dataLayer = new DataLayer(datafeed, this.eventBus)
    this.dataLayer.setChart(this.chart)
    this.htfSeries = new HtfSeriesTracker({
      cache: this.cache,
      ticker: () => this.symbol.ticker,
      adjusted: () => this.dataLayer.isAdjusted(),
      windowEnd: () => {
        const anchor = this.dataLayer.getAnchorTimestamp()
        if (anchor == null) return null
        return anchor + anchorForwardMs(this.anchorVisibleWindow.endDayOffset ?? 0)
      },
      fetch: (endMs, signal) =>
        this.fetchPeriodWindow(this.symbol, PERIODS['1D'], signal, endMs ?? Date.now()),
      onReady: () => this.refreshMtfSessionInstances(),
      now: () => Date.now(),
    })
    // Size the initial anchored fetch to the window's span BEFORE loadHistory
    // fires — a persisted multi-day window must load its prior sessions on the
    // first request, not via scroll-back after the fact. 0 for a single-day
    // window, so every other chart's fetch is unchanged.
    this.dataLayer.setMinAnchorLookbackMs(
      anchorLookbackMs(this.anchorVisibleWindow.startDayOffset ?? 0),
    )
    this.dataLayer.setAnchorForwardMs(anchorForwardMs(this.anchorVisibleWindow.endDayOffset ?? 0))

    // Pan guard fires on every reason transition. Auto-fit is gated on initial
    // load + `reason='floor'` and lives in `load()` / cache-hit paths instead.
    this.eventBus.on('scrollback:exhausted', (payload: { reason: ScrollBackExhaustedReason }) => {
      this.applyPanGuard(payload.reason)
      this.syncHistoryStartMarker()
    })
    // First-bar rocket for thin symbols; created/moved/removed by
    // `syncHistoryStartMarker` on exhaustion changes and on every history load
    // (the reason can flip to 'floor' before the last page is prepended).
    registerHistoryStartMarkerOverlay()

    // Pre/Post price mark on live daily charts. The host feeds the session's
    // extended-hours price (setExtendedSessionPrice); DataLayer's premarket
    // stream (it suppresses today's forming candle pre-open) feeds a fresher
    // Pre price via 'premarket:price'.
    registerExtendedSessionPriceMarkOverlay()
    // Programmatic reference price line (anchored/historical charts showing
    // TODAY's live price) — registered here, created via setReferencePriceLine.
    // Creation is deferred until the first `data:loaded`: the host's quote can
    // resolve before the candles, and drawing the line on an empty canvas
    // looks broken (the native price mark similarly needs a last bar).
    registerReferencePriceLineOverlay()
    registerBarCountdownPillOverlay()
    registerPriceAlertLineOverlay()
    // Per-sub-pane mirror segments for the vertical line (created by this
    // manager's span reconciler). Self-registered here — not just in EdgeChart —
    // so a host that mounts ChartManager without EdgeChart still gets the
    // cross-pane span. `registerOnce` makes the double registration a no-op.
    registerVerticalLineSpanOverlay()
    this.eventBus.on('data:loaded', () => {
      this.hasLoadedBars = true
      this.syncHistoryStartMarker()
      if (this.pendingReferencePriceLine) {
        const pending = this.pendingReferencePriceLine
        this.pendingReferencePriceLine = null
        this.setReferencePriceLine(pending)
      }
      this.syncPriceAlertLines()
    })
    // Session-timeframe indicators: a load can change the anchor (History) or
    // the ticker whose bars are on screen; a tick can roll the session over.
    this.eventBus.on('data:loaded', (payload: { symbol?: string }) => {
      const swapped = payload.symbol !== this.mtfBarsTicker
      this.mtfBarsTicker = payload.symbol ?? null
      if (this.mtfSessionInstances.size === 0) return
      this.primeHtfSeries()
      if (swapped && this.htfSeries.rows()) this.refreshMtfSessionInstances()
    })
    this.eventBus.on('data:updated', () => {
      if (this.mtfSessionInstances.size > 0) this.htfSeries.ensure()
    })
    this.eventBus.on('premarket:price', (payload: { price: number | null }) => {
      this.setStreamPrePrice(payload.price)
    })
    // The mark only belongs on a live daily chart; re-check when either changes.
    this.eventBus.on('period:changed', () => this.syncExtendedSessionMark())
    this.eventBus.on('data:loaded', () => this.syncExtendedSessionMark())
    // A live tick moves the last-price pill; keep the Pre/Post pill clear of it.
    this.eventBus.on('data:updated', () => {
      if (this.extendedMarkId) this.syncExtendedSessionMark()
    })

    // Kick off the per-symbol floor lookup (chart-lookback spec 2026-05-04,
    // "Floor lookup (once per symbol)"). Fire-and-forget: the floor only
    // affects the scroll-back loop, not the initial `loadHistory()` call,
    // so it is safe to race the network fetch against the bar load.
    void this.dataLayer.setSymbol(symbol)

    // Apply initial extended-hours setting. This sets both the client-side
    // bar filter (load-bearing for Ably realtime) and the boolean threaded
    // through to history-bar URLs (`?market_session=mkt` when off).
    if (extendedHours === false && timezone) {
      this.dataLayer.setExtendedHours(false, timezone)
    }
    // Apply initial adjusted setting. Default true matches the backend default
    // (URL omits the `adjusted` param) so most charts hit the canonical cache
    // key. Only flipped via the SettingsModal toggle.
    if (adjusted === false) {
      this.dataLayer.setAdjusted(false)
    }

    if (mainIndicators) {
      const suppressTooltip = () => ({ name: '', calcParamsText: '', icons: [], values: [] })
      const used = new Set<string>()
      mainIndicators.forEach((ind) => {
        const name = typeof ind === 'string' ? ind : ind.name
        // Repairs arrays saved short (see completeCalcParams) so they load drawn.
        const calcParams =
          typeof ind === 'string' ? undefined : completeCalcParams(name, ind.calcParams)
        const sourceTimeframe = typeof ind === 'string' ? undefined : ind.sourceTimeframe
        // Give a repeated MA-family overlay its own registered name so the
        // second/third instance coexists instead of being swallowed. An MTF
        // instance is ALWAYS an alias, even as the first of its name — only
        // the app-owned clone calc honours extendData.timeframe.
        let instanceName = name
        if (isDuplicableIndicator(name)) {
          const taken = sourceTimeframe ? new Set([...used, name]) : used
          instanceName = allocateInstanceName(name, taken)
          ensureInstanceRegistered(instanceName, name)
        }
        used.add(instanceName)
        this.mainInstanceNames.push(instanceName)
        const base: Record<string, unknown> = {
          name: instanceName,
          createTooltipDataSource: suppressTooltip,
        }
        if (calcParams) base.calcParams = calcParams
        if (sourceTimeframe) {
          base.extendData = this.mtfExtendData(instanceName, CANDLE_PANE_ID, {
            timeframe: sourceTimeframe,
          })
        }
        this.chart.createIndicator(base as Parameters<Chart['createIndicator']>[0], true, {
          id: CANDLE_PANE_ID,
        })
      })
      // A RESTORED sizing readout mounts through this direct createIndicator
      // path (not this.createIndicator), so seed its extendData here — without
      // it the calc has no manual levels and no way to reach the React pill.
      this.schedulePositionSizeLevelSync()
    }
    if (subIndicators) {
      const usedSub = new Set<string>()
      subIndicators.forEach((ind, i) => {
        const name = typeof ind === 'string' ? ind : ind.name
        // Repairs arrays saved short (see completeCalcParams) so they load drawn.
        const calcParams =
          typeof ind === 'string' ? undefined : completeCalcParams(name, ind.calcParams)
        const placement = typeof ind === 'string' ? undefined : ind.placement
        const sourceTimeframe = typeof ind === 'string' ? undefined : ind.sourceTimeframe
        const ratio = this.calcSubPaneRatio(i)
        const height = Math.round(this.container.clientHeight * ratio)
        if (name === 'VOL' && placement === 'main') {
          // Overlay mode: volume renders as a self-scaled band on the candle
          // pane via VOL_OVERLAY's custom `draw` — no sub-pane, no ratio
          // bookkeeping. See registerVolumeIndicator().
          this.chart.createIndicator({ name: VOL_OVERLAY_NAME }, true, {
            id: CANDLE_PANE_ID,
          } as Parameters<Chart['createIndicator']>[2])
        } else if (name === 'VOL') {
          const paneId = this.chart.createIndicator(
            { name, shortName: 'Vol', calcParams: calcParams ?? [...VOL_DEFAULT_CALC_PARAMS] },
            false,
            { height, gap: { bottom: 0 } } as Parameters<Chart['createIndicator']>[2],
          )
          if (paneId) {
            this.subPaneIds.set(name, paneId as string)
            this.subPaneRatios.set(paneId as string, ratio)
          }
        } else {
          // MTF sub-pane instance (ATR from a higher timeframe): same
          // always-alias rule as the main pane — the clone calc is the only
          // one that honours extendData.timeframe.
          let instanceName = name
          if (sourceTimeframe && isDuplicableIndicator(name)) {
            instanceName = allocateInstanceName(name, new Set([...usedSub, name]))
            ensureInstanceRegistered(instanceName, name)
          }
          usedSub.add(instanceName)
          this.subInstanceNames[i] = instanceName
          const value: Record<string, unknown> = { name: instanceName }
          if (calcParams) value.calcParams = calcParams
          if (sourceTimeframe) {
            value.extendData = this.mtfExtendData(instanceName, instanceName, {
              timeframe: sourceTimeframe,
            })
          }
          const paneId = this.chart.createIndicator(
            value as Parameters<Chart['createIndicator']>[0],
            false,
            { height } as Parameters<Chart['createIndicator']>[2],
          )
          if (paneId) {
            this.subPaneIds.set(instanceName, paneId as string)
            this.subPaneRatios.set(paneId as string, ratio)
            this.retrackMtfPane(instanceName, paneId as string)
          }
        }
      })
      this.chart.resize()
      this.notifyLayoutChanged()
    }

    // Update stored ratios when user manually drags pane separators
    this.chart.subscribeAction(ActionType.OnPaneDrag, () => {
      this.syncPaneRatios()
      this.notifyLayoutChanged()
    })

    // Drawing keyboard actions: Delete/Backspace removes, ⌘/Ctrl-C copies,
    // ⌘/Ctrl-V duplicates. Container-scoped (see tabindex below) so only the
    // focused chart responds, and never while typing in a field.
    this.keydownHandler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return
      }

      // ⌘/Ctrl-C — remember the selected drawing for a later paste. Shift/Alt
      // chords are left alone (they belong to other copy semantics).
      const mod = e.ctrlKey || e.metaKey
      if (mod && !e.altKey && !e.shiftKey && (e.key === 'c' || e.key === 'C')) {
        if (!this.selectedOverlayId) return
        this.copiedOverlayId = this.selectedOverlayId
        e.preventDefault()
        return
      }
      // ⌘/Ctrl-V — duplicate the copied drawing (offset like the quick bar's
      // Duplicate). Falls back to the live selection so paste works without an
      // explicit copy; a stale copied id (original since deleted) self-clears.
      if (mod && !e.altKey && !e.shiftKey && (e.key === 'v' || e.key === 'V')) {
        const srcId = this.copiedOverlayId ?? this.selectedOverlayId
        if (!srcId) return
        e.preventDefault()
        if (!this.duplicateOverlay(srcId)) this.copiedOverlayId = null
        return
      }

      // Delete/Backspace removes the selected overlay. Route through
      // removeOverlay so the deselect event + style cleanup happen here too,
      // rather than only on the quick bar's delete button.
      if (!this.selectedOverlayId) return
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      e.preventDefault()
      this.removeOverlay(this.selectedOverlayId)
    }
    this.container.addEventListener('keydown', this.keydownHandler)
    // Make container focusable so it receives keyboard events
    if (!this.container.getAttribute('tabindex')) {
      this.container.setAttribute('tabindex', '-1')
      this.container.style.outline = 'none'
    }

    // ── Drawing-tool hotkeys: h = horizontal line, v = vertical line, r = ray
    // line. They place the overlay exactly where the cursor sits, so they hang
    // off real local hover (not focus) — the chart the pointer is over is the
    // one that responds.
    this.pointerEnterHandler = () => {
      this.isPointerOver = true
      // "Hold Shift, then move onto the chart" should still arm history mode.
      if (this.altHeld) this.updateBarHighlight()
    }
    this.pointerLeaveHandler = () => {
      this.isPointerOver = false
      this.lastCursor = null
      // Restore the normal chart once the pointer leaves.
      this.clearBarHighlight()
    }
    this.container.addEventListener('pointerenter', this.pointerEnterHandler)
    this.container.addEventListener('pointerleave', this.pointerLeaveHandler)

    // Double-click the time (x) axis to reset the horizontal zoom to default and
    // scroll back to the latest bar. The x-axis is the strip
    // below every pane's content, so we treat a double-click below the lowest
    // pane bottom as an axis click.
    this.dblclickHandler = (e: MouseEvent) => {
      const rect = this.container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      // Double-click a pane's price (y) axis to release a manually-scaled axis
      // back to auto-fit — the vertical analogue of the time-axis reset below.
      const yAxisPaneId = this.paneIdAtYAxis(x, y)
      if (yAxisPaneId) {
        e.preventDefault()
        // A double click on an alert's label cancels it (spec C3). klinecharts never hands
        // y-axis double clicks to figures (KC:12925-12934), so the labels are hit-tested
        // here; klinecharts has already released a hand-scaled axis (KC:12927-12931).
        if (yAxisPaneId === CANDLE_PANE_ID) {
          // klinecharts raises its double click on the second mouseup and, on a hand-scaled
          // axis, snaps back to auto there (KC:12266-12274, 12927-12931), before this DOM
          // dblclick runs. The label has usually moved by now, so the alert pressed on
          // wins when this double click lands where that press began.
          const press = this.axisAlertPress
          this.axisAlertPress = null
          if (
            press &&
            Date.now() - press.at <= AXIS_ALERT_PRESS_MAX_MS &&
            Math.hypot(e.clientX - press.x, e.clientY - press.y) <= AXIS_ALERT_PRESS_MAX_PX &&
            this.cancelPriceAlertById(press.id)
          ) {
            return
          }
          if (this.cancelPriceAlertAt(e.clientX, e.clientY)) return
        }
        this.resetYAxis(yAxisPaneId)
        return
      }
      if (this.isInTimeAxisBand(y)) {
        e.preventDefault()
        this.resetTimeScale()
        return
      }
      // Body double-click (not an axis, not a drawing) → reset the whole chart:
      // every pane's price axis back to auto-fit + the time scale. ThinkorSwim-
      // style, completing the per-axis resets above. A double-click on a drawing
      // opens its settings instead (guarded via the overlay onDoubleClick flag).
      if (this.overlayEditFromDblClick) {
        this.overlayEditFromDblClick = false
        return
      }
      e.preventDefault()
      for (const pid of this.allPaneIds()) this.resetYAxis(pid)
      this.resetTimeScale()
    }
    this.container.addEventListener('dblclick', this.dblclickHandler)

    // Remember which alert label a left press on the price axis lands on (capture phase, so
    // before klinecharts handles the press); the double click above cancels that one.
    this.axisAlertPressHandler = (e: MouseEvent) => {
      this.axisAlertPress = null
      if (e.button !== 0) return
      // The alert lookup first: it bails before any DomPosition read when nothing can be
      // cancelled, which test harnesses that mock klinecharts without it rely on.
      const id = this.priceAlertIdAt(e.clientX, e.clientY)
      if (!id) return
      const rect = this.container.getBoundingClientRect()
      if (this.paneIdAtYAxis(e.clientX - rect.left, e.clientY - rect.top) !== CANDLE_PANE_ID) {
        return
      }
      this.axisAlertPress = { id, x: e.clientX, y: e.clientY, at: Date.now() }
    }
    this.container.addEventListener('mousedown', this.axisAlertPressHandler, { capture: true })

    // Scroll-wheel over a pane's price (y) axis zooms that axis' scale — the
    // vertical analogue of wheel-over-chart time zoom.
    // Installed in the capture phase and BEFORE the optional lastBar wheel
    // handler (added later by setZoomAnchor), so when the pointer is over a
    // y-axis this consumes the event and the time-zoom path never fires.
    this.yAxisWheelHandler = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
      const rect = this.container.getBoundingClientRect()
      const paneId = this.paneIdAtYAxis(e.clientX - rect.left, e.clientY - rect.top)
      if (!paneId) return
      // Scroll up → zoom in (shrink the range); down → zoom out. In proportion
      // to wheel travel, so a trackpad's many small events don't each step 10%.
      const factor = priceWheelFactor(wheelDeltaPx(e.deltaY, e.deltaMode))
      if (this.scaleYAxis(paneId, factor)) {
        e.preventDefault()
        e.stopImmediatePropagation()
      }
    }
    this.container.addEventListener('wheel', this.yAxisWheelHandler, {
      capture: true,
      passive: false,
    })

    // Track the live cursor price / timestamp / pane so a keypress can anchor an
    // overlay at it. Separate from `subscribeCrosshair` (the sync path) because
    // we want the raw local position regardless of any sync wiring.
    this.cursorTrackHandler = (raw: Crosshair) => {
      if (raw.x == null || raw.y == null || !raw.paneId) {
        this.lastCursor = null
        return
      }
      const point = this.chart.convertFromPixel([{ x: raw.x, y: raw.y }], { paneId: raw.paneId })
      const pt = Array.isArray(point) ? point[0] : point
      if (pt?.value == null) return
      const k = raw.kLineData
      this.lastCursor = {
        timestamp: k?.timestamp ?? 0,
        value: pt.value,
        paneId: raw.paneId,
        high: k?.high,
        low: k?.low,
        open: k?.open,
        close: k?.close,
      }
      // Keep the yellow candle highlight glued to whatever bar the cursor is over.
      if (this.altHeld) this.updateBarHighlight()
      this.applyMagnetToCrosshair(raw)
    }
    this.chart.subscribeAction(ActionType.OnCrosshairChange, this.cursorTrackHandler)

    this.setupAltHistoryMode()

    this.setupEscapeDrawCancel()

    this.setupDrawingHotkeys()

    // A mousedown anywhere OUTSIDE the chart deselects the active overlay.
    // klinecharts only handles clicks on its own canvas, so without this a
    // selected drawing stays selected (and now, grab-handles-visible) when the
    // user clicks another panel / the toolbar / empty page. Capture phase so it
    // runs regardless of downstream stopPropagation.
    this.outsideDeselectHandler = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (target && this.container.contains(target)) return
      // Chart CHROME (the floating quick-edit bar, and anything else that opts
      // in) lives OUTSIDE this container but is still part of the chart — it is
      // a sibling of the canvas div, not a child. Without this, mousedown on
      // the bar deselects the very drawing it edits and unmounts it, so no
      // control on it can ever fire. Capture-phase means a React
      // `stopPropagation` cannot help; the check has to live here.
      if (target instanceof Element && target.closest('[data-chart-chrome]')) return
      this.deselectOverlay()
    }
    this.addGlobalListener('document', 'mousedown', this.outsideDeselectHandler, {
      capture: true,
    })

    // Set initial focus anchor before first load (for historical rewind)
    if (options.initialFocusTimestamp != null) {
      chartDebug(
        'constructor: setting initial anchor',
        new Date(options.initialFocusTimestamp).toISOString(),
      )
      this.dataLayer.setAnchorTimestamp(options.initialFocusTimestamp)
      // Anchored from birth: the buffer's last bar is historical, so the native
      // last-price mark would mislabel an old close as "current". Hide it now
      // (theme already applied via init) rather than waiting for a focus change.
      this.setLastPriceMarkVisible(false)
    }

    // Expose on window for console debugging
    if (_win) {
      _win.__chart = this
      _win.__chartDebug = _win.__chartDebug ?? false
    }

    // Track foreground load so prefetch can defer until it settles — running
    // both in parallel competes for backend compute and stalls the visible
    // chart. Prefetch chains AFTER the foreground load resolves; floor lookup
    // also has time to settle so we can clamp the prefetch window properly.
    this.currentLoad = this.load()
    this.currentLoad.catch(() => undefined).finally(() => this.prefetchDailyAndCoarser())
    this.primeHtfSeries()
  }

  /**
   * extendData for an MTF instance. A session timeframe (1D/1W) gets the
   * daily-series reader and the chart clock attached, and the instance is
   * tracked so the series is fetched and the line recomputed when it lands.
   * Anything else passes through untouched (and stops being tracked), so the
   * extendData other indicators carry is never altered.
   */
  private mtfExtendData(name: string, paneId: string, extendData: unknown): unknown {
    const timeframe = (extendData as MtfExtendData | null | undefined)?.timeframe
    if (!isSessionTimeframe(timeframe)) {
      this.mtfSessionInstances.delete(name)
      return extendData
    }
    this.mtfSessionInstances.set(name, { paneId, timeframe })
    const ext: MtfExtendData = {
      ...(extendData as MtfExtendData),
      readHtf: this.readHtfRows,
      nowMs: this.mtfNowMs,
    }
    return ext
  }

  /** A sub-pane instance learns its pane id only once created. */
  private retrackMtfPane(name: string, paneId: string): void {
    const tracked = this.mtfSessionInstances.get(name)
    if (tracked) tracked.paneId = paneId
  }

  /** The daily rows a 1D/1W calc reads; undefined (no line) while they load
   *  or while another ticker's bars are still on screen. */
  private readonly readHtfRows = (): readonly SessionRow[] | undefined => {
    if (this.mtfBarsTicker !== this.symbol.ticker) return undefined
    return this.htfSeries.rows()
  }

  /**
   * The chart's present for a 1D/1W calc. Replay-aware: during a replay the
   * clock is the end of the last replayed sub-bar, so the 09:00 bar joins
   * today's session only once replayed trades pass 09:30 and no later day
   * reads as developing.
   */
  private readonly mtfNowMs = (): number => {
    const replay = this.replay?.getState()
    if (replay?.active && replay.cursorTs != null) {
      return replay.cursorTs + subPeriodFor(this.period).ms
    }
    return Date.now()
  }

  /** Fetch (or join) the daily series once any instance needs one. Chained
   *  behind the foreground load like the preload, so the ticker floor is known
   *  and the window matches the one our 1D chart and the preload request. */
  private primeHtfSeries(): void {
    if (this.mtfSessionInstances.size === 0) return
    void (this.currentLoad ?? Promise.resolve())
      .catch(() => undefined)
      .finally(() => {
        if (this.mtfSessionInstances.size > 0) this.htfSeries.ensure()
      })
  }

  /** The daily series landed: hand each session instance a fresh extendData
   *  object, which is what makes klinecharts re-run its calc. */
  private refreshMtfSessionInstances(): void {
    for (const [name, { paneId }] of this.mtfSessionInstances) {
      const extendData = { ...(this.getIndicatorExtendData(paneId, name) ?? {}) }
      this.chart.overrideIndicator(
        { name, extendData } as Parameters<Chart['overrideIndicator']>[0],
        paneId,
      )
    }
    this.eventBus.emit('mtf:series', undefined)
  }

  private getIndicatorExtendData(paneId: string, name: string): MtfExtendData | undefined {
    const live = this.getIndicatorState(paneId, name) as { extendData?: MtfExtendData } | null
    if (live?.extendData) return live.extendData
    const tracked = this.mtfSessionInstances.get(name)
    return tracked
      ? (this.mtfExtendData(name, paneId, { timeframe: tracked.timeframe }) as MtfExtendData)
      : undefined
  }

  /**
   * Rows a 1D/1W indicator on this chart computes over (sessions, or weeks),
   * or undefined while the daily series loads. The settings drawer compares it
   * with the indicator's period to name a shortfall ("Needs 200 daily bars,
   * 126 available"). Listen for `mtf:series` to learn when it changes.
   */
  getMtfSourceBarCount(timeframe: string): number | undefined {
    if (!isSessionTimeframe(timeframe)) return undefined
    // A daily chart's weekly line groups the chart's own sessions.
    if (!isIntradayPeriod(this.period)) {
      return weeklyRows(dailyRowsFromBars(this.chart.getDataList())).rows.length
    }
    const fetched = this.readHtfRows()
    if (!fetched) return undefined
    return sessionRowCount(timeframe, this.chart.getDataList(), {
      fetched,
      chartMs: this.period.ms,
      nowMs: this.mtfNowMs(),
    })
  }

  /**
   * Skip the prefetch when the chart is hidden (collapsed panel, off-screen,
   * background tab). Keeps a workspace with many chart panels from firing
   * dozens of background fetches on mount before the user is even looking.
   */
  private isVisible(): boolean {
    if (typeof document !== 'undefined' && document.hidden) return false
    return this.container.clientWidth > 0
  }

  /**
   * Compute the same window `DataLayer.loadHistory()` would use for a
   * foreground load — `to = Date.now()`, `from = to - pageMsFromBars(period)`.
   * Anchor mode is intentionally ignored: the prefetch always grabs the
   * most-recent page so a later un-anchored switch into the cached period
   * has the right data. (Anchored switches re-fetch via the miss path.)
   *
   * Wires the supplied `AbortSignal` through to the underlying `fetch()` so
   * the cache layer can cancel the network request when the entry is evicted
   * (LRU rotation or `evictTicker`).
   */
  private fetchPeriodWindow(
    symbol: EdgeSymbol,
    period: Period,
    signal: AbortSignal,
    // Anchored session-timeframe series end at the chart's date instead; every
    // other caller (and a live chart's series) keeps "now", so their URLs match.
    to: number = Date.now(),
  ): Promise<EdgeBar[]> {
    const rawFrom = to - pageMsFromBars(period)
    // Clamp to the per-ticker floor when known. When the preload window reaches
    // before the ticker's earliest date (a wide daily page, or a coarser period
    // if one is re-added to PREFETCH_TARGETS), this avoids a huge wasted backend
    // range scan for years the ticker didn't exist.
    const floor = this.dataLayer.getFloor()
    const from = floor != null && rawFrom < floor ? floor : rawFrom
    // Thread the active `adjusted` flag into the prefetch URL so the cache
    // entry's bars match the user's mode. Without this, prefetch always hit
    // the default URL (adjusted=true) and a user with adjusted=false would see
    // split-adjusted bars on D/W/M cache hits. extendedHours doesn't apply to
    // daily+ resolutions (intraday-only param), so it's not threaded.
    return this.datafeed.getHistoryBars(symbol, period, from, to, {
      signal,
      adjusted: this.dataLayer.isAdjusted(),
    })
  }

  /**
   * Fire-and-forget background fetch for the coarse preload target(s) — see
   * PREFETCH_TARGETS (currently daily only). Skipped if the chart is not
   * visible or if the cache already holds a fresh entry. The
   * cache stores the in-flight promise + its `AbortController` so:
   *   - a concurrent `setPeriod` into the same period shares the result
   *     (no duplicate request);
   *   - evicting the entry (LRU when MAX_TICKERS is exceeded, or
   *     `evictTicker`) aborts the underlying fetch — saving the network
   *     round-trip when the user rapidly switches tickers.
   */
  private prefetchDailyAndCoarser(): void {
    if (!this.enablePreload) return
    if (!this.isVisible()) return
    // Cache key + URL must match the user's current mode — a fresh entry put
    // under the current mode is what `setPeriod` will look up.
    const modeOpts = { adjusted: this.dataLayer.isAdjusted() }
    for (const periodKey of ChartManager.PREFETCH_TARGETS) {
      const existing = this.cache.get(this.symbol.ticker, periodKey, modeOpts)
      if (existing && !this.cache.isStale(this.symbol.ticker, periodKey, modeOpts)) continue
      const controller = new AbortController()
      const promise = this.fetchPeriodWindow(this.symbol, PERIODS[periodKey], controller.signal)
      // Suppress unhandled-rejection on AbortError — the cache's bookkeeping
      // `.catch` swallows it, but the promise reference we hold here also
      // needs a no-op `.catch` to satisfy the runtime.
      promise.catch(() => undefined)
      this.cache.set(this.symbol.ticker, periodKey, promise, controller, modeOpts)
    }
  }

  /** Read current pane pixel sizes and update stored ratios */
  private syncPaneRatios(): void {
    const totalHeight = this.container.clientHeight
    if (totalHeight <= 0) return
    for (const [, paneId] of this.subPaneIds) {
      const size = this.chart.getSize(paneId)
      if (size && size.height > 0) {
        this.subPaneRatios.set(paneId, size.height / totalHeight)
      }
    }
  }

  /** Configure date formatter — daily+ timespans show date only, intraday shows date + time */
  private applyDateFormatter(): void {
    this.chart.setCustomApi({
      formatDate: (dateTimeFormat, timestamp, format, type) => {
        // Read the timespan LIVE at format time. Capturing it once let a daily
        // chart keep the intraday "HH:mm" x-axis / crosshair label until the next
        // re-apply (e.g. after switching up from a 5m chart) — daily+ has no
        // intraday time, so it must always render date-only.
        const ts = this.period.timespan
        const isDailyOrHigher = ts === 'day' || ts === 'week' || ts === 'month' || ts === 'year'
        if (
          isDailyOrHigher &&
          (type === FormatDateType.XAxis || type === FormatDateType.Crosshair)
        ) {
          const parts = dateTimeFormat.formatToParts(timestamp)
          const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
          return `${get('year')}-${get('month')}-${get('day')}`
        }
        // Intraday day break: klinecharts asks for 'MM-DD' ("08-25", which reads
        // like the time 08:25). Render "Aug 25" instead. Its day-change check
        // compares two of these strings, so any stable per-day output works.
        if (type === FormatDateType.XAxis && format === 'MM-DD') {
          return formatDayBreakLabel(timestamp, dateTimeFormat.resolvedOptions().timeZone)
        }
        return utils.formatDate(dateTimeFormat, timestamp, format)
      },
    })
  }

  private ready = false
  /** Whether the `data:updated → updateAxisWidth` listener has been wired. Kept
   *  separate from `ready` (which is reset on every setPeriod) so the listener
   *  is subscribed exactly once — otherwise each period change leaks another. */
  private axisWidthWired = false
  private loadGeneration = 0

  /**
   * Mark the chart ready and (re)emit `chart:ready`. Wires the
   * `data:updated → updateAxisWidth` listener on the FIRST call only. Called
   * from every post-load fast-path (initial load, cache hit, 1m downsample) —
   * previously each inlined the wire-up, leaking a listener per period change.
   */
  private markReady(): void {
    this.ready = true
    if (!this.axisWidthWired) {
      this.axisWidthWired = true
      this.eventBus.on('data:updated', () => this.updateAxisWidth())
      // Auto-analysis follows the BAR, not the tick: a timestamp change means
      // a bar closed and a new one opened, which is when a level or swing can
      // actually change. Debounced so a burst of first ticks is one recompute.
      this.eventBus.on('data:updated', (payload: { bar?: { timestamp?: number } }) => {
        const ts = payload?.bar?.timestamp
        if (ts == null || ts === this.lastAutoAnalysisBarTs) return
        this.lastAutoAnalysisBarTs = ts
        this.scheduleAutoAnalysisRefresh()
      })
    }
    this.eventBus.emit('chart:ready', undefined)
  }
  /** Tracks the in-flight foreground load so prefetch can defer behind it. */
  private currentLoad: Promise<void> | null = null

  /** True once the bar width differs from the one the chart picked itself. */
  private hasUserZoom(): boolean {
    const chosen = this.chosenBarSpace
    return chosen == null || Math.abs(this.chart.getBarSpace() - chosen) > 1e-6
  }

  /** Record the current width as the chart's own choice, clearing user-zoom. */
  private markChosenBarSpace(): void {
    this.chosenBarSpace = this.chart.getBarSpace()
  }

  /**
   * Remember what a zoomed anchored chart is showing so the load a ticker or
   * date click starts can restore it. No-op on a chart nobody zoomed (it keeps
   * framing the session), on live charts, in replay, or if already armed (a
   * Records row click fires setSymbol then setFocusTimestamp: the first view
   * wins, the second click must not re-read a half-loaded one).
   */
  private armKeptZoom(): void {
    if (this.pendingKeptZoom || !this.ready || this.replay?.getState().active) return
    const anchor = this.dataLayer.getAnchorTimestamp()
    if (anchor == null || !this.hasUserZoom()) return
    // Daily bars sit at midnight UTC, which buckets into the previous ET day, so
    // a time-of-day window would drift a session per click. They keep width only.
    const view = isIntradayPeriod(this.period) ? this.viewWithoutRightMargin() : null
    const kept = view && windowFromRange(anchor, view.from, view.to, this.getLoadedBars())
    this.pendingKeptZoom = {
      // Only the right edge places the view (`from` just finds a bar), so widen the
      // start to the open: a stretch with no trades must not fall back to the session end.
      window: kept && {
        ...kept,
        startMin: Math.min(kept.startMin, DEFAULT_ANCHOR_WINDOW.startMin),
      },
    }
  }

  /**
   * The bars in view, minus the empty room on the right. `setVisibleDateRange`
   * leaves `rightMarginBars` of room after the bar it lands on, so the right
   * edge is the bar `rightMarginBars` before the last bar's right-side gap.
   * The rounded visible range cannot give it back: its `to` is
   * `round(gap + n + 0.5)`, which sits on a .5 boundary (one bar off, and float
   * noise can flip it), and `getOffsetRightDistance` clamps at 0 once the view
   * is mid-data. So read the exact gap from the time scale store.
   */
  private viewWithoutRightMargin(): { from: number; to: number } | null {
    const data = this.chart.getDataList()
    if (data.length === 0) return null
    const internal = this.chart as unknown as {
      getChartStore?: () => {
        getTimeScaleStore?: () => { getLastBarRightSideDiffBarCount: () => number }
      }
    }
    const gap = internal.getChartStore?.().getTimeScaleStore?.().getLastBarRightSideDiffBarCount()
    if (gap == null) return null
    const last = data.length - 1
    const fromIdx = Math.min(last, Math.max(0, this.chart.getVisibleRange().from))
    const toIdx = Math.min(last, Math.max(fromIdx, Math.round(last + gap - this.rightMarginBars)))
    return { from: data[fromIdx].timestamp, to: data[toIdx].timestamp }
  }

  /**
   * Stretch bars when the just-loaded ticker is genuinely thin (real floor,
   * not lookback cap). Skips for `'lookback'` and `null`. Called from `load()`
   * and the cache-hit branch in `setPeriod()`.
   */
  private applyAutoFitIfThin(): void {
    if (this.dataLayer.getScrollBackExhaustedReason() !== 'floor') return
    const barCount = this.chart.getDataList().length
    if (barCount === 0) return
    const canvasWidth = this.container.clientWidth
    if (canvasWidth <= 0) return
    // A zoom the user chose outranks the stretch.
    if (this.hasUserZoom()) return
    // Reserve room for `RIGHT_OFFSET_BARS` worth of trailing space so the
    // stretched chart still has the same breathing room as a normal one.
    const raw = canvasWidth / (barCount + this.rightMarginBars)
    const barSpace = clamp(raw, MIN_BAR_SPACE, MAX_BAR_SPACE)
    this.chart.setBarSpace(barSpace)
    this.markChosenBarSpace()
    this.chart.setOffsetRightDistance(this.rightMarginBars * barSpace)
  }

  /**
   * Show a rocket glyph under the first bar when scroll-back reached the
   * per-ticker floor (real thin history), and only then: the soft lookback
   * cap (`'lookback'`) means older data exists, so nothing is drawn. The
   * glyph carries no text; it does not claim an IPO or a founding date.
   * Idempotent: called on every exhaustion change and history load.
   */
  private syncHistoryStartMarker(): void {
    const first = this.chart.getDataList()[0] as { timestamp: number; close?: number } | undefined
    const show = this.dataLayer.getScrollBackExhaustedReason() === 'floor' && first != null
    if (!show) {
      if (this.historyStartMarkerId) {
        this.chart.removeOverlay(this.historyStartMarkerId)
        this.historyStartMarkerId = null
        this.historyStartMarkerTs = null
      }
      return
    }
    if (this.historyStartMarkerId && this.historyStartMarkerTs === first.timestamp) return
    const points = [{ timestamp: first.timestamp, value: first.close ?? 0 }]
    if (this.historyStartMarkerId && this.chart.getOverlayById(this.historyStartMarkerId)) {
      this.chart.overrideOverlay({ id: this.historyStartMarkerId, points } as Parameters<
        Chart['overrideOverlay']
      >[0])
    } else {
      this.historyStartMarkerId =
        (this.chart.createOverlay({
          name: HISTORY_START_MARKER_NAME,
          points,
          lock: true,
        } as Parameters<Chart['createOverlay']>[0]) as string | null) ?? null
    }
    this.historyStartMarkerTs = first.timestamp
  }

  /**
   * Pin pan to the first bar on exhaustion; clear on reset. At the real
   * floor a little blank room stays left of the first bar so the
   * history-start glyph centered on it is never cut by the pane edge.
   */
  private applyPanGuard(reason: ScrollBackExhaustedReason): void {
    const setMaxOffsetLeft = (
      this.chart as unknown as { setMaxOffsetLeftDistance?: (d: number) => void }
    ).setMaxOffsetLeftDistance
    if (typeof setMaxOffsetLeft !== 'function') return
    const distance =
      reason === null
        ? PAN_GUARD_RESET_DISTANCE
        : reason === 'floor'
          ? HISTORY_START_MARKER_LEFT_ROOM
          : 0
    setMaxOffsetLeft.call(this.chart, distance)
  }

  private async load(): Promise<void> {
    const gen = ++this.loadGeneration
    // Veil signal for the host: a full reload is in flight, so the on-screen
    // bars (kept — see setSymbol / applyFocusTimestamp) are stale. EdgeChart
    // dims the canvas between pending and settled. `settled` is gen-gated so an
    // overlapping newer load keeps the veil up until IT finishes.
    this.eventBus.emit('load:pending', {})
    try {
      await this.loadInner(gen)
    } finally {
      if (gen === this.loadGeneration) {
        this.swapPending = false
        this.eventBus.emit('load:settled', {})
      }
    }
  }

  private async loadInner(gen: number): Promise<void> {
    chartDebug(
      'load: start gen =',
      gen,
      'anchor =',
      this.dataLayer.getAnchorTimestamp()
        ? new Date(this.dataLayer.getAnchorTimestamp()!).toISOString()
        : 'null',
    )
    const loaded = await this.dataLayer.loadHistory(this.symbol, this.period)
    if (gen !== this.loadGeneration) {
      chartDebug('load: stale gen', gen, '(current', this.loadGeneration, '), skipping')
      return
    }
    if (!loaded) {
      // The page never landed (retry budget exhausted, or aborted). Do NOT
      // subscribe: the stream would paint a lone forming candle on whatever
      // is left on screen — the exact "only the last bar" symptom. The
      // `data:error` handler has already cleared stale bars if a swap was
      // pending; the next symbol/period change (or reload) re-fetches.
      chartDebug('load: failed gen', gen, '— skipping realtime subscribe')
      // Nothing landed to frame: drop a reset the swap armed rather than leave
      // it for some later, unrelated load to apply.
      this.pendingSymbolZoomReset = false
      this.pendingKeptZoom = null
      return
    }
    const dataList = this.chart.getDataList()
    chartDebug(
      'load: done, bars =',
      dataList.length,
      dataList.length > 0
        ? `first = ${new Date(dataList[0].timestamp).toISOString()}, last = ${new Date(dataList[dataList.length - 1].timestamp).toISOString()}`
        : '',
    )
    this.updateAxisWidth()
    this.applyAutoFitIfThin()
    this.refreshAutoAnalysis()
    const anchor = this.dataLayer.getAnchorTimestamp()
    // "Reset zoom on ticker change": these are the new ticker's bars, so frame
    // them the way the reset gesture does. On an anchored (History) chart that
    // replaces the focus-date fit, exactly as a double-click would.
    const resetZoom = this.pendingSymbolZoomReset
    this.pendingSymbolZoomReset = false
    // A click on a zoomed chart armed this; consume it with the load it started.
    const keptZoom = this.pendingKeptZoom
    this.pendingKeptZoom = null
    if (anchor == null) {
      this.dataLayer.subscribe(this.symbol, this.period)
    } else if (!resetZoom) {
      chartDebug('load: calling zoomToDate', new Date(anchor).toISOString())
      this.zoomToDate(anchor, keptZoom)
      // Log what's visible after zoom (deferred to after render)
      requestAnimationFrame(() => {
        const dl = this.chart.getDataList()
        const visible = this.chart.getVisibleRange()
        chartDebug('load: visible after zoom', {
          from: visible.from,
          to: visible.to,
          firstVisible:
            visible.from >= 0 && dl[visible.from]
              ? new Date(dl[visible.from].timestamp).toISOString()
              : 'n/a',
          lastVisible:
            visible.to >= 0 && visible.to < dl.length && dl[visible.to]
              ? new Date(dl[visible.to].timestamp).toISOString()
              : 'n/a',
          visibleBars: visible.to - visible.from + 1,
        })
      })
    }
    if (resetZoom) this.resetTimeScale()
    if (!this.ready) {
      this.markReady()
    }
  }

  /**
   * Hide the price & time axis scales. The crosshair price/time labels still
   * render at the chart edges: klinecharts' getAutoSize collapses the axis
   * strip to `max(axisSize, crosshairLabelSize)`, so with the ticks hidden the
   * y-axis shrinks to exactly the floating price label's width. We must switch
   * the y-axis to size:'auto' for that collapse to apply (the dynamic
   * width-pinner otherwise holds an explicit tier) and stand the pinner down
   * while hidden so it doesn't re-pin a numeric width on the next data tick.
   */
  setHideAxisScale(hide: boolean): void {
    this.hideAxisScale = hide
    const axis = {
      axisLine: { show: !hide },
      tickLine: { show: !hide },
      tickText: { show: !hide },
    }
    this.chart.setStyles({
      // XAxisStyle has no `inside` (unlike yAxis), so the native crosshair time
      // label can't overlay. Instead collapse the x-axis strip to 1px while
      // hidden (klinecharts' getAutoSize returns the numeric size verbatim, so
      // the dead strip below the volume pane disappears) — EdgeChart renders a
      // CrosshairTimeLabel overlay to replace the floating time label. On
      // un-hide restore the theme/klinecharts default of size:'auto'.
      xAxis: hide ? { ...axis, size: 1 } : { ...axis, size: 'auto' },
      // inside:true while hidden so the candle/time area spans the full width
      // (no reserved right-hand gap under the time scale) and the crosshair
      // price label overlays at the right edge instead of in a reserved strip.
      yAxis: hide ? { ...axis, inside: true, size: 'auto' } : { ...axis, inside: false },
    } as Parameters<typeof this.chart.setStyles>[0])
    // Scrolling AND zooming the chart stay fully available with the scales
    // hidden — but the AXES' own scroll-zoom gets locked: the invisible
    // y-axis strip on the right would otherwise still capture scrolls and
    // silently re-scale the price range (kicking it off auto-fit) with no
    // visible feedback. Locked = the price scale stays automatic; restored
    // when the scales come back. NB: klinecharts' setPaneOptions is a no-op
    // without an `id`, so this must be applied per pane (candle + subs).
    for (const paneId of this.allPaneIds()) {
      this.chart.setPaneOptions({
        id: paneId,
        axisOptions: { scrollZoomEnabled: !hide },
      } as Parameters<typeof this.chart.setPaneOptions>[0])
    }
    if (!hide) {
      // Re-pin the explicit y-axis width tier the dynamic sizer maintains.
      this.axisWidthTier = 0
      this.updateAxisWidth()
    }
    // The alert labels move between the axis and the pane.
    this.syncPriceAlertLines()
    this.notifyLayoutChanged()
  }

  /** Set Y-axis width based on max visible value magnitude (stepped to avoid jitter) */
  private updateAxisWidth(): void {
    // While the scales are hidden the y-axis is intentionally size:'auto' so it
    // collapses to the crosshair label — don't fight that with an explicit pin.
    if (this.hideAxisScale) return
    const data = this.chart.getDataList()
    if (data.length === 0) return
    const range = this.chart.getVisibleRange()

    let maxPrice = 0
    for (let i = Math.max(0, range.from); i < Math.min(data.length, range.to + 1); i++) {
      const d = data[i]
      if (!d) continue
      maxPrice = Math.max(maxPrice, d.high ?? 0)
    }
    // Alert labels on screen join the tier's magnitude; precision follows the bars only.
    const tierPrice = Math.max(maxPrice, this.visiblePriceAlertMax() ?? 0)

    // Precision scales with price magnitude. Penny / sub-penny tickers need
    // MORE decimals, not fewer: at 2 decimals every bar on a ~$0.04 ticker
    // rounds to the same 0.04, AND — critically — klinecharts derives its
    // y-axis "min ≈ max" threshold as 10^(-precision) and pads the range by
    // ±4 of it. With precision 2 that threshold is $0.01, larger than the
    // whole price range of a sub-penny name, so the axis inflates to ~[0,0.08]
    // and the candles flatten into a line. More decimals shrink the threshold
    // below the real range and the auto-scale behaves again. ≥10k drops
    // decimals entirely (fractional cents are noise at that magnitude).
    let desiredPricePrecision: number
    if (maxPrice >= 10000) desiredPricePrecision = 0
    else if (maxPrice >= 1) desiredPricePrecision = 2
    else if (maxPrice >= 0.01) desiredPricePrecision = 4
    else desiredPricePrecision = 6

    // Stepped y-axis width tiers based on price magnitude. Tuned to sit between
    // the klinecharts auto-size (very tight, ~30–35px) and the previous wider
    // tiers (55–85px). Sub-$1 tickers now carry 4–6 decimals, so widen by the
    // extra label characters; ≥10k loses decimals so its tier can stay narrow.
    // NB: the tier must fit the widest *displayed* gridline, which rounds UP past
    // the data max — a chart topping out at ~$95 still labels a "100.00" gridline,
    // so the <100 tier carries a full extra magnitude's worth of glyphs.
    let tier: number
    if (tierPrice < 100) tier = 46
    else if (tierPrice < 1000) tier = 52
    else if (tierPrice < 10000) tier = 58
    // An alert above $10k over bars still under it keeps the bars' decimals ("10050.00"), a
    // digit wider per magnitude than the 4-digit tier, so the `< 100000` tier below (sized
    // for no decimals) would be narrower than the chart is without the alert.
    else if (desiredPricePrecision > 0) tier = 58 + 6 * (String(Math.floor(tierPrice)).length - 4)
    else if (tierPrice < 100000) tier = 54
    else tier = 66
    if (desiredPricePrecision > 2) tier += (desiredPricePrecision - 2) * 7
    // An alert label carries a bell the crosshair label does not (priceAlertLine.ts).
    if (this.priceAlertOverlayIds?.size) tier += ALERT_LABEL_AXIS_EXTRA

    // Set precision BEFORE the setStyles call below: setStyles triggers a
    // synchronous re-render via adjustPaneViewport() while
    // setPriceVolumePrecision() does not. If we called setStyles first the
    // redraw would still use the old precision.
    const currentPrecision = this.chart.getPriceVolumePrecision()
    const precisionChanged = currentPrecision.price !== desiredPricePrecision
    if (precisionChanged) {
      this.chart.setPriceVolumePrecision(desiredPricePrecision, currentPrecision.volume)
    }

    if (tier !== this.axisWidthTier) {
      this.axisWidthTier = tier
      this.chart.setStyles({ yAxis: { size: tier } } as Parameters<typeof this.chart.setStyles>[0])
      this.notifyLayoutChanged()
    } else if (precisionChanged) {
      // Precision changed but tier didn't — force a redraw so the y-axis
      // labels re-render with the right number of decimals.
      this.chart.setStyles({} as Parameters<typeof this.chart.setStyles>[0])
    }
  }

  /** The period the chart is currently loaded (or loading) for. */
  getPeriod(): Period {
    return this.period
  }

  getEventBus(): EventBus {
    return this.eventBus
  }

  /**
   * Fired after every pane relayout (indicator pane added/removed, container
   * resize, separator drag, y-axis width re-pin). klinecharts' own
   * `onVisibleRangeChange` only fires from a layout pass when the main-area
   * WIDTH changes, so overlays anchored to pane geometry (scroll-to-latest
   * button) need this to track height-only changes. Listeners should read
   * geometry on the next frame: klinecharts writes the new bounding after
   * some of its own action callbacks have already run.
   */
  private notifyLayoutChanged(): void {
    this.eventBus.emit(LAYOUT_CHANGED_EVENT, undefined)
  }

  getDataLayer(): DataLayer {
    return this.dataLayer
  }

  /** The loaded bars, ascending by timestamp (for anchor-relative viewport sync). */
  getLoadedBars(): ReadonlyArray<{ timestamp: number }> {
    return this.chart.getDataList()
  }

  getChart(): Chart {
    return this.chart
  }

  /**
   * Pixel top offset (relative to the chart root) of every sub-pane, keyed by
   * paneId. Used to anchor each sub-pane's indicator legend inside its own pane
   * rather than overlapping the candle-pane OHLC header.
   */
  getSubPaneTops(): Record<string, number> {
    const tops: Record<string, number> = {}
    for (const [, paneId] of this.subPaneIds) {
      const size = this.chart.getSize(paneId)
      if (size) tops[paneId] = size.top
    }
    return tops
  }

  /**
   * Pixel height of each tracked sub-pane, keyed by paneId. Parallel to
   * getSubPaneTops(); used to anchor lane rows inside a multi-lane strip pane.
   */
  getSubPaneHeights(): Record<string, number> {
    const heights: Record<string, number> = {}
    for (const [, paneId] of this.subPaneIds) {
      const size = this.chart.getSize(paneId)
      if (size) heights[paneId] = size.height
    }
    return heights
  }

  /**
   * Subscribe to pane separator drags (the user resizing a pane). Returns an
   * unsubscribe fn. Used by the boolean lane-strip to persist its height.
   */
  subscribeOnPaneDrag(cb: () => void): () => void {
    this.chart.subscribeAction(ActionType.OnPaneDrag, cb)
    return () => this.chart.unsubscribeAction(ActionType.OnPaneDrag, cb)
  }

  /** Current pixel height of a pane, or undefined if klinecharts has no size. */
  getPaneHeight(paneId: string): number | undefined {
    return this.chart.getSize(paneId)?.height
  }

  /** Total chart container height in pixels — the denominator for sub-pane
   *  height ratios (matches the total `syncPaneRatios` divides by). */
  getContainerHeight(): number {
    return this.container.clientHeight
  }

  /** Current pane height as a fraction of the container height (0..1), or
   *  undefined if either measurement is unavailable. Used to compare a
   *  persisted `paneHeightRatio` against the live layout without re-deriving
   *  the total on the caller side. */
  getPaneHeightRatio(paneId: string): number | undefined {
    const total = this.container.clientHeight
    const height = this.chart.getSize(paneId)?.height
    if (!total || total <= 0 || height == null || height <= 0) return undefined
    return height / total
  }

  /** The symbol the chart is showing (or swapping to). */
  getSymbol(): EdgeSymbol {
    return this.symbol
  }

  /**
   * Resolves true once the chart shows `ticker` with bars on screen, false on
   * timeout. For a host that must act on a chart AFTER a ticker change it
   * requested through the workspace (the Trade Planner placing levels): acting
   * during the swap would anchor on the previous ticker's bars, which the
   * in-place swap deliberately keeps on screen until the new ones land.
   */
  whenSymbolLoaded(ticker: string, timeoutMs = 8_000): Promise<boolean> {
    const loaded = () =>
      this.symbol.ticker.toUpperCase() === ticker.toUpperCase() &&
      !this.swapPending &&
      this.chart.getDataList().length > 0
    if (loaded()) return Promise.resolve(true)
    return new Promise((resolve) => {
      const finish = (ok: boolean) => {
        off()
        clearTimeout(timer)
        resolve(ok)
      }
      const off = this.eventBus.on('load:settled', () => {
        if (loaded()) finish(true)
      })
      const timer = setTimeout(() => finish(false), timeoutMs)
    })
  }

  setSymbol(symbol: EdgeSymbol): void {
    // Skip if already loaded with this symbol (avoids duplicate load on mount)
    if (this.symbol.ticker === symbol.ticker) return
    this.stopMotion()
    this.armKeptZoom()
    this.symbol = symbol
    // Per-symbol state that used to die with the REMOUNT (hosts keyed the chart
    // by ticker; they no longer do — in-place swap is the path now):
    //  • an active bar replay belongs to the old symbol's timeline — exit it;
    //  • the old ticker's drawings are per-symbol — remove their live overlays
    //    and reset the sync maps, so the host's useDrawingRestore (idempotent
    //    per syncId, re-fires when its per-ticker drawings prop changes) can
    //    lay down the NEW ticker's set on a clean slate.
    // Indicators deliberately survive the swap (in-place symbol swap), and so
    // does zoom unless the user opted into the reset below.
    if (this.replay?.getState().active) this.replay.exit()
    // The sweep is a VIEW change, never a delete. klinecharts fires each
    // overlay's onRemoved synchronously, and handleOverlayRemoved publishes a
    // sync `remove` for any mapped overlay, which the host persists as a
    // server DELETE. Unmap first and sweep under `isSyncing` so no event leaves:
    // an imperative setSymbol (outside a React commit, callback still bound to
    // the OLD ticker) would otherwise wipe every saved drawing on that ticker.
    const sweptIds = [...this.localToSyncId.keys()]
    this.clearOverlaySyncMappings()
    this.isSyncing = true
    try {
      for (const localId of sweptIds) this.chart.removeOverlay(localId)
    } finally {
      this.isSyncing = false
    }
    // Tell the host the overlay slate was wiped. EdgeChart's drawing-restore
    // effect runs BEFORE the setSymbol effect in the same React commit, so on
    // a swap BACK to a ticker it re-applies that ticker's drawings first and
    // the cleanup above then deletes them. This event lets the restore hook
    // re-apply on a clean slate (idempotent per syncId, so the other ordering
    // double-applies nothing).
    this.eventBus.emit('overlays:cleared', undefined)
    // Refresh per-symbol floor cache + reset the scroll-back state machine
    // before loading bars (chart-lookback spec 2026-05-04). Fire-and-forget
    // because the floor only gates the scroll-back loop, not `load()` itself.
    void this.dataLayer.setSymbol(symbol)
    // NO clearData() here — in-place symbol swap (Chris, 2026-07-30): the old
    // symbol's bars stay on screen (dimmed by the host via load:pending) until
    // the new bars land, and loadHistory's applyNewData() REPLACES the dataset
    // wholesale. Clearing first is what produced the empty "0–10" placeholder
    // axis and the narrow→wide width bounce between similar-priced tickers.
    // `swapPending` covers the failure path: stale bars under a new ticker name
    // would lie, so a data:error while a swap is in flight clears them (wired
    // in the constructor).
    this.swapPending = true
    // "Reset zoom on ticker change" (opt-in; by default the in-place swap keeps
    // zoom). Armed here and applied by `loadInner` once the new ticker's bars
    // land, on live and anchored (History) charts alike: the view a double-click
    // gives, the reset-zoom policy for this timeframe class. Applying it when
    // this promise settled framed the OLD bars whenever a newer load (the
    // focus-date change of a History row click) superseded this one.
    const resetZoom = this.resetZoomOnSymbolChange
    this.pendingSymbolZoomReset = resetZoom
    // A kept intraday view is already placed by `zoomToDate`; the focus-date
    // scroll below would drag it to the end of the day. Daily and coarser charts
    // have no window to keep, and that scroll is what lands them on the date.
    const keptView = this.pendingKeptZoom?.window != null
    this.currentLoad = this.load().then(() => {
      // The reset is the view; the focus-date scroll below would nudge it.
      if (resetZoom || keptView) return
      const anchor = this.dataLayer.getAnchorTimestamp()
      if (anchor != null) {
        // History/anchor swap with the reset off: scroll to the focus date.
        this.scrollToTimestamp(anchor)
      }
    })
    this.eventBus.emit('symbol:changed', { ticker: symbol.ticker })
    // Defer prefetch behind the foreground load so the visible chart isn't
    // starved by D/W/M background fetches. Floor lookup also has time to
    // settle, letting `fetchPeriodWindow` clamp `from` to the real floor.
    this.currentLoad.catch(() => undefined).finally(() => this.prefetchDailyAndCoarser())
    this.primeHtfSeries()
  }

  /** Canvas → Margins: bars of empty space kept right of the newest candle.
   *  Re-applies the offset immediately so the chart nudges live. */
  setRightMarginBars(bars: number): void {
    const next = Number.isFinite(bars) && bars >= 0 ? Math.floor(bars) : RIGHT_OFFSET_BARS
    if (next === this.rightMarginBars) return
    this.rightMarginBars = next
    const barSpace = this.chart.getBarSpace()
    this.chart.setOffsetRightDistance(next * barSpace)
  }

  /**
   * Flag read by the klinecharts TimeScaleStore patch (see
   * patches/klinecharts@9.8.12.patch): with the bar period it extrapolates
   * times past either end of the loaded bars, so a drawing anchor placed right
   * of the last candle saves a real timestamp instead of none, and draws in the
   * right place on every other timeframe.
   */
  private writeEdgePeriod(): void {
    ;(this.chart as unknown as { __edgePeriod?: { type: string; span: number } }).__edgePeriod = {
      type: this.period.timespan,
      span: this.period.multiplier,
    }
  }

  setPeriod(period: Period): void {
    this.stopMotion()
    // A timeframe switch keeps its own view (reset-zoom spec D1), so a reset a
    // ticker swap armed just before it does not carry over to it.
    this.pendingSymbolZoomReset = false
    this.pendingKeptZoom = null
    // Capture the in-memory bar snapshot + the source period BEFORE any
    // mutation, so the 1m-downsample fast path (Task 4a) reads the bars that
    // belonged to the OLD period, not whatever lands next.
    const sourcePeriod = this.period
    const sourceSnapshot =
      sourcePeriod.timespan === 'minute' && sourcePeriod.multiplier === 1
        ? (this.chart.getDataList() as unknown as EdgeBar[]).slice()
        : null

    this.period = period
    this.writeEdgePeriod()
    // A yellow history-mode highlight belongs to the old (daily) period — reset.
    this.clearBarHighlight()
    // Reset the per-period scroll-back state machine (floorReached /
    // loadingOlder) before loading bars, so the new period starts in the
    // `idle` state regardless of where the previous period left off
    // (chart-lookback spec 2026-05-04).
    this.dataLayer.setPeriod(period)
    this.applyDateFormatter()
    // Reset ready so chart:ready re-emits for the new period. The flag is set
    // once on initial load and never cleared, so subscribers gated on the event
    // (e.g. liveBarTimestamps refresh in BacktestChart) would miss the re-arm
    // after a timeframe change. Clearing here means every fast-path
    // (cache-hit, 1m-downsample, fresh load()) re-fires the event through the
    // existing `if (!this.ready)` guards below.
    this.ready = false
    // NO clearData() here — in-place period switch, same contract as setSymbol
    // (2026-09-08): the previous period's bars stay on screen (dimmed by the
    // host via load:pending) until the new page REPLACES them wholesale via
    // applyNewData. Clearing up front is what left the chart empty with one
    // lone streaming candle whenever the replacement fetch failed; the fast
    // paths below apply their bars synchronously, and the network path arms
    // `swapPending` so a failed load clears the stale bars instead.

    // Multi-period preload cache hit (Task 3 of chart-lookback spec).
    // Skip on anchor mode — anchored loads slice bars by focus date, which
    // the cache (always-now-ending window) doesn't support.
    const periodKey = periodKeyOf(period)
    const anchor = this.dataLayer.getAnchorTimestamp()
    // Mode-aware lookup — must match the same opts passed to set() so we don't
    // accidentally serve adjusted-mode bars when the chart is in unadjusted mode.
    const modeOpts = { adjusted: this.dataLayer.isAdjusted() }
    const cached =
      anchor == null && periodKey != null
        ? this.cache.get(this.symbol.ticker, periodKey, modeOpts)
        : null

    if (cached) {
      const stale = this.cache.isStale(this.symbol.ticker, periodKey!, modeOpts)
      if (!stale) {
        const gen = ++this.loadGeneration
        const symbol = this.symbol
        void cached.promise
          .then((bars) => {
            // Bail if the user switched away (or to a new symbol) before the
            // cached promise settled. `loadGeneration` is bumped by every
            // path that mutates chart contents.
            if (gen !== this.loadGeneration) return
            this.dataLayer.applyPreloadedBars(symbol, period, bars)
            this.updateAxisWidth()
            this.applyAutoFitIfThin()
            this.refreshAutoAnalysis()
            this.dataLayer.subscribe(symbol, period)
            if (!this.ready) {
              this.markReady()
            }
          })
          .catch(() => {
            // Cache fetch failed — fall through to a fresh load() to recover.
            if (gen !== this.loadGeneration) return
            this.swapPending = true
            this.load()
          })
        this.eventBus.emit('period:changed', { period })
        return
      }
      // Stale: drop the cached entry so the foreground load below refills it
      // on the next prefetch tick. Treat as miss.
    }

    // 1m → higher-timeframe local downsample (Task 4a). Eligibility:
    //   - source period was 1m
    //   - target.ms is a positive multiple of 1m.ms (always true for our
    //     periods, but we check defensively)
    //   - target is finer-than-source-DIVISIBLE — i.e. target.ms > 1m.ms
    //   - anchor mode is OFF
    //   - we have in-memory 1m bars to aggregate
    //   - aggregator emits at least one bar (otherwise the chart would be
    //     empty until scroll-back fired — better to fetch instead)
    if (
      sourceSnapshot != null &&
      anchor == null &&
      isIntradayPeriod(period) &&
      period.ms > 60_000 &&
      period.ms % 60_000 === 0 &&
      sourceSnapshot.length > 0
    ) {
      const aggregated = aggregateBars(sourceSnapshot, period)
      if (aggregated.length > 0) {
        // Bump the generation counter so a concurrent setPeriod that raced this
        // fast path detects staleness via loadGeneration (the increment is the
        // side effect — the value itself is not read on this branch).
        ++this.loadGeneration
        const symbol = this.symbol
        this.dataLayer.applyPreloadedBars(symbol, period, aggregated)
        this.updateAxisWidth()
        this.applyAutoFitIfThin()
        this.refreshAutoAnalysis()
        this.dataLayer.subscribe(symbol, period)
        if (!this.ready) {
          this.markReady()
        }
        this.eventBus.emit('period:changed', { period })
        return
      }
    }

    // Stale bars under a new period label would lie if the load fails for
    // good: arm the failure fallback (data:error → clearData, wired in the
    // constructor). Only the network path arms it — the fast paths above
    // already applied their bars, and leaving it armed after a successful
    // apply would let a later scroll-back error blank the chart.
    this.swapPending = true
    this.currentLoad = this.load().then(() => {
      const a = this.dataLayer.getAnchorTimestamp()
      if (a != null) this.scrollToTimestamp(a)
    })
    this.eventBus.emit('period:changed', { period })
    // Defer prefetch behind the foreground load (network contention + floor settle).
    this.currentLoad.catch(() => undefined).finally(() => this.prefetchDailyAndCoarser())
  }

  // `applySettings` is implemented by the React proxy in EdgeChart.tsx — it
  // owns the local settings state that drives `useChartStyles`. This stub
  // satisfies the EdgeChartApi interface for the (rare) case where someone
  // pokes at the manager directly; in practice the proxy intercepts first.
  applySettings(_settings: ChartSettings): void {
    /* no-op — see EdgeChart.tsx imperative proxy */
  }

  /**
   * Show/move/remove the programmatic reference price line — a full-width
   * dashed horizontal at `ref.price` with a y-axis pill (see
   * `referencePriceLine.ts`). Used by anchored/historical charts to keep
   * TODAY's live price visible while the buffer ends in the past.
   *
   * Replay caveat: the line is price-anchored chart chrome, not replay state —
   * it stays wherever the host last set it while a bar replay runs. Hosts that
   * don't want a "future" price hint during replay should clear it (null) on
   * `replay:started` and restore it on exit.
   */
  onBarsLoaded(cb: () => void): () => void {
    const off = this.eventBus.on('data:loaded', () => cb())
    if (this.hasLoadedBars) cb()
    return off
  }

  setReferencePriceLine(ref: { price: number; label?: string } | null): void {
    if (ref === null) {
      this.pendingReferencePriceLine = null
      if (this.referencePriceLineId) {
        this.chart.removeOverlay(this.referencePriceLineId)
        this.referencePriceLineId = null
      }
      return
    }
    // No candles yet → park the request; the `data:loaded` listener replays it.
    // A reference line on an empty canvas reads as broken chrome.
    if (!this.hasLoadedBars) {
      this.pendingReferencePriceLine = ref
      return
    }
    const points = [{ value: ref.price }]
    // Muted grey (matches the native neutral price-mark tone): the line is a
    // quiet reference, not an accent — see the "standard price line" feedback.
    const extendData = { color: CHART_COLORS.textMuted, label: ref.label }
    if (this.referencePriceLineId) {
      this.chart.overrideOverlay({
        id: this.referencePriceLineId,
        points,
        extendData,
      } as Parameters<Chart['overrideOverlay']>[0])
    } else {
      this.referencePriceLineId =
        (this.chart.createOverlay({
          name: REFERENCE_PRICE_LINE_NAME,
          points,
          lock: true,
          extendData,
        } as Parameters<Chart['createOverlay']>[0]) as string | null) ?? null
    }
  }

  /**
   * Draw the user's active price alerts for this ticker as alert lines
   * (utils/priceAlertLine.ts). Data lines, not drawings: locked, created without the
   * drawing callbacks, re-created after an id-less removeOverlay() wipe and left visible by
   * the drawing-bar eye. Parked until bars exist, like the Today line.
   */
  setPriceAlertLines(lines: readonly PriceAlertLine[]): void {
    this.priceAlertLines = lines
    this.syncPriceAlertLines()
  }

  /**
   * True while a user's own drawing is under the pointer (klinecharts' hover state, the one
   * the drawing cursor reads). The alert figure ignores mouse moves, so the hover stays on
   * the drawing beneath it. Data lines, the countdown pill and other chrome do not count.
   */
  isUserDrawingHovered(): boolean {
    return this.hoveredUserDrawingId() != null
  }

  private hoveredUserDrawingId(): string | null {
    const store = (
      this.chart as unknown as {
        getChartStore?: () => {
          getOverlayStore?: () => {
            getHoverInstanceInfo: () => {
              instance: { id: string; name?: string } | null
              figureType: number
            }
          }
        }
      }
    )
      .getChartStore?.()
      ?.getOverlayStore?.()
    const hover = store?.getHoverInstanceInfo()
    // figureType 0 = EventOverlayInfoFigureType.None (the pointer is not over a figure).
    if (!hover?.instance || hover.figureType === 0) return null
    return NON_DRAWING_OVERLAY_NAMES.has(hover.instance.name ?? '') ? null : hover.instance.id
  }

  /** A right click, a double click or a long press on an alert calls this; null leaves the
   *  alerts without clicks. */
  setPriceAlertCancelHandler(cb: ((alertId: string) => void) | null): void {
    this.priceAlertCancel = cb
    this.syncPriceAlertLines()
  }

  /**
   * Cancel the alert whose line (within PRICE_ALERT_HIT_PX) or label is at a viewport point:
   * EdgeChart's right click and long press, and an axis double click. Geometry on the event's
   * own coordinates, so no pointer state can undo it. True when an alert was hit.
   */
  cancelPriceAlertAt(clientX: number, clientY: number): boolean {
    const id = this.priceAlertIdAt(clientX, clientY)
    return id != null && this.cancelPriceAlertById(id)
  }

  /** Cancel a known alert unless it is gone, already cancelling, or nothing can cancel. */
  private cancelPriceAlertById(id: string): boolean {
    const cancel = this.priceAlertCancel
    const line = this.priceAlertLines?.find((l) => l.id === id)
    if (cancel == null || !line || line.cancelling) return false
    cancel(id)
    return true
  }

  /** The alert whose line or label is at a viewport point, on the range on screen now. Pure. */
  priceAlertIdAt(clientX: number, clientY: number): string | null {
    const lines = this.priceAlertLines
    // Before any DomPosition read: test harnesses mock klinecharts without it.
    if (!lines?.length || this.priceAlertCancel == null || !this.priceAlertOverlayIds?.size) {
      return null
    }
    const rect = this.container.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const main = this.chart.getSize(CANDLE_PANE_ID, DomPosition.Main)
    const axis = this.chart.getSize(CANDLE_PANE_ID, DomPosition.YAxis)
    if (!main || !axis || y < main.top || y > main.top + main.height) return null
    const collapsed = this.hideAxisScale === true
    const onAxis = !collapsed && x >= axis.left && x <= axis.left + axis.width
    if (!onAxis && (x < main.left || x > main.left + main.width)) return null
    // klinecharts lays the axis out at x 0 on the left and at totalWidth - width on the right,
    // inside or outside (KC:13438-13456), so this holds with the scales hidden too.
    const axisOnLeft = axis.left <= main.left
    const fromEdge = axisOnLeft ? x - main.left : main.left + main.width - x
    const precision = this.chart.getPriceVolumePrecision().price
    const toY = (value: number) =>
      (this.chart.convertToPixel({ value }, { paneId: CANDLE_PANE_ID }) as Partial<Coordinate>)?.y
    const close = this.countdownPillClose()
    const closeY = close == null ? undefined : toY(close)
    const id = alertHitAt({
      y: y - main.top,
      pillBand: closeY == null ? null : countdownPillBand(closeY, main.height),
      lines: lines.map((l) => ({
        id: l.id,
        y: toY(l.level) ?? Number.NaN,
        cancelling: l.cancelling,
        onLabel:
          onAxis || (collapsed && fromEdge <= estimateLabelWidth(l.level.toFixed(precision))),
      })),
    })
    return id || null
  }

  private syncPriceAlertLines(): void {
    const lines = this.priceAlertLines
    if (!lines || !this.hasLoadedBars) return
    const ids = (this.priceAlertOverlayIds ??= new Map())
    const wanted = new Set(lines.map((l) => l.id))
    for (const [alertId, overlayId] of ids) {
      if (wanted.has(alertId)) continue
      this.chart.removeOverlay(overlayId)
      ids.delete(alertId)
    }
    for (const line of lines) {
      const points = [{ value: line.level }]
      const extendData = this.priceAlertExtend(line)
      const overlayId = ids.get(line.id)
      if (overlayId) {
        this.chart.overrideOverlay({ id: overlayId, points, extendData } as Parameters<
          Chart['overrideOverlay']
        >[0])
        continue
      }
      const created =
        (this.chart.createOverlay({
          name: PRICE_ALERT_LINE_NAME,
          points,
          lock: true,
          zLevel: PRICE_ALERT_Z_LEVEL,
          extendData,
          ...this.priceAlertCallbacks(),
        } as Parameters<Chart['createOverlay']>[0]) as string | null) ?? null
      if (created) ids.set(line.id, created)
    }
    this.updateAxisWidth()
  }

  private priceAlertExtend(line: PriceAlertLine): PriceAlertLineExtend {
    return {
      alertId: line.id,
      cancelling: line.cancelling === true,
      cancellable: this.priceAlertCancel != null,
      collapsed: this.hideAxisScale === true,
      countdownClose: () => this.countdownPillClose(),
    }
  }

  /** The last close while the countdown pill shows (alerts under it take no clicks). */
  private countdownPillClose(): number | null {
    if (!this.barCountdownId) return null
    const data = this.chart.getDataList() as Array<{ close?: number }>
    const close = data[data.length - 1]?.close
    return typeof close === 'number' ? close : null
  }

  /** The highest alert level inside the candle axis's current range, or null. */
  private visiblePriceAlertMax(): number | null {
    const lines = this.priceAlertLines
    if (!lines?.length || !this.priceAlertOverlayIds?.size) return null
    const r = this.getYAxis(CANDLE_PANE_ID)?.getRange()
    if (!r) return null
    const lo = Math.min(r.realFrom, r.realTo)
    const hi = Math.max(r.realFrom, r.realTo)
    let max: number | null = null
    for (const l of lines)
      if (l.level >= lo && l.level <= hi) max = Math.max(max ?? l.level, l.level)
    return max
  }

  /** Per-instance callbacks for an alert line. Not overlayDrawCallbacks(): that is the
   *  drawings' persistence path. */
  private priceAlertCallbacks() {
    return {
      // Only the PRICE_ALERT_HIT_KEY figures take events, and only double clicks.
      onDoubleClick: (event: OverlayEvent) => {
        // The drawings' flag (overlayDrawCallbacks): the native dblclick that follows must
        // not also reset the chart (read in dblclickHandler). klinecharts raises this on the
        // second mouseup; the browser then dispatches click and dblclick before any timer
        // runs, so the setTimeout(0) clear always lands after the dblclick. Keep it a
        // setTimeout(0): a later clear would swallow the next real body double click.
        this.overlayEditFromDblClick = true
        setTimeout(() => {
          this.overlayEditFromDblClick = false
        }, 0)
        // A drawing under the pointer wins (the main workflow is an alert on your own line).
        // klinecharts' figure handler returns true whatever the callback returns
        // (KC:8796-8803) and the alert sorts last, so dispatch stops here and the drawing
        // beneath never hears the click: hand it over by calling its own handler.
        const drawingId = this.hoveredUserDrawingId()
        const drawing = drawingId
          ? (this.chart.getOverlayById(drawingId) as {
              onDoubleClick?: (e: OverlayEvent) => unknown
            } | null)
          : null
        if (drawing) {
          drawing.onDoubleClick?.({ ...event, overlay: drawing } as unknown as OverlayEvent)
          return true
        }
        const ext = (event.overlay as { extendData?: PriceAlertLineExtend }).extendData
        if (ext && !ext.cancelling) this.priceAlertCancel?.(ext.alertId)
        return true
      },
      // Guard, as the template's: never klinecharts' right-click removal.
      onRightClick: () => true,
    }
  }

  /**
   * Toggle klinecharts' native last-price mark (dashed line + pill at the most
   * recent close). Hidden while the chart is anchored to a historical focus —
   * there the buffer's last bar is NOT the current price, so the native mark
   * would mislabel stale data. Tracked in `lastPriceMarkShow` so `setTheme`
   * (whose bundle resets `show: true`) can re-assert the hidden state.
   */
  private setLastPriceMarkVisible(show: boolean): void {
    this.lastPriceMarkShow = show
    this.chart.setStyles({ candle: { priceMark: { last: { show } } } } as Parameters<
      typeof this.chart.setStyles
    >[0])
  }

  setFocusTimestamp(timestamp: number | null): void {
    // Clearing the focus while a replay is active means "go back to live" — let
    // the controller run its exit sequence (which re-enters this method with
    // `active` already false, so the real reload below runs exactly once).
    if (timestamp === null && this.replay?.getState().active) {
      // Explicit "go live" beats the pre-replay anchor the controller would
      // otherwise restore. The caller asked for now, not for wherever the
      // replay was launched from.
      this.replay.exit({ restoreTo: null })
      return
    }
    // A date click on a zoomed chart keeps the width and the time of day in view.
    // Armed here, not in applyFocusTimestamp: ReplayController calls that directly.
    if (timestamp != null) this.armKeptZoom()
    // Not awaited, so a reload a newer one superseded would reject unhandled
    // with an AbortError (seen in prod on /history). That is expected and
    // silent; anything else still rejects, so error tracking sees it.
    this.applyFocusTimestamp(timestamp).catch((err: unknown) => {
      if ((err as { name?: unknown } | null)?.name === 'AbortError') return
      throw err
    })
  }

  /**
   * Raw focus-anchor primitive: set the DataLayer anchor, drop the live feed,
   * clear the buffer, and reload the bounded window. `setFocusTimestamp` wraps
   * this with the replay-exit guard; ReplayController wires its `applyAnchor`
   * closure straight to this to avoid re-triggering that guard.
   */
  private applyFocusTimestamp(timestamp: number | null): Promise<void> {
    chartDebug('setFocusTimestamp:', timestamp ? new Date(timestamp).toISOString() : 'null')
    // Anchored ⇒ the buffer ends in the past, so the native last-price mark
    // would mislabel a historical close as "current" — hide it; live (null)
    // restores it. Replay is exempt: ReplayController.enter() flips
    // `active` BEFORE calling applyAnchor, so replay anchoring keeps the
    // native mark tracking the forming replay bar (exit() flips it back
    // before re-anchoring to null, restoring the mark on the way out).
    if (!this.replay?.getState().active) {
      this.setLastPriceMarkVisible(timestamp == null)
    }
    this.dataLayer.setAnchorTimestamp(timestamp)
    this.dataLayer.unsubscribe()
    // NO clearData() — same in-place swap as setSymbol: the current
    // bars hold (dimmed) until the anchored window replaces them wholesale.
    this.swapPending = true
    // Returned so ReplayController.enter can await the bounded reload — the
    // forward prefetch must not race it.
    return this.load()
  }

  /**
   * Enter bar replay anchored at `startTs` (EdgeChartApi surface). Thin
   * delegate to the lazily-created ReplayController — EdgeChart's imperative
   * proxy intercepts first in practice, but this keeps the manager itself a
   * complete EdgeChartApi implementation.
   */
  startReplay(startTs: number): Promise<void> {
    return this.getReplay().enter(startTs)
  }

  /** Exit bar replay → back to live (EdgeChartApi surface). */
  exitReplay(): void {
    this.getReplay().exit()
  }

  /** One sub-bar forward in an active replay (EdgeChartApi surface). */
  stepReplay(): Promise<boolean> {
    return this.getReplay().step()
  }

  /**
   * The replay strip's loop button. One press arms a two-click range selection
   * on the chart (the `replayLoopRange` highlight - both edges preview while
   * selecting; once looping only the start edge + a rightward wash remain);
   * completing it starts `ReplayController.startLoop` over the range, which
   * replays it on repeat. Pressing again - at ANY point in that lifecycle -
   * closes it back down: mid-selection the half-drawn highlight is cancelled
   * (Escape does the same via `cancelPendingDraw`), and while looping the
   * highlight is removed and the loop stops (playback continues as ordinary
   * replay).
   *
   * Strip state rides the replay:* event family: `replay:loop-armed` when the
   * selection arms, `replay:loop-started` / `replay:loop-ended` from the
   * controller (the manager also emits loop-ended on its own cancel paths).
   */
  toggleReplayLoop(): void {
    // Mid-selection → cancel the half-drawn highlight.
    if (this.replayLoopOverlayId != null && this.pendingDrawId === this.replayLoopOverlayId) {
      this.cancelPendingDraw()
      return
    }
    // Looping (or a stray completed highlight) → tear down.
    if (this.replayLoopOverlayId != null || this.replay?.getState().looping) {
      this.removeReplayLoopOverlay()
      if (this.replay?.getState().looping) this.replay.stopLoop()
      else this.eventBus.emit('replay:loop-ended', undefined)
      return
    }
    // Off → arm the selection (only meaningful inside a replay session).
    if (!this.getReplay().getState().active) return
    this.armReplayLoopSelection()
  }

  /**
   * Create the two-click loop-range overlay with a BESPOKE onDrawEnd:
   * deliberately not `overlayDrawCallbacks()` / `handleOverlayDrawEnd`, whose
   * default branch would persist + sync the highlight as a real drawing (and
   * whose onDrawing would broadcast the in-progress selection to sync peers).
   */
  private armReplayLoopSelection(): void {
    const id = this.chart.createOverlay({
      name: REPLAY_LOOP_RANGE_NAME,
      onDrawEnd: (event: OverlayEvent) => {
        const { id: oid, points } = event.overlay
        if (this.pendingDrawId === oid) this.pendingDrawId = null
        const pts = points as Array<{ timestamp?: number }>
        const t0 = pts?.[0]?.timestamp
        const t1 = pts?.[1]?.timestamp
        if (t0 == null || t1 == null || t0 === t1) {
          // Unresolvable clicks (off-data whitespace) or a zero-width range -
          // nothing loopable; disarm as if cancelled.
          this.chart.removeOverlay(oid)
          this.replayLoopOverlayId = null
          this.eventBus.emit('replay:loop-ended', undefined)
          return false
        }
        // startLoop refuses degenerate/future ranges itself (emitting
        // loop-ended); drop the orphaned highlight when it does.
        void this.getReplay()
          .startLoop(t0, t1)
          .then(() => {
            if (!this.getReplay().getState().looping) this.removeReplayLoopOverlay()
          })
        return false
      },
    } as Parameters<Chart['createOverlay']>[0])
    const oid = (id as string | null) ?? null
    this.replayLoopOverlayId = oid
    // Armed → drawing: Escape cancels via the standard pending-draw path.
    this.pendingDrawId = oid
    if (oid) this.eventBus.emit('replay:loop-armed', undefined)
  }

  /** Remove the loop-range highlight overlay, if present. */
  private removeReplayLoopOverlay(): void {
    if (this.replayLoopOverlayId == null) return
    this.chart.removeOverlay(this.replayLoopOverlayId)
    this.replayLoopOverlayId = null
  }

  /**
   * Lazily create the bar-replay engine, wiring its narrow dependency seam to
   * real chart/DataLayer calls. Ephemeral session state — nothing persisted.
   */
  getReplay(): ReplayController {
    if (!this.replay) {
      this.replay = new ReplayController({
        fetchBars: (period, from, to) => this.dataLayer.fetchForwardBars(from, to, period),
        applyAnchor: (ts) => this.applyFocusTimestamp(ts),
        appendBar: (bar) => this.dataLayer.applyReplayBar(bar),
        periodOf: () => this.period,
        regularSession: () => this.dataLayer.regularSessionBuckets(),
        // Pre-replay focus anchor, so exit() returns to the day the user was
        // on rather than always snapping to live.
        currentAnchor: () => this.dataLayer.getAnchorTimestamp(),
        nowMs: () => Date.now(),
        eventBus: this.eventBus,
      })
    }
    return this.replay
  }

  /**
   * Zoom the visible area to show bars between two timestamps.
   * Calculates the appropriate bar width so the matching bars fill the chart,
   * then scrolls to center them.
   *
   * `keepBarSpace` (internal, from the anchored-load path only): keep the current
   * bar space instead of re-fitting the range to the chart width — used to
   * preserve a restored zoom (a template remount) or a zoom the user chose
   * before a ticker or date click. Public callers (viewport
   * sync peers) omit it and get the fit-to-range behavior.
   */
  setVisibleDateRange(from: number, to: number, keepBarSpace = false): void {
    this.stopMotion()
    if (_win?.__chartDebug) {
      chartDebug(
        'setVisibleDateRange:',
        new Date(from).toISOString(),
        '→',
        new Date(to).toISOString(),
      )
    }
    const dataList = this.chart.getDataList()
    if (dataList.length === 0) {
      chartDebug('setVisibleDateRange: no data')
      return
    }

    // Timestamps are sorted ascending, so binary-search both edges instead of
    // scanning the whole buffer — viewport sync runs this on every synced
    // scroll frame on every peer, and the buffer can hold 50k bars.
    // firstIdx = first bar with ts >= from.
    let lo = 0
    let hi = dataList.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (dataList[mid].timestamp < from) lo = mid + 1
      else hi = mid
    }
    const firstIdx = dataList[lo].timestamp >= from && dataList[lo].timestamp <= to ? lo : -1
    // lastIdx = last bar with ts <= to.
    let lastIdx = -1
    if (firstIdx !== -1) {
      lo = firstIdx
      hi = dataList.length - 1
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1
        if (dataList[mid].timestamp <= to) lo = mid
        else hi = mid - 1
      }
      lastIdx = lo
    }

    if (_win?.__chartDebug) {
      chartDebug(
        'setVisibleDateRange: matched bars',
        firstIdx,
        '→',
        lastIdx,
        firstIdx >= 0
          ? `(${new Date(dataList[firstIdx].timestamp).toISOString()} → ${new Date(dataList[lastIdx].timestamp).toISOString()})`
          : '(none)',
      )
    }

    if (firstIdx === -1) {
      this.scrollToTimestamp(from)
      return
    }

    const barCount = lastIdx - firstIdx + 1

    // Use klinecharts' own measurement for the chart drawing area width
    const size = this.chart.getSize(CANDLE_PANE_ID)
    const chartWidth = (size?.width ?? this.container.clientWidth) - 60

    const minBarsToShow = 20
    const targetBars = Math.max(minBarsToShow, barCount)
    // keepBarSpace: preserve the current (restored) zoom; otherwise fit the
    // matched range to the chart width — minus RIGHT_OFFSET_BARS of trailing
    // breathing room so the last candle isn't flush against the price axis
    // (same reservation as applyAutoFitIfThin; this path used to zero it).
    // Floor at klinecharts' NATIVE min (0.5, widened from stock 1 by the
    // vendored patch), NOT a hardcoded 1: a dense intraday range (a 1m full
    // session is ~960 bars) needs sub-1px bars to fit a narrow pane. Flooring at
    // 1 capped the fit at ~pane-px bars, so the day was cut off even though a
    // manual wheel zoom-out (which klinecharts allows down to 0.5) could show
    // it. klinecharts REJECTS a below-min setBarSpace outright, so the clamp
    // also keeps the request valid.
    const barSpace = keepBarSpace
      ? this.chart.getBarSpace()
      : Math.max(
          NATIVE_MIN_BAR_SPACE,
          Math.min(50, chartWidth / (targetBars + this.rightMarginBars)),
        )

    chartDebug(
      'setVisibleDateRange: barCount =',
      barCount,
      'chartWidth =',
      chartWidth,
      'barSpace =',
      barSpace.toFixed(2),
      'keepBarSpace =',
      keepBarSpace,
    )

    if (!keepBarSpace) this.chart.setBarSpace(barSpace)
    // Keep the resting (max-scroll) gap in sync with the bar space…
    this.chart.setOffsetRightDistance(this.rightMarginBars * barSpace)
    this.chart.scrollToDataIndex(lastIdx)
    // …but klinecharts' scrollToDataIndex CONSUMES the right offset in its
    // distance math — `(lastBarRightSideDiffBarCount + (len-1-idx)) * barSpace`
    // — pinning `lastIdx` flush against the price axis (verified empirically:
    // offset read 0 after this call). Back off the same gap so the last candle
    // keeps RIGHT_OFFSET_BARS of breathing room (same post-scroll nudge
    // pattern as scrollToTimestamp's anchored -5-bar offset below).
    this.chart.scrollByDistance(-this.rightMarginBars * barSpace)

    chartDebug(
      'setVisibleDateRange: scrolled to lastIdx',
      lastIdx,
      'dataList.length',
      dataList.length,
    )

    // Log what's actually visible after scroll
    requestAnimationFrame(() => {
      const visible = this.chart.getVisibleRange()
      chartDebug('setVisibleDateRange: visible after scroll', {
        from: visible.from,
        to: visible.to,
        firstVisible:
          visible.from >= 0 && dataList[visible.from]
            ? new Date(dataList[visible.from].timestamp).toISOString()
            : 'n/a',
        lastVisible:
          visible.to >= 0 && visible.to < dataList.length && dataList[visible.to]
            ? new Date(dataList[visible.to].timestamp).toISOString()
            : 'n/a',
        visibleBars: visible.to - visible.from + 1,
        chartWidth,
        barSpace: barSpace.toFixed(2),
      })
    })
  }

  /**
   * Zoom to show the full trading session (4:00 AM – 8:00 PM ET) for the anchor date.
   * ET = UTC-4 (EDT, Mar–Nov) or UTC-5 (EST, Nov–Mar).
   */
  private zoomToDate(
    timestamp: number,
    keptZoom?: { window: AnchorVisibleWindow | null } | null,
  ): void {
    // Wall-clock window (default 4:00–20:00 ET) → UTC range; the DST math and
    // its unit tests live in `computeAnchorWindow`. A nonzero day offset needs
    // the loaded sessions to walk back N trading days; the single-day path skips
    // the O(n) derivation entirely.
    const win = keptZoom?.window ?? this.anchorVisibleWindow
    const hasOffset = (win.startDayOffset ?? 0) !== 0 || (win.endDayOffset ?? 0) !== 0
    const dataList = this.chart.getDataList()
    const sessionDates = hasOffset ? deriveSessionDateKeys(dataList) : undefined
    // Data-aware resolution: if the requested window holds no loaded bars (a
    // pre-market window on a day whose first trade is after the open, an outcome
    // window not yet reloaded), fall back to the full event-day session so the
    // chart stays on the event day instead of scrolling into the prior session.
    const { from, to } = resolveAnchorVisibleRange(dataList, timestamp, win, sessionDates)

    chartDebug(
      'zoomToDate:',
      `${win.startMin}-${win.endMin} ET min → ` +
        `${new Date(from).toISOString()} → ${new Date(to).toISOString()}`,
    )

    // Keep the bar width when a click on a zoomed chart armed `keptZoom`, or the
    // chart was built with a restored zoom (a template remount); otherwise fit
    // the whole window and remember that width as the chart's own choice.
    const keepBarSpace = keptZoom != null || this.hasRestoredBarSpace
    // The restore serves one load; with no bars yet the call frames nothing.
    if (dataList.length > 0) this.hasRestoredBarSpace = false
    this.setVisibleDateRange(from, to, keepBarSpace)
    if (!keepBarSpace) this.markChosenBarSpace()
  }

  /**
   * Override the visible window anchored charts scroll to (see
   * {@link AnchorVisibleWindow}). Applies live: if this chart is currently
   * anchored, it re-zooms to the new window immediately, so the History grid can
   * push a shared window onto already-loaded cells without a remount.
   */
  setAnchorVisibleWindow(window: AnchorVisibleWindow): void {
    this.anchorVisibleWindow = window
    this.dataLayer.setMinAnchorLookbackMs(anchorLookbackMs(window.startDayOffset ?? 0))
    this.dataLayer.setAnchorForwardMs(anchorForwardMs(window.endDayOffset ?? 0))
    const anchor = this.dataLayer.getAnchorTimestamp()
    if (anchor == null) return

    // Does the buffer already span the new window? If so, just re-zoom (no
    // fetch). If it reaches past the loaded data — earlier than the first bar,
    // or later than the last (an outcome window looking ahead) — reload ONCE at
    // the new size, then zoom. Skip a back-reach below the known floor (no data
    // there) or a forward-reach past now (not yet traded). A token guards the
    // async zoom against a newer window/ticker.
    const dataList = this.chart.getDataList()
    const { from, to } = computeAnchorWindow(anchor, window, deriveSessionDateKeys(dataList))
    const earliest = dataList.length > 0 ? dataList[0].timestamp : null
    const latest = dataList.length > 0 ? dataList[dataList.length - 1].timestamp : null
    const floor = this.dataLayer.getFloorTimestamp()
    const needsBack = earliest != null && from < earliest && !(floor != null && from < floor)
    const needsForward = latest != null && to > latest && to <= Date.now()
    if (needsBack || needsForward) {
      const token = ++this.anchorReloadToken
      void this.reloadData().then(() => {
        if (token !== this.anchorReloadToken) return // superseded
        this.zoomToDate(anchor)
      })
    } else {
      this.zoomToDate(anchor)
    }
  }

  /**
   * Clear-and-reload for a setting that changes the bars themselves (extended
   * hours, adjusted, volume profile, a wider anchor window).
   *
   * It must carry the readiness duty too. These setters fire from React effects
   * right after mount when settings hydrate late, which lands them WHILE the
   * first `load()` is still in flight. `loadHistory` aborts that first request,
   * `loadInner` sees "not loaded" and returns before `markReady`, and the bars
   * then arrive through this path. If this path does not mark ready, nothing
   * ever does: `chart:ready` never fires for the life of the chart, and anything
   * parked on it (saved drawings waiting for bars) waits forever. That is how a
   * grid reloaded with its drawings on one cell out of three.
   */
  private reloadData(): Promise<void> {
    return this.dataLayer.reload(this.symbol, this.period).then(() => {
      if (this.ready || this.chart.getDataList().length === 0) return
      this.markReady()
      // Diagnostic only: this reload superseded the first load. The host
      // reports it, so we learn how often that happens in the wild.
      this.eventBus.emit('chart:ready-recovered', { period: this.period.label })
    })
  }

  setTheme(theme: EdgeTheme): void {
    this.chart.setStyles(applyTheme(theme))
    // The theme bundles hardcode `candle.priceMark.last.show: true`; re-assert
    // the current visibility so an anchored chart stays mark-free across a
    // theme flip.
    if (!this.lastPriceMarkShow) {
      this.setLastPriceMarkVisible(false)
    }
  }

  setTimezone(timezone: string): void {
    this.chart.setTimezone(timezone)
  }

  setExtendedHours(enabled: boolean, timezone: string): void {
    this.dataLayer.setExtendedHours(enabled, timezone)
    void this.reloadData()
    // No MultiPeriodCache eviction needed: D/W/M URLs do not include
    // `market_session` (intraday-only param) and the post-fetch barFilter
    // is intraday-only too, so daily+ bar values don't change with this toggle.
  }

  /**
   * Toggle split-adjusted vs. unadjusted prices. Backend serves either via
   * the `adjusted=true|false` query param. Adjusted-vs-unadjusted is NOT a
   * filter relationship (post-split bars have entirely different prices), so
   * the cache treats the two modes as independent entries — which means a
   * full reload is required when the toggle flips.
   *
   * The MultiPeriodCache is keyed on `adjusted` so mode-distinct entries
   * coexist; we still evict the active ticker so the next setPeriod re-fetches
   * fresh under the new mode rather than serving a stale (now-old-mode) entry
   * that may not yet exist for the new mode (forcing a fresh prefetch is
   * cheaper than waiting for the user to switch period and discover the miss).
   */
  setAdjusted(adjusted: boolean): void {
    this.dataLayer.setAdjusted(adjusted)
    this.cache.evictTicker(this.symbol.ticker)
    void this.reloadData()
    // Re-prime the cache for D/W/M under the new mode so a quick switch to
    // 1D/1W/1M after the toggle still sees a warm cache.
    this.prefetchDailyAndCoarser()
    // 1D/1W indicators read the new mode's daily series (no-op without any).
    this.primeHtfSeries()
  }

  /**
   * Turn per-bar volume histograms on/off for subsequent history fetches, and
   * reload so the current view picks them up.
   *
   * Differs from `setAdjusted` in two ways worth knowing:
   *  - No cache eviction. A histogram-bearing response is a strict SUPERSET of
   *    the plain one and lives under its own URL, so the plain entry stays
   *    valid for when the indicator comes back off. (adjusted/unadjusted are
   *    mutually exclusive price universes; these are not.)
   *  - No prefetch re-prime. `PREFETCH_TARGETS` is daily-only, which is
   *    ineligible for histograms — the datafeed omits the param for daily+, so
   *    that cache key is identical either way.
   *
   * No-ops when the flag is unchanged, so re-adding an indicator that's already
   * on doesn't cost a reload.
   */
  setVolumeProfile(enabled: boolean): void {
    if (this.dataLayer.isVolumeProfile() === enabled) return
    this.dataLayer.setVolumeProfile(enabled)
    void this.reloadData()
  }

  getSubPaneId(name: string): string | undefined {
    return this.subPaneIds.get(name)
  }

  /** klinecharts instance names assigned to the initial main-pane indicators,
   *  in prop order. The EdgeChart legend seed zips these onto its rows. */
  getMainInstanceNames(): string[] {
    return this.mainInstanceNames
  }

  /** Same as getMainInstanceNames, for the initial sub-pane indicators. Sparse:
   *  VOL entries keep their own naming, so those slots stay undefined and the
   *  legend seed falls back to the entry name. */
  getSubInstanceNames(): Array<string | undefined> {
    return this.subInstanceNames
  }

  /** Calculate ratio for the Nth sub-pane (0-indexed): 25% first, 20% next two, then split up to 50% total */
  private calcSubPaneRatio(subPaneIndex: number): number {
    if (subPaneIndex === 0) return 0.25
    if (subPaneIndex <= 2) return 0.2
    // Beyond 3 panes: distribute up to 50% cap evenly
    return 0.5 / (subPaneIndex + 1)
  }

  /**
   * Pre/after-hours background shading, spanning EVERY pane (candle + all
   * sub-panes) so it reads as one continuous background column rather than
   * stopping at the candle pane's bottom edge. Pass a config to show it, `null`
   * to clear it everywhere. The config is remembered so later sub-pane changes
   * keep the column complete (see `createIndicator` / `removeIndicator`).
   */
  setSessionHighlight(config: SessionHighlightConfig | null): void {
    this.sessionHighlightConfig = config
    for (const paneId of this.allPaneIds()) {
      // Remove-then-recreate so a config change (colors/timezone) re-seeds cleanly.
      this.chart.removeIndicator(paneId, SESSION_HIGHLIGHT_NAME)
      this.addSessionHighlightToPane(paneId)
    }
  }

  /** Seed the session-shading indicator on one pane (no-op when shading is off). */
  private addSessionHighlightToPane(paneId: string): void {
    if (!this.sessionHighlightConfig) return
    this.chart.createIndicator(
      {
        name: SESSION_HIGHLIGHT_NAME,
        // Negative zLevel → `destination-over` (behind the candles). The specific
        // value keeps this the backmost negative layer so volume profiles / TPO /
        // HTF / VOL overlay all sit in front of the shading. See the constant.
        zLevel: BACKGROUND_SHADING_Z_LEVEL,
        extendData: this.sessionHighlightConfig,
      } as Parameters<Chart['createIndicator']>[0],
      true,
      { id: paneId } as Parameters<Chart['createIndicator']>[2],
    )
  }

  /** Shade an arbitrary [fromTs, toTs] span behind the bars (null clears it). Mirrors
   *  setSessionHighlight — remove-then-recreate so a range change re-seeds cleanly. */
  setRangeHighlight(config: RangeHighlightConfig | null): void {
    this.rangeHighlightConfig = config
    for (const paneId of this.allPaneIds()) {
      this.chart.removeIndicator(paneId, RANGE_HIGHLIGHT_NAME)
      this.addRangeHighlightToPane(paneId)
    }
  }

  private addRangeHighlightToPane(paneId: string): void {
    if (!this.rangeHighlightConfig) return
    this.chart.createIndicator(
      {
        name: RANGE_HIGHLIGHT_NAME,
        zLevel: BACKGROUND_SHADING_Z_LEVEL,
        extendData: this.rangeHighlightConfig,
      } as Parameters<Chart['createIndicator']>[0],
      true,
      { id: paneId } as Parameters<Chart['createIndicator']>[2],
    )
  }

  createIndicator(
    name: string,
    isStack = false,
    paneId?: string,
    calcParams?: unknown[],
    extendData?: unknown,
  ): string | null {
    const base: Record<string, unknown> = { name }
    // `name` may be an instance alias (`MA#2`); the catalog knows the base name.
    if (calcParams) base.calcParams = completeCalcParams(baseIndicatorName(name), calcParams)
    if (extendData !== undefined) {
      base.extendData = this.mtfExtendData(name, paneId ?? name, extendData)
    }
    // Suppress canvas-drawn tooltip for main-pane indicators (React legend handles it)
    if (paneId === CANDLE_PANE_ID) {
      base.createTooltipDataSource = () => ({ name: '', calcParamsText: '', icons: [], values: [] })
    }
    const ratio = paneId ? 0 : this.calcSubPaneRatio(this.subPaneIds.size)
    const paneOpts: Record<string, unknown> = paneId
      ? { id: paneId }
      : { height: Math.round(this.container.clientHeight * ratio) }
    if (!paneId && name === 'VOL') paneOpts.gap = { bottom: 0 }
    const result = this.chart.createIndicator(
      base as Parameters<Chart['createIndicator']>[0],
      isStack,
      paneOpts as Parameters<Chart['createIndicator']>[2],
    )
    if (result && !paneId) {
      this.subPaneIds.set(name, result as string)
      this.subPaneRatios.set(result as string, ratio)
      this.retrackMtfPane(name, result as string)
      // A freshly-spawned sub-pane joins the session-shading column.
      this.addSessionHighlightToPane(result as string)
      this.chart.resize()
      this.notifyLayoutChanged()
      // A new sub-pane must grow a segment for every existing vertical line.
      this.scheduleVerticalLineSpanSync()
    }
    // A freshly-mounted sizing readout must see the lines already drawn.
    if (name === POSITION_SIZE_NAME) this.schedulePositionSizeLevelSync()
    // Created under a closed eye: bring it in line (the sweep skips system
    // chrome and sub-panes, exactly as the toggle itself does).
    if (result && paneId === CANDLE_PANE_ID && this.indicatorsHidden) {
      this.setIndicatorsVisible(false)
    }
    this.primeHtfSeries()
    return (result as string | null) ?? null
  }

  removeIndicator(paneId: string, name?: string): void {
    // Session shading sits on every sub-pane; left in place it would keep an
    // otherwise-empty sub-pane alive (klinecharts only drops a pane when its LAST
    // indicator goes). So pull the shading off this sub-pane first, then restore
    // it below if the pane survives the removal.
    const managesSession = this.sessionHighlightConfig !== null && paneId !== CANDLE_PANE_ID
    if (managesSession) this.chart.removeIndicator(paneId, SESSION_HIGHLIGHT_NAME)

    if (name) {
      this.chart.removeIndicator(paneId, name)
      this.mtfSessionInstances.delete(name)
    } else {
      this.chart.removeIndicator(paneId)
      for (const [n, tracked] of this.mtfSessionInstances) {
        if (tracked.paneId === paneId) this.mtfSessionInstances.delete(n)
      }
    }
    // Dropping a sub-pane is a height-only relayout, see notifyLayoutChanged.
    this.notifyLayoutChanged()
    // klinecharts drops a sub-pane once its last indicator is removed — drop
    // our ratio/id bookkeeping with it so later pane-ratio math and
    // getSubPaneTops() don't operate on a dead pane.
    if (paneId !== CANDLE_PANE_ID && !this.chart.getSize(paneId)) {
      for (const [indName, pid] of this.subPaneIds) {
        if (pid === paneId) this.subPaneIds.delete(indName)
      }
      this.subPaneRatios.delete(paneId)
      // The gone pane's segments are now orphaned — reconcile drops them.
      this.scheduleVerticalLineSpanSync()
    } else if (managesSession) {
      // Pane still has other indicators — put its shading back.
      this.addSessionHighlightToPane(paneId)
    }
  }

  /**
   * Live-update an existing indicator's calcParams, styles, visibility, or
   * precision without tearing it down and recreating it. Routes through
   * klinecharts `overrideIndicator`, which preserves the indicator's pane
   * binding and series identity — the difference between a 1-frame flicker
   * and a seamless edit. Returns `false` when the named indicator is not
   * mounted on the given pane (caller should fall back to createIndicator).
   */
  updateIndicator(
    paneId: string,
    name: string,
    patch: {
      calcParams?: unknown[]
      styles?: Partial<IndicatorStyle>
      visible?: boolean
      precision?: number
      shortName?: string
      extendData?: unknown
    },
  ): boolean {
    const existing = this.getIndicatorState(paneId, name)
    if (!existing) return false
    const override: IndicatorCreate = { name } as IndicatorCreate
    if (patch.calcParams !== undefined) {
      ;(override as { calcParams?: unknown[] }).calcParams = completeCalcParams(
        baseIndicatorName(name),
        patch.calcParams,
      )
    }
    if (patch.extendData !== undefined) {
      // Re-runs calc: klinecharts' setExtendData sets the calcFlag, which is
      // what makes a live timeframe change re-derive without a re-mount.
      ;(override as { extendData?: unknown }).extendData = this.mtfExtendData(
        name,
        paneId,
        patch.extendData,
      )
    }
    if (patch.styles !== undefined) {
      ;(override as { styles?: Partial<IndicatorStyle> }).styles = patch.styles
    }
    if (patch.visible !== undefined) {
      ;(override as { visible?: boolean }).visible = patch.visible
    }
    if (patch.precision !== undefined) {
      ;(override as { precision?: number }).precision = patch.precision
    }
    if (patch.shortName !== undefined) {
      ;(override as { shortName?: string }).shortName = patch.shortName
    }
    this.chart.overrideIndicator(override, paneId)
    if (patch.extendData !== undefined) this.primeHtfSeries()
    return true
  }

  /**
   * Read the current klinecharts state for a single indicator on a pane.
   * Returns null when the indicator is not mounted. Useful for seeding the
   * settings drawer with the indicator's live state.
   */
  getIndicatorState(paneId: string, name: string): Indicator | null {
    const result = this.chart.getIndicatorByPaneId(paneId, name)
    // klinecharts overloads return type based on args: with both paneId+name
    // we expect Indicator | null, but the typing surfaces all variants.
    if (!result) return null
    if (result instanceof Map) return null
    return result as Indicator
  }

  /**
   * Render read-only pattern annotations (design §6). Clears any prior pattern
   * overlays and draws the given `patterns` as locked klinecharts overlays keyed
   * off each pattern's `lines`. These are derived data — NOT persisted to
   * `SavedDrawing[]` and NOT user-draggable. Detection + curation happen upstream
   * (in the chart panel, on data change); this method only syncs the result.
   */
  setPatternOverlays(patterns: readonly Pattern[]): void {
    for (const id of this.patternOverlayIds) this.chart.removeOverlay(id)
    this.patternOverlayIds = []

    for (const p of patterns) {
      const line = p.lines[0]
      if (!line) continue
      const points = [
        { timestamp: line.from[0], value: line.from[1] },
        { timestamp: line.to[0], value: line.to[1] },
      ]
      const isTrend = p.type === 'trendline'
      const extendData: PatternExtendData = {
        color: colorForPattern(p.type, p.confidence),
        label: patternLabel(p),
        lineWidth: lineWidthForPattern(p),
        bandHalf: p.scoreComponents.bandHalf,
        bandColor: bandColorForPattern(p.type, p.confidence),
        // A trendline projects on to the right edge and labels its live value;
        // an S/R level stops at the last bar and labels off its start.
        extendRight: isTrend,
        labelAlign: isTrend ? 'end' : 'start',
      }
      const id = this.chart.createOverlay({
        name: PATTERN_OVERLAY_NAME,
        points,
        lock: true,
        extendData,
      } as Parameters<Chart['createOverlay']>[0]) as string | null
      if (id) this.patternOverlayIds.push(id)
    }
  }

  /**
   * Enable/configure pattern detection (design §6). The chart-panel toggle group
   * and confidence slider drive this. Re-renders immediately with current bars.
   */
  setPatternDetection(config: Partial<{ enabled: boolean } & CurateOptions>): void {
    this.patternConfig = { ...this.patternConfig, ...config }
    this.refreshPatternOverlays()
  }

  /**
   * Enable/configure the auto Fibonacci (Beta). Re-anchors immediately with
   * the current bars; clears when disabled.
   */
  setAutoFib(config: Partial<{ enabled: boolean; swingMult: number }>): void {
    this.autoFibConfig = { ...this.autoFibConfig, ...config }
    this.refreshAutoFib()
  }

  /**
   * Enable/configure auto trendlines (Beta). Shares the pattern-annotation
   * layer and the confidence slider with S/R; `maxPerDirection` is its own
   * curation budget (design §6: per-group budgets).
   */
  setAutoTrendlines(config: Partial<{ enabled: boolean; maxPerDirection: number }>): void {
    this.trendlineConfig = { ...this.trendlineConfig, ...config }
    this.refreshPatternOverlays()
  }

  /** Wicks or candle bodies for EVERY auto-analysis detector; recomputes all. */
  setAutoAnalysisTouchInput(input: TouchInput): void {
    if (input === this.touchInput) return
    this.touchInput = input
    this.refreshAutoAnalysis()
  }

  private clearAutoFib(): void {
    for (const id of this.autoFibOverlayIds) this.chart.removeOverlay(id)
    this.autoFibOverlayIds = []
  }

  /**
   * Recompute + redraw the auto Fib from the current bar list: the most recent
   * swing of at least `swingMult` ATRs, drawn as a locked, non-persisted
   * `autoFibonacci` overlay extended to the right edge. Detection runs once
   * here, never in the overlay's per-frame render.
   */
  private refreshAutoFib(): void {
    if (!this.autoFibConfig.enabled) {
      if (this.autoFibOverlayIds.length) this.clearAutoFib()
      return
    }
    const bars = this.chart.getDataList() as unknown as EdgeBar[]
    const pair = pickAutoFibSwing(bars, {
      swingMult: this.autoFibConfig.swingMult,
      touchInput: this.touchInput,
      // The current session on intraday charts, months on daily: the biggest
      // swing of the window, not the last half hour.
      lookbackBars: autoFibLookbackBars(bars, this.period),
    })
    this.clearAutoFib()
    if (!pair) return
    const id = this.createOverlayAtPoints(AUTO_FIB_OVERLAY_NAME, autoFibPoints(pair), undefined, {
      extendRight: true,
    })
    if (id) this.autoFibOverlayIds.push(id)
  }

  /**
   * Every Beta auto-analysis layer in one go — the shared hook for the data
   * paths (load, period change, new bar, drawings wipe). Each layer is its own
   * no-op when disabled, so this is cheap to call unconditionally.
   */
  private refreshAutoAnalysis(): void {
    this.refreshPatternOverlays()
    this.refreshAutoFib()
  }

  /** New-bar trigger: coalesce a burst of `data:updated` into one recompute. */
  private scheduleAutoAnalysisRefresh(): void {
    if (!this.patternConfig.enabled && !this.autoFibConfig.enabled && !this.trendlineConfig.enabled)
      return
    if (this.autoAnalysisTimer) clearTimeout(this.autoAnalysisTimer)
    this.autoAnalysisTimer = setTimeout(() => {
      this.autoAnalysisTimer = null
      this.refreshAutoAnalysis()
    }, 250)
  }

  /**
   * Recompute + redraw pattern overlays from the current bar list. Runs the pure
   * detect → curate pipeline ONCE here (on data change / config change), never in
   * the overlay's per-frame render. No-op (and clears) when detection is disabled.
   */
  private refreshPatternOverlays(): void {
    const srOn = this.patternConfig.enabled
    const trendOn = this.trendlineConfig.enabled
    if (!srOn && !trendOn) {
      if (this.patternOverlayIds.length) this.setPatternOverlays([])
      return
    }
    const bars = this.chart.getDataList() as unknown as EdgeBar[]
    if (bars.length === 0) {
      this.setPatternOverlays([])
      return
    }
    // Detect over the FULL loaded history — a level from months ago is still real
    // if price is near it now (e.g. MBOT's 2025-09-08 level). Relevance is handled
    // downstream: the proximity gate demotes far-from-price levels, recency favors
    // recent touches, and curation caps the count. (Earlier we sliced to the
    // visible window; the proximity gate makes that restriction unnecessary and it
    // was dropping still-valid historical levels.)
    const ticker = this.symbol.ticker
    const timeframe = periodKeyOf(this.period) ?? ''
    const { minConfidence, maxN } = this.patternConfig
    const floor = minConfidence ?? 0
    // Build the STABLE candidate set independent of the slider (curate at floor 0),
    // then let the slider only HIDE. Same bars → same stable set every time, so
    // moving the confidence slider strictly subtracts lines — it never re-selects,
    // moves, or promotes new ones (curation's reservation/overlap-suppression are
    // not monotonic in the floor, which is why floor-coupled curation made lines
    // "change" as the slider moved). Each group curates under its OWN budget
    // (design §6) so a busy S/R layer cannot crowd out a strong trendline.
    const out: Pattern[] = []
    if (srOn) {
      const stable = curatePatterns(
        detectPatterns(bars, ticker, timeframe, { touchInput: this.touchInput }),
        {
          minConfidence: 0,
          maxN,
        },
      )
      out.push(...stable.filter((p) => p.confidence >= floor))
    }
    if (trendOn) {
      const stable = curateTrendlines(
        detectTrendlines(bars, ticker, timeframe, { touchInput: this.touchInput }),
        {
          minConfidence: 0,
          maxPerDirection: this.trendlineConfig.maxPerDirection,
        },
      )
      out.push(...stable.filter((p) => p.confidence >= floor))
    }
    this.setPatternOverlays(out)
  }

  createOverlay(
    name: string,
    initialStyle?: OverlayStyleOverride,
    initialExtendData?: unknown,
  ): string | null {
    const id = this.chart.createOverlay({
      name,
      // Per-tool default style, applied from the first frame so the drawing is
      // styled while it's being drawn (not just after draw-end).
      ...(initialStyle ? { styles: this.overlayStyleToKline(initialStyle) } : {}),
      // Saved per-tool extendData (a Fib's level visibility) rides along the
      // same way; the draw-end sync payload reads it back off the overlay, so
      // it persists with the drawing. The hotkey placement paths already did
      // this; the drawing-bar path did not, which is why "Extensions off, save
      // as default" never reached the next Fib.
      ...(initialExtendData !== undefined ? { extendData: initialExtendData } : {}),
      // Magnet is per-overlay in klinecharts, so it has to ride along on every
      // create or new drawings silently ignore the toggle.
      mode: this.klineOverlayMode(),
      ...this.overlayDrawCallbacks(),
    } as Parameters<Chart['createOverlay']>[0])
    const oid = (id as string | null) ?? null
    // Remember the default so it rides along into the persisted create payload.
    if (oid && initialStyle) this.pendingCreateStyles.set(oid, initialStyle)
    // Armed → drawing: cancellable via Escape until the draw completes.
    this.pendingDrawId = oid
    return oid
  }

  /**
   * Standard interaction callbacks for a drawable line/shape overlay: persist on
   * draw-end / move-end / remove, and track selection for keyboard-delete.
   * Shared by the hotkey placement helpers below.
   */
  private overlayDrawCallbacks(): Record<string, unknown> {
    return {
      onDrawing: (event: OverlayEvent) => {
        this.emitDrawProgress(event.overlay)
        return false
      },
      onDrawEnd: (event: OverlayEvent) => {
        this.handleOverlayDrawEnd(event)
        return false
      },
      onPressedMoving: (event: OverlayEvent) => {
        // Keep the sub-pane segments glued to the line WHILE it's dragged, not
        // just on release — coalesced to one reconcile per frame.
        if (event.overlay.name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
        // A dragged horizontal line/ray re-sizes the position readout live.
        if (MANUAL_LEVEL_OVERLAY_NAMES.has(event.overlay.name)) {
          this.schedulePositionSizeLevelSync()
        }
        return false
      },
      onPressedMoveEnd: (event: OverlayEvent) => {
        this.handleOverlayMoveEnd(event)
        return false
      },
      onRemoved: (event: OverlayEvent) => {
        this.handleOverlayRemoved(event)
        return false
      },
      onSelected: (event: OverlayEvent) => {
        this.selectedOverlayId = event.overlay.id
        this.emitOverlaySelection(event)
        this.container.focus()
        return false
      },
      onDeselected: () => {
        this.selectedOverlayId = null
        this.eventBus.emit('overlay:deselected', undefined)
        return false
      },
      onClick: (event: OverlayEvent) => {
        // The position plan's on-chart "+ Add entry" pill. Routed by
        // figureKey, the same channel the cursor feedback uses.
        this.handlePlanAddEntryClick(event)
        return false
      },
      onRightClick: (event: OverlayEvent) => {
        // MUST return true for a COMPLETED drawing: klinecharts' default
        // figure right-click action is `overlayStore.removeInstance(overlay)`
        // — i.e. right-click DELETES the drawing (and onRemoved persists the
        // delete) unless the callback consumes the event. The body context
        // menu still opens via the DOM `contextmenu` listener in EdgeChart,
        // which is independent of this. Mid-draw the same callback backs
        // klinecharts' right-click-cancels-the-gesture — decline (false) so
        // that keeps working alongside Escape.
        const drawing = (event.overlay as { isDrawing?: () => boolean }).isDrawing?.() === true
        return !drawing
      },
      onDoubleClick: (event: OverlayEvent) => {
        // A drawing was double-clicked → open its settings. Flag it so the
        // body double-click reset (the native `dblclick` fires for the same
        // click too) doesn't ALSO reset the chart out from under the edit.
        // Fallback-cleared next tick in case the native dblclick never reaches
        // the container (e.g. consumed by klinecharts).
        this.overlayEditFromDblClick = true
        setTimeout(() => {
          this.overlayEditFromDblClick = false
        }, 0)
        this.emitOverlayEdit(event)
        return false
      },
    }
  }

  /**
   * Emit `overlay:selected` so a host can show the floating quick-edit bar.
   *
   * Distinct from `overlay:edit` (double-click → full settings drawer): this
   * fires on a single click and carries `lock`/`visible` too, because the quick
   * bar acts on them directly.
   */
  private emitOverlaySelection(event: OverlayEvent): void {
    // The ruler is ephemeral — never persisted, torn down on the next click.
    // Offering colour / width / lock / duplicate for something that is about to
    // disappear is meaningless, so it gets no quick-edit bar.
    if (event.overlay.name === 'measure') return
    const overlay = event.overlay as {
      id: string
      name: string
      points?: Array<{ timestamp?: number; value?: number }>
      styles?: unknown
      lock?: boolean
      visible?: boolean
    }
    this.eventBus.emit('overlay:selected', {
      id: overlay.id,
      name: overlay.name,
      points: overlay.points,
      // The APP-LEVEL override (raw hex), not klinecharts' resolved styles.
      // Those come back already alpha-composed as `rgba(...)`, and re-applying
      // opacity to an rgba string is a no-op (`withAlphaOrKeep` passes non-hex
      // through) — so opacity could be lowered but never raised again.
      // klinecharts selects a drawing on its FIRST click, before draw-end has
      // recorded it in overlayStyleById, so fall back to the style it is being
      // drawn with before klinecharts' own.
      styles:
        this.overlayStyleById.get(overlay.id) ??
        this.pendingCreateStyles.get(overlay.id) ??
        overlay.styles,
      lock: overlay.lock ?? false,
      visible: overlay.visible ?? true,
    })
  }

  /**
   * Emit the `overlay:edit` EventBus event so the host (EdgeChart, Phase 2) can
   * open the overlay settings panel. Payload mirrors the live overlay:
   * `{ id, name, points, styles }` read straight off `event.overlay`.
   */
  private emitOverlayEdit(event: OverlayEvent): void {
    const overlay = event.overlay as {
      id: string
      name: string
      points?: Array<{ timestamp?: number; value?: number }>
      styles?: unknown
      extendData?: unknown
    }
    // Container-relative pixel coords of the double-click, so the host can
    // anchor the settings drawer beside the click (converted to viewport coords
    // there). Undefined for non-pointer opens (keyboard) → host centers instead.
    const x = typeof event.x === 'number' ? event.x : undefined
    const y = typeof event.y === 'number' ? event.y : undefined
    this.eventBus.emit('overlay:edit', {
      id: overlay.id,
      name: overlay.name,
      points: overlay.points,
      styles: overlay.styles,
      extendData: overlay.extendData,
      x,
      y,
    })
  }

  /**
   * Arm history mode: tint the vertical crosshair yellow and paint the candle
   * under the cursor yellow. No-op unless Shift is held on a daily chart. The
   * crosshair tint applies even before a bar is resolved; the candle overlay is
   * created/moved in place (via `overrideOverlay`, so it tracks without flicker)
   * once the bar's OHLC is known.
   */
  private updateBarHighlight(): void {
    if (
      !this.intradayDrillEnabled ||
      !this.altHeld ||
      this.altChordConsumed ||
      this.period.timespan !== 'day'
    ) {
      this.clearBarHighlight()
      return
    }
    this.setCrosshairColor(HISTORY_CROSSHAIR_COLOR)

    const cursor = this.lastCursor
    if (
      !cursor?.timestamp ||
      cursor.high == null ||
      cursor.low == null ||
      cursor.open == null ||
      cursor.close == null
    ) {
      // No bar under the cursor yet — keep the crosshair tint, drop stale paint.
      this.removeBarHighlightOverlay()
      return
    }
    // Four points (high, low, open, close) so the fill traces the candle's wick
    // and body in price space.
    const points = [
      { timestamp: cursor.timestamp, value: cursor.high },
      { timestamp: cursor.timestamp, value: cursor.low },
      { timestamp: cursor.timestamp, value: cursor.open },
      { timestamp: cursor.timestamp, value: cursor.close },
    ]
    if (this.barHighlightId) {
      this.chart.overrideOverlay({ id: this.barHighlightId, points } as Parameters<
        Chart['overrideOverlay']
      >[0])
    } else {
      this.barHighlightId =
        (this.chart.createOverlay({
          name: BAR_HIGHLIGHT_NAME,
          points,
          lock: true,
        } as Parameters<Chart['createOverlay']>[0]) as string | null) ?? null
    }
  }

  /** Remove the yellow candle overlay + restore the crosshair to the default. */
  private clearBarHighlight(): void {
    this.removeBarHighlightOverlay()
    if (this.savedCrosshairColor != null) {
      this.setCrosshairColor(this.savedCrosshairColor)
      this.savedCrosshairColor = null
    }
  }

  private removeBarHighlightOverlay(): void {
    if (this.barHighlightId) {
      this.chart.removeOverlay(this.barHighlightId)
      this.barHighlightId = null
    }
  }

  /**
   * Override the vertical crosshair line color, capturing the current value
   * into `savedCrosshairColor` the first time so it can be restored later.
   */
  private setCrosshairColor(color: string): void {
    if (this.savedCrosshairColor == null) {
      const current = this.chart.getStyles?.()?.crosshair?.vertical?.line?.color
      this.savedCrosshairColor = current ?? '#758696'
    }
    this.chart.setStyles({ crosshair: { vertical: { line: { color } } } } as Parameters<
      typeof this.chart.setStyles
    >[0])
  }

  /**
   * Place a fully-pointed overlay at the cursor and finish it immediately.
   * klinecharts treats a fully-pointed overlay as finished, so `onDrawEnd`
   * never fires — we persist + register it for sync manually via
   * `persistOverlayCreate`.
   *
   * "Fully pointed" means `points.length >= totalStep - 1`; anything short of
   * that leaves the overlay mid-draw (armed) — so this is only for tools whose
   * `totalStep` is 2. `persistOverlayCreate` refuses to save a still-drawing
   * overlay, so a mismatch here shows up as a drawing that never persists
   * rather than as a corrupt half-saved record.
   */
  private placeOverlayAtCursor(
    name: string,
    cursor: { timestamp: number; value: number; paneId: string },
  ): string | null {
    const points = [{ timestamp: cursor.timestamp, value: cursor.value }]
    // Apply the effective per-tool default (built-in color + any saved user
    // default) so hotkey-placed lines match the ones drawn from the DrawingBar.
    const initial = resolveOverlayDefault(name)
    const id = this.chart.createOverlay(
      {
        name,
        points,
        ...(initial?.style ? { styles: this.overlayStyleToKline(initial.style) } : {}),
        // Saved per-instance defaults (a Fib's level colours / hidden set) seed
        // the new overlay alongside the line style.
        ...(initial?.extendData !== undefined ? { extendData: initial.extendData } : {}),
        mode: this.klineOverlayMode(),
        ...this.overlayDrawCallbacks(),
      } as Parameters<Chart['createOverlay']>[0],
      cursor.paneId,
    )
    const oid = (id as string | null) ?? null
    if (oid) {
      if (initial?.style) this.pendingCreateStyles.set(oid, initial.style)
      this.persistOverlayCreate(oid, name, points)
      // Single-click placement may not fire `onDrawEnd`, so grow the sub-pane
      // segments here too (idempotent if draw-end also fires).
      if (name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
      // ...and for the same reason, snapshot the sizing indicator's manual
      // levels. Without this a click-placed horizontal line did not reach the
      // readout until the user happened to DRAG it (which fires
      // handleOverlayMoveEnd), so the pill sat on "no data" next to a line
      // that was plainly on screen.
      if (MANUAL_LEVEL_OVERLAY_NAMES.has(name)) this.schedulePositionSizeLevelSync()
    }
    return oid
  }

  /**
   * Arm a multi-point overlay (ray line) with its first point anchored at the
   * cursor, then hand off to klinecharts' progressive drawing — it follows the
   * pointer and completes on the user's next click, firing `onDrawEnd` (which
   * persists it). No manual persist here.
   */
  private startOverlayAtCursor(
    name: string,
    cursor: { timestamp: number; value: number; paneId: string },
  ): string | null {
    // Effective per-tool default — matches the DrawingBar-drawn overlays.
    const initial = resolveOverlayDefault(name)
    const id = this.chart.createOverlay(
      {
        name,
        points: [{ timestamp: cursor.timestamp, value: cursor.value }],
        ...(initial?.style ? { styles: this.overlayStyleToKline(initial.style) } : {}),
        ...(initial?.extendData !== undefined ? { extendData: initial.extendData } : {}),
        mode: this.klineOverlayMode(),
        ...this.overlayDrawCallbacks(),
      } as Parameters<Chart['createOverlay']>[0],
      cursor.paneId,
    )
    const oid = (id as string | null) ?? null
    // First point is placed but the draw completes on the next click — Escape
    // can still cancel until then. Stash the default so onDrawEnd's persist
    // carries it (multi-point tools persist via handleOverlayDrawEnd).
    if (oid && initial?.style) this.pendingCreateStyles.set(oid, initial.style)
    this.pendingDrawId = oid
    return oid
  }

  /**
   * Position (risk/reward) tool. Placed with a single click — the registered
   * overlay finishes after one click, then `expandPositionOverlay` turns that
   * lone anchor into the four draggable handles (entry-left, entry-right,
   * target, stop). Wired separately from `createOverlay` so the generic
   * line/shape tools keep their normal multi-click drawing flow.
   */
  /**
   * Arm a drawing tool, routing to the right create path for its shape.
   *
   * Position tools are NOT plain overlays: they are registered with
   * `totalStep: 2` (one click) and must then be expanded to 4 anchors by
   * `createPositionOverlay`. Going through `createOverlay` instead leaves a
   * single orphaned point that disappears on the next click — which is exactly
   * what the grid's shared drawing bar used to do.
   *
   * `EdgeChart` wraps this to also apply the per-tool default style and arm the
   * active-tool highlight; see `EdgeChartApi.selectTool`.
   */
  selectTool(
    name: string,
    initialStyle?: OverlayStyleOverride,
    initialExtendData?: unknown,
  ): string | null {
    return name === 'longPositionTool' || name === 'shortPositionTool'
      ? this.createPositionOverlay(name, initialStyle)
      : this.createOverlay(name, initialStyle, initialExtendData)
  }

  createPositionOverlay(name: string, initialStyle?: OverlayStyleOverride): string | null {
    const id = this.chart.createOverlay({
      name,
      ...(initialStyle ? { styles: this.overlayStyleToKline(initialStyle) } : {}),
      mode: this.klineOverlayMode(),
      // Same standard interaction callbacks as the line/shape tools, except
      // onDrawEnd is wrapped to expand the single click into the four handles
      // first. (Deliberately no onMouseEnter/onMouseLeave: defining them would
      // make klinecharts skip its native hover redraw — labels read the live
      // hover/click store at render time via the constructor's resolver.)
      ...this.overlayDrawCallbacks(),
      onDrawEnd: (event: OverlayEvent) => {
        this.expandPositionOverlay(event.overlay, name)
        this.handleOverlayDrawEnd(event)
        return false
      },
    } as Parameters<Chart['createOverlay']>[0])
    const oid = (id as string | null) ?? null
    if (oid && initialStyle) this.pendingCreateStyles.set(oid, initialStyle)
    // Armed → drawing: cancellable via Escape until the draw completes.
    this.pendingDrawId = oid
    return oid
  }

  /**
   * Apply (or clear) the drawing-hover cursor.
   *
   * klinecharts sets `cursor: 'crosshair'` INLINE on every pane container
   * (dist/index.esm.js:8891), and those panes are descendants of our container.
   * An inline cursor on a child always beats one on the parent, so setting
   * `this.container.style.cursor` alone is invisible — the override has to land
   * on the same elements klinecharts styled.
   *
   * Each element's original cursor is stashed on first override and restored on
   * clear, so we never guess what the base cursor was (panes differ: the chart
   * body is `crosshair`, axes are `ns-resize` / `ew-resize`).
   */
  private applyDrawingCursor(cursor: PositionCursor): void {
    // Re-queried per transition rather than cached: panes come and go as
    // indicators are added, and this only runs on enter/leave, not per move.
    const styled = Array.from(this.container.querySelectorAll<HTMLElement>('*')).filter(
      (el) => el.style.cursor !== '' || el.dataset.edgeBaseCursor !== undefined,
    )
    for (const el of styled) {
      if (cursor !== null) {
        if (el.dataset.edgeBaseCursor === undefined) el.dataset.edgeBaseCursor = el.style.cursor
        el.style.cursor = cursor
      } else if (el.dataset.edgeBaseCursor !== undefined) {
        el.style.cursor = el.dataset.edgeBaseCursor
        delete el.dataset.edgeBaseCursor
      }
    }
    this.container.style.cursor = cursor ?? ''
  }

  /** Expand a freshly single-clicked position anchor into the four handles. */
  private expandPositionOverlay(
    overlay: {
      id: string
      points?: Array<{ dataIndex?: number; timestamp?: number; value?: number }>
    },
    name: string,
  ): void {
    const pts = overlay.points
    const p0 = pts?.[0]
    if (!p0 || typeof p0.value !== 'number' || typeof p0.dataIndex !== 'number') return
    if (pts && pts.length >= 4) return // already expanded

    const entry = p0.value
    const di = p0.dataIndex
    // Short flips the box: profit (target) sits below entry, risk (stop) above.
    const short = name === 'shortPositionTool'

    const dataList = this.chart.getDataList() as Array<{
      timestamp: number
      high?: number
      low?: number
    }>
    // Size the default box off the VISIBLE window, not a fixed % of price —
    // a flat `entry * 1.05` is off-screen on a 5m chart and invisible on a
    // multi-year one. See `positionDefaultGeometry`.
    const visible = this.chart.getVisibleRange()
    const span = this.visiblePriceSpan()
    const { target, stop, halfWidth } = positionDefaultGeometry({
      entry,
      // `positionDefaultGeometry` only uses the SPAN, so hand it a normalised
      // low/high pair rather than duplicating the scan. A zero span (flat or
      // pre-layout data) makes it fall back to a price-relative box.
      visibleLow: 0,
      visibleHigh: span,
      visibleBars: visible.to - visible.from,
      short,
    })

    const baseTs =
      typeof p0.timestamp === 'number' ? p0.timestamp : (dataList[Math.round(di)]?.timestamp ?? 0)
    const interval = dataList.length > 1 ? dataList[1].timestamp - dataList[0].timestamp : 60_000
    const leftTs = baseTs - halfWidth * interval
    const rightTs = baseTs + halfWidth * interval

    this.chart.overrideOverlay({
      id: overlay.id,
      points: [
        { dataIndex: di - halfWidth, timestamp: leftTs, value: entry },
        { dataIndex: di + halfWidth, timestamp: rightTs, value: entry },
        // Target/stop grab handles align to the left edge (same x as entry-left).
        { dataIndex: di - halfWidth, timestamp: leftTs, value: target },
        { dataIndex: di - halfWidth, timestamp: leftTs, value: stop },
      ],
    } as Parameters<Chart['overrideOverlay']>[0])
  }

  createOverlayAtPoints(
    name: string,
    points: Array<{ timestamp: number; value?: number }>,
    styles?: OverlayStyleOverride,
    extendData?: unknown,
  ): string | null {
    const id = this.chart.createOverlay({
      name,
      points,
      lock: true,
      ...(styles ? { styles: this.overlayStyleToKline(styles) } : {}),
      ...(extendData !== undefined ? { extendData } : {}),
    } as Parameters<Chart['createOverlay']>[0])
    if (name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
    return (id as string | null) ?? null
  }

  /**
   * Create a persisted, UNLOCKED, editable overlay at explicit points —
   * for programmatic placement (e.g. the Trade Planner pushing a position
   * drawing onto a chart), not user drawing. Modelled on
   * `placeOverlayAtCursor`: klinecharts treats a fully-pointed overlay as
   * finished, so `onDrawEnd` never fires — this persists + registers it for
   * sync manually via `persistOverlayCreate`.
   *
   * Use this (not `createOverlayAtPoints`) whenever the placed overlay
   * should be user-editable and survive reload. `createOverlayAtPoints`
   * hardcodes `lock: true` and never persists — it exists for locked
   * annotation markers only.
   *
   * For `longPositionTool` / `shortPositionTool`, `points` must be the four
   * anchors `expandPositionOverlay` seeds, in this order: entry-left,
   * entry-right, target, stop. Entry is a horizontal segment (two points at
   * the same value, not a single point) — a plan carrying only an
   * entry/target/stop triple needs the entry duplicated into two points
   * before calling this. A mismatched count returns null without creating
   * anything, the same as the chart-not-ready case below, rather than
   * throwing — both are "could not place the overlay" outcomes the caller
   * should treat identically.
   */
  placeOverlayAtPoints(
    name: string,
    rawPoints: Array<{ timestamp?: number; barOffset?: number; value: number }>,
    opts?: { styles?: OverlayStyleOverride; extendData?: unknown; paneId?: string },
  ): string | null {
    if ((name === 'longPositionTool' || name === 'shortPositionTool') && rawPoints.length !== 4) {
      return null
    }
    // A point may name a bar relative to the last loaded bar (`barOffset`,
    // 0 = last bar, negative = earlier) instead of a timestamp, so a caller
    // outside the chart (the Trade Planner) can anchor a drawing on what is on
    // screen without reading the data list itself. No data yet: nothing to
    // anchor on, so nothing is placed.
    const points: Array<{ timestamp: number; value: number }> = []
    let dataList: Array<{ timestamp: number }> | null = null
    for (const p of rawPoints) {
      if (typeof p.timestamp === 'number') {
        points.push({ timestamp: p.timestamp, value: p.value })
        continue
      }
      dataList ??= this.chart.getDataList() as Array<{ timestamp: number }>
      if (dataList.length === 0) return null
      const idx = Math.max(
        0,
        Math.min(dataList.length - 1, dataList.length - 1 + (p.barOffset ?? 0)),
      )
      points.push({ timestamp: dataList[idx].timestamp, value: p.value })
    }
    // Per-tool default (built-in + saved user default) so a programmatically
    // placed overlay matches what the DrawingBar would have produced, unless
    // the caller supplies its own — e.g. the Trade Planner's long/short
    // colour.
    const initial = resolveOverlayDefault(name)
    const styles = opts?.styles ?? initial?.style
    const extendData = opts?.extendData !== undefined ? opts.extendData : initial?.extendData
    const id = this.chart.createOverlay(
      {
        name,
        points,
        ...(styles ? { styles: this.overlayStyleToKline(styles) } : {}),
        ...(extendData !== undefined ? { extendData } : {}),
        mode: this.klineOverlayMode(),
        lock: false,
        ...this.overlayDrawCallbacks(),
      } as Parameters<Chart['createOverlay']>[0],
      opts?.paneId,
    )
    const oid = (id as string | null) ?? null
    if (oid) {
      if (styles) this.pendingCreateStyles.set(oid, styles)
      this.persistOverlayCreate(oid, name, points, extendData)
      if (name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
      if (MANUAL_LEVEL_OVERLAY_NAMES.has(name)) this.schedulePositionSizeLevelSync()
    }
    return oid
  }

  /**
   * Price range across the currently visible bars, or 0 when unavailable.
   * Shared by the position tool's default sizing and duplicate's offset — both
   * need "a fraction of what the user can actually see", not a fraction of price.
   */
  private visiblePriceSpan(): number {
    const dataList = this.chart.getDataList() as Array<{ high?: number; low?: number }>
    const visible = this.chart.getVisibleRange()
    let low = Number.POSITIVE_INFINITY
    let high = Number.NEGATIVE_INFINITY
    for (let i = Math.max(0, visible.from); i < Math.min(dataList.length, visible.to); i += 1) {
      const bar = dataList[i]
      if (typeof bar?.low === 'number' && bar.low < low) low = bar.low
      if (typeof bar?.high === 'number' && bar.high > high) high = bar.high
    }
    return Number.isFinite(low) && Number.isFinite(high) && high > low ? high - low : 0
  }

  /**
   * Duplicate a drawing, offset one bar right and slightly DOWN so the copy is
   * visibly distinct rather than sitting exactly on top of the original.
   *
   * Deliberately NOT `createOverlayAtPoints`, which hardcodes `lock: true` for
   * programmatic markers — a duplicate must be immediately editable. Goes
   * through the normal draw callbacks so it persists and syncs like any
   * user-drawn overlay.
   */
  duplicateOverlay(id: string): string | null {
    const src = this.chart.getOverlayById(id) as unknown as {
      name?: string
      points?: Array<{ timestamp?: number; value?: number; dataIndex?: number }>
      styles?: unknown
      extendData?: unknown
    } | null
    if (!src?.name || !src.points?.length) return null

    const dataList = this.chart.getDataList() as Array<{ timestamp: number }>
    const barMs = dataList.length > 1 ? dataList[1].timestamp - dataList[0].timestamp : 60_000
    // Offset the copy right AND down so it reads as a separate drawing rather
    // than a redraw of the original. The vertical step is a fraction of the
    // VISIBLE price span, not of price — a fixed percentage would be invisible
    // on a wide chart and a mile off on a tight intraday one.
    const span = this.visiblePriceSpan()
    const dropBy = span > 0 ? span * DUPLICATE_DROP_FRACTION : 0
    const points = src.points.map((p) => ({
      ...(typeof p.timestamp === 'number' ? { timestamp: p.timestamp + barMs } : {}),
      ...(typeof p.value === 'number' ? { value: p.value - dropBy } : {}),
    }))

    const newId = this.chart.createOverlay({
      name: src.name,
      points,
      ...(src.styles ? { styles: src.styles } : {}),
      ...(src.extendData !== undefined ? { extendData: src.extendData } : {}),
      ...this.overlayDrawCallbacks(),
    } as Parameters<Chart['createOverlay']>[0])
    const oid = (newId as string | null) ?? null
    if (oid) {
      // Seed the copy's style so it PERSISTS, not just renders. Applying it to
      // the live overlay above is not enough — `persistOverlayCreate` reads
      // from `pendingCreateStyles`, so without this the saved copy has no
      // style and reloads with the tool default.
      const srcStyle = this.overlayStyleById.get(id)
      if (srcStyle) this.pendingCreateStyles.set(oid, srcStyle)
      // Persist + broadcast the copy, exactly as a fresh drawing would be.
      this.persistOverlayCreate(oid, src.name, points, src.extendData)
      // Programmatic create never fires `onDrawEnd`, so grow the copy's
      // sub-pane span segments here (the interactive-draw path relies on
      // draw-end for this).
      if (src.name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
    }
    return oid
  }

  /** Show or hide the canvas last-price + countdown pill (utils/barCountdownPill.ts). */
  setBarCountdownVisible(on: boolean): void {
    this.barCountdownOn = on
    this.barCountdownText = null
    if (!on) {
      if (this.barCountdownId) {
        this.chart.removeOverlay(this.barCountdownId)
        this.barCountdownId = null
      }
      return
    }
    if (this.barCountdownId) return
    this.barCountdownId =
      (this.chart.createOverlay({
        name: BAR_COUNTDOWN_PILL_NAME,
        // Unused: the template reads readBarCountdown at paint time. klinecharts
        // only paints an overlay that has a point (KC:8972-8975).
        points: [{ value: 0 }],
        lock: true,
        zLevel: BAR_COUNTDOWN_Z_LEVEL,
        extendData: { read: this.readBarCountdown },
      } as Parameters<Chart['createOverlay']>[0]) as string | null) ?? null
  }

  /** Called once a second by the host: repaint only when the countdown text changed.
   *  Price and colour changes arrive with data ticks, which repaint everything. */
  tickBarCountdown(now: number): void {
    if (!this.barCountdownId) return
    const text = this.readBarCountdown(now)?.countdown ?? null
    if (text === this.barCountdownText) return
    this.barCountdownText = text
    this.repaintBarCountdown()
  }

  /** Repaint the overlay canvases so the pill shows the new text. A new
   *  extendData object is required, or klinecharts skips the update (KC:1297-1302).
   *  The id-less updatePane(1) this triggers repaints overlay canvases only. */
  private repaintBarCountdown(): void {
    if (!this.barCountdownId) return
    this.chart.overrideOverlay({
      id: this.barCountdownId,
      extendData: { read: this.readBarCountdown },
    } as Parameters<Chart['overrideOverlay']>[0])
  }

  /**
   * Anchor for the current-bar countdown pill (utils/barCountdownPill.ts): that
   * bar's last close, END timestamp, and the price text + pill colour matched to
   * the native last-price tag (resolved up/down/no-change colour, price at the
   * chart's precision). Null when there's no data. `period.ms` is exact for
   * intraday bars and an approximation for week/month/year.
   */
  getBarCountdownAnchor(): {
    close: number
    barEnd: number
    price: string
    color: string
  } | null {
    const dataList = this.chart.getDataList() as Array<{
      timestamp: number
      open: number
      close: number
    }>
    if (dataList.length === 0) return null
    const i = dataList.length - 1
    const last = dataList[i]

    // Colour by the current bar's OWN direction (close vs open) so the tag matches
    // the candle AND the last-price line, using the theme's resolved last-mark
    // colours so it's indistinguishable from the native tag.
    const lastMark = (
      this.chart.getStyles?.() as {
        candle?: {
          priceMark?: { last?: { upColor?: string; downColor?: string; noChangeColor?: string } }
        }
      } | null
    )?.candle?.priceMark?.last
    const color =
      last.close === last.open
        ? (lastMark?.noChangeColor ?? '#4b5563')
        : last.close > last.open
          ? (lastMark?.upColor ?? '#22c55e')
          : (lastMark?.downColor ?? '#ef4444')

    const precision = this.chart.getPriceVolumePrecision().price
    return {
      close: last.close,
      barEnd: last.timestamp + this.period.ms,
      price: last.close.toFixed(precision),
      color,
    }
  }

  removeOverlay(id?: string): void {
    if (id) {
      this.chart.removeOverlay(id)
      this.overlayStyleById.delete(id)
    } else {
      this.chart.removeOverlay()
      this.overlayStyleById.clear()
      this.overlaysHidden = false
      // The id-less wipe takes the read-only auto-analysis annotations with it
      // (klinecharts has no "user drawings only" remove). They are derived
      // chrome, not drawings, so put them straight back rather than leaving
      // the chart bare until the next data load happens to redraw them.
      this.patternOverlayIds = []
      this.autoFibOverlayIds = []
      this.refreshAutoAnalysis()
      // Same for the Pre/Post price mark: chart chrome, not a drawing.
      this.extendedMarkId = null
      this.syncExtendedSessionMark()
      // And the countdown pill: the wipe took it too.
      if (this.barCountdownOn) {
        this.barCountdownId = null
        this.setBarCountdownVisible(true)
      }
      // And the alert lines: data, not drawings.
      if (this.priceAlertOverlayIds) {
        this.priceAlertOverlayIds.clear()
        this.syncPriceAlertLines()
      }
    }
    // A removed drawing cannot stay "selected". klinecharts does not fire
    // `onDeselected` when an overlay is destroyed, so without this the host's
    // selection chrome (the floating quick-edit bar) lingers, pointing at an
    // overlay that no longer exists. Covers every delete path — quick bar,
    // Delete key, trash-all, context menu.
    if (!id || id === this.selectedOverlayId) {
      this.selectedOverlayId = null
      this.eventBus.emit('overlay:deselected', undefined)
    }
    // Forget a remembered copy once its source is gone, so a later paste can't
    // resurrect a deleted drawing.
    if (!id || id === this.copiedOverlayId) this.copiedOverlayId = null
  }

  /**
   * Show/hide user DRAWINGS (the drawing-bar eye toggle). An id-less override
   * hits every overlay, then the system overlays — the premarket price mark and
   * the transient Alt-hover bar highlight — are put back: they're chart chrome,
   * not user drawings. Exposed so the scanner can broadcast across cells.
   */
  setOverlaysVisible(visible: boolean): void {
    this.overlaysHidden = !visible
    this.chart.overrideOverlay({ visible } as Parameters<Chart['overrideOverlay']>[0])
    // The id-less override above already toggled the span segments too (they're
    // user-drawing overlays); reconcile so a later state change re-derives from
    // the primaries' visibility rather than this blanket value.
    this.scheduleVerticalLineSpanSync()
    // Hidden drawings drop out of the sizing readout's manual-level set.
    this.schedulePositionSizeLevelSync()
    if (!visible) {
      if (this.extendedMarkId) {
        this.chart.overrideOverlay({ id: this.extendedMarkId, visible: true } as Parameters<
          Chart['overrideOverlay']
        >[0])
      }
      if (this.barCountdownId) {
        this.chart.overrideOverlay({ id: this.barCountdownId, visible: true } as Parameters<
          Chart['overrideOverlay']
        >[0])
      }
      if (this.barHighlightId) {
        this.chart.overrideOverlay({ id: this.barHighlightId, visible: true } as Parameters<
          Chart['overrideOverlay']
        >[0])
      }
      // Data lines, not drawings: dilution / offering levels and the Today
      // reference price. They are plain overlays, so the blanket hide caught
      // them by accident (and they returned on the next reload regardless).
      // By NAME, because their ids belong to the hooks that create them.
      for (const name of DATA_LINE_OVERLAY_NAMES) {
        this.chart.overrideOverlay({ name, visible: true } as Parameters<
          Chart['overrideOverlay']
        >[0])
      }
    }
  }

  /**
   * Show/hide user indicators on the MAIN (price) pane — the drawing-bar eye's
   * "indicators" / "both" modes and the `s` hotkey. Deliberately scoped:
   *  - Sub-pane studies (RSI/MACD/VOL in their own panes) are left alone; the
   *    toggle declutters the PRICE view, and hiding them would leave empty panes.
   *  - System chrome (session highlight / breaks / previous close) is never a
   *    user study, so it's skipped even though it lives on the candle pane.
   * Exposed so the scanner can broadcast across cells.
   */
  /**
   * The indicator instance names the current timeframe rule hides. The global
   * show/hide toggle keeps these hidden even when SHOWING, so pressing "S" / the
   * eye never overrides an indicator's per-timeframe visibility setting.
   */
  setTimeframeSuppressed(names: Iterable<string>): void {
    this.timeframeSuppressed = new Set(names)
  }

  /**
   * EdgeChartApi: a grid's shared eye was cleared from another cell. EdgeChart
   * owns the legend rows, so it does the conversion on this event.
   */
  releaseIndicatorHide(): void {
    if (!this.indicatorsHidden) return
    this.eventBus.emit('indicators:releaseRequested', undefined)
  }

  /** Whether the drawing-bar eye / "S" toggle currently hides price-pane indicators. */
  areIndicatorsHidden(): boolean {
    return this.indicatorsHidden
  }

  setIndicatorsVisible(visible: boolean): void {
    this.indicatorsHidden = !visible
    // EdgeChart listens: a grid broadcasts this straight to the manager, so the
    // chart would otherwise never learn its legend rows are hidden.
    this.eventBus.emit('indicators:hidden', !visible)
    const byPane = this.chart.getIndicatorByPaneId() as
      | Map<string, Map<string, Indicator>>
      | null
      | undefined
    if (!byPane || typeof byPane.forEach !== 'function') return
    byPane.forEach((paneMap, paneId) => {
      if (paneId !== CANDLE_PANE_ID) return
      paneMap.forEach((_ind, name) => {
        if (SYSTEM_INDICATOR_NAMES.has(name)) return
        // Showing must NOT reveal an indicator the timeframe rule hides on the
        // current period — the global toggle is orthogonal to that setting.
        const eff = visible && !this.timeframeSuppressed.has(name)
        this.chart.overrideIndicator(
          { name, visible: eff } as Parameters<Chart['overrideIndicator']>[0],
          paneId,
        )
      })
    })
  }

  /**
   * Pull the CROSSHAIR onto the value the magnet will snap a drawing point to.
   *
   * klinecharts snaps only when it resolves a drawing point
   * (`_coordinateToPoint`, called from four sites — all drawing or dragging).
   * The crosshair itself is never touched, so with the magnet on the pointer
   * slid freely and only the finished point jumped onto the bar. The one thing
   * the user watches gave no feedback, which is why the magnet read as broken.
   *
   * Scoped to WHILE A TOOL IS ARMED. Snapping the crosshair during ordinary
   * chart reading would fight the cursor for no benefit, and would corrupt the
   * OHLC readout, which follows the crosshair.
   *
   * `notExecuteAction` suppresses the change event so re-setting the crosshair
   * here cannot re-enter this handler.
   */
  private applyMagnetToCrosshair(raw: Crosshair): void {
    if (this.overlayMode === 'normal' || this.pendingDrawId === null) return
    const bar = raw.kLineData
    if (!bar || raw.y == null || !raw.paneId) return

    const point = this.chart.convertFromPixel([{ x: raw.x ?? 0, y: raw.y }], { paneId: raw.paneId })
    const value = (Array.isArray(point) ? point[0] : point)?.value
    if (value == null) return

    // klinecharts' `modeSensitivity` is in PIXELS; snapValueToBar works in
    // price. Convert by measuring how much price one band spans at this zoom —
    // it is not constant, so it has to be derived per call.
    const edge = this.chart.convertFromPixel(
      [{ x: raw.x ?? 0, y: raw.y - MAGNET_SENSITIVITY_PX }],
      {
        paneId: raw.paneId,
      },
    )
    const edgeValue = (Array.isArray(edge) ? edge[0] : edge)?.value
    if (edgeValue == null) return
    const sensitivity = Math.abs(edgeValue - value)

    const snapped = snapValueToBar(value, bar, this.overlayMode, sensitivity)
    if (snapped === value) return

    const coord = this.chart.convertToPixel([{ value: snapped }], { paneId: raw.paneId })
    const y = (Array.isArray(coord) ? coord[0] : coord)?.y
    if (y == null || Math.abs(y - raw.y) < 0.5) return

    const store = (
      this.chart as unknown as {
        getChartStore?: () => {
          getTooltipStore?: () => {
            setCrosshair?: (c: unknown, o?: { notExecuteAction?: boolean }) => void
          }
        }
      }
    )
      .getChartStore?.()
      ?.getTooltipStore?.()
    store?.setCrosshair?.({ ...raw, y }, { notExecuteAction: true })
  }

  /**
   * Magnet: snap drawing points to the nearest OHLC value of the bar under the
   * cursor.
   *
   * klinecharts does the snapping itself, inside `_coordinateToPoint`
   * (dist/index.esm.js:8647) — it walks the hovered bar's high/low/open/close
   * and pulls the point to the closest one. But it only does so when the
   * OVERLAY'S OWN `mode` says to, and mode is per-instance. So turning the
   * magnet on has to reach every overlay already on the chart AND every one
   * created afterwards; setting a component-level flag (which is all this used
   * to do) changed nothing at all.
   *
   * `weak` only snaps within `modeSensitivity` (8px) of a value, so the cursor
   * stays free elsewhere; `strong` always snaps.
   */
  setOverlayMode(mode: OverlayModeName): void {
    this.overlayMode = mode
    // No id → klinecharts applies this to every instance, including the one
    // currently being drawn.
    this.chart.overrideOverlay({
      mode: this.klineOverlayMode(),
    } as Parameters<Chart['overrideOverlay']>[0])
  }

  /**
   * Current magnet mode for `createOverlay` / `overrideOverlay` payloads.
   *
   * Deliberately the STRING, not klinecharts' `OverlayMode` enum. The enum's
   * members are exactly these strings (dist/index.esm.js:1220-1222) and
   * klinecharts compares them by value — but the enum is missing entirely from
   * the package's CJS build (`main`), so importing it resolves to `undefined`
   * anywhere the ESM build isn't picked (Vitest, SSR, any CJS consumer).
   * Passing the literal is equivalent and can't be undefined.
   */
  private klineOverlayMode(): OverlayModeName {
    return this.overlayMode
  }

  /**
   * Lock / unlock EVERY overlay on this chart.
   *
   * klinecharts' `overrideOverlay` applies to all overlays when handed no id —
   * something the per-id `overrideOverlay` below cannot express, which is why
   * the drawing bar's "Lock All" needs its own entry point rather than looping
   * over ids it would have to enumerate first.
   */
  setOverlaysLocked(locked: boolean): void {
    this.chart.overrideOverlay({ lock: locked } as Parameters<Chart['overrideOverlay']>[0])
  }

  overrideOverlay(id: string, props: { lock?: boolean; visible?: boolean; mode?: string }): void {
    const override: Record<string, unknown> = { id }
    if (props.lock !== undefined) override.lock = props.lock
    if (props.visible !== undefined) override.visible = props.visible
    if (props.mode !== undefined) {
      // Same reasoning as `klineOverlayMode`: literals, not the enum, which is
      // absent from klinecharts' CJS build and would be `undefined` there.
      override.mode =
        props.mode === 'strong_magnet'
          ? 'strong_magnet'
          : props.mode === 'weak_magnet'
            ? 'weak_magnet'
            : 'normal'
    }
    this.chart.overrideOverlay(override as Parameters<Chart['overrideOverlay']>[0])
    // Propagate lock/visibility to the sub-pane span segments.
    this.scheduleVerticalLineSpanSync()
    // A hidden line must stop sizing the position readout (and vice versa).
    this.schedulePositionSizeLevelSync()
  }

  /**
   * Map our overlay-agnostic `OverlayStyleOverride` onto klinecharts'
   * `{ line, point }` shape. Only defined keys are emitted so unspecified
   * fields fall through to the overlay's defaults; `dashedValue` is left to
   * the klinecharts default (a solid line ignores it).
   */
  private overlayStyleToKline(
    o: OverlayStyleOverride,
    /** The overlay's template name. Pass it when restyling a LIVE overlay so a
     *  shape's turned-off fill is cleared (see the fill block below). */
    name?: string,
  ): {
    line: { color?: string; size?: number; style?: 'solid' | 'dashed' }
    point: {
      color?: string
      borderColor?: string
      borderSize?: number
      radius?: number
      activeColor?: string
      activeBorderColor?: string
      activeBorderSize?: number
      activeRadius?: number
    }
    rect?: OverlayFillStyle
    polygon?: OverlayFillStyle
    circle?: OverlayFillStyle
  } {
    const line: { color?: string; size?: number; style?: 'solid' | 'dashed' } = {}
    // Opacity is the DRAWING's opacity, not just its fill's. Applying it only
    // to the fill made the control a no-op on every line-like tool — which is
    // most of the catalog. Alpha the stroke too.
    if (o.color !== undefined) {
      line.color = o.fillOpacity !== undefined ? withAlphaOrKeep(o.color, o.fillOpacity) : o.color
    }
    if (o.size !== undefined) line.size = o.size
    if (o.style !== undefined) line.style = o.style
    // Grab handle: a hollow donut — the chart background fills the centre (so it
    // masks the line and reads as a distinct handle rather than blending in),
    // ringed in the line's own colour. Larger + thicker on hover/drag for clear
    // grab feedback. Shared by EVERY drawing tool via the single style funnel.
    const point: {
      color?: string
      borderColor?: string
      borderSize?: number
      radius?: number
      activeColor?: string
      activeBorderColor?: string
      activeBorderSize?: number
      activeRadius?: number
    } = {}
    if (o.color !== undefined) {
      point.color = this.overlayHandleFill
      point.borderColor = o.color
      point.borderSize = 1.5
      point.radius = 3.75
      point.activeColor = this.overlayHandleFill
      point.activeBorderColor = o.color
      point.activeBorderSize = 1.875
      point.activeRadius = 4.5
    }
    // Closed-shape fill. klinecharts keys fill off three separate OverlayStyle
    // entries (`rect`, `polygon`, `circle`) depending on which figure type the
    // template emits, so one user-facing "fill" must populate all three or a
    // tool's figures paint inconsistently. Omitted entirely when unset so
    // line-like tools and legacy drawings are byte-identical to before.
    if (o.fillColor === undefined) {
      // Except on a live shape: klinecharts MERGES style updates, so omitting
      // the keys would leave a turned-off Background painted until reload.
      // Overwrite with an empty fill, which the shape builders draw as outline.
      if (name !== undefined && FILLABLE_SHAPES.has(name)) {
        const none = { color: '' }
        return { line, point, rect: none, polygon: none, circle: none }
      }
      return { line, point }
    }
    const fill = {
      style: 'stroke_fill',
      // The Background picker's own opacity wins; legacy and AI-drawn shapes
      // (no fillColorOpacity) keep deriving it from the drawing's opacity.
      color: withAlphaOrKeep(
        o.fillColor,
        o.fillColorOpacity ?? o.fillOpacity ?? DEFAULT_FILL_OPACITY,
      ),
      // Border matches the (already alpha-adjusted) stroke, so a shape's
      // outline and its line-tool siblings look identical at the same opacity.
      ...(line.color !== undefined ? { borderColor: line.color } : {}),
    }
    return { line, point, rect: fill, polygon: fill, circle: fill }
  }

  /** Keep the drawing grab-handle hollow fill in sync with the chart's canvas
   *  background (so handles read as a clean donut on any theme / custom bg). */
  setOverlayHandleFill(background: string): void {
    if (background) this.overlayHandleFill = background
  }

  /**
   * Deselect any selected overlay by clearing klinecharts' click-instance info.
   * Grab handles now render only for the selected overlay, so this hides them;
   * it also fires the overlay's `onDeselected` (syncing `selectedOverlayId`).
   * No-op when nothing is selected. Used to drop selection on a click outside
   * the chart — klinecharts itself only reacts to clicks on its own canvas.
   */
  private deselectOverlay(): void {
    const store = (
      this.chart as unknown as {
        getChartStore?: () => {
          getOverlayStore?: () => {
            getClickInstanceInfo: () => { instance: { id: string } | null }
            setClickInstanceInfo: (info: Record<string, unknown>) => void
          }
        }
      }
    )
      .getChartStore?.()
      ?.getOverlayStore?.()
    if (!store || !store.getClickInstanceInfo().instance) return
    store.setClickInstanceInfo({
      paneId: '',
      instance: null,
      figureType: 0,
      figureKey: '',
      figureIndex: -1,
      attrsIndex: -1,
    })
  }

  /**
   * Apply a style override (color / width / line-style) to a live overlay and
   * publish a sync `update` carrying the style so it persists across reloads and
   * reaches peer charts. The Style-tab edit path for Phase 2's settings panel.
   */
  overrideOverlayStyle(id: string, style: OverlayStyleOverride): void {
    // MERGE, then apply the merged result. Applying the raw patch dropped every
    // field it omitted — so changing opacity alone lost the colour, and the
    // stroke came back undefined instead of re-tinted.
    const merged: OverlayStyleOverride = { ...this.overlayStyleById.get(id), ...style }
    this.overlayStyleById.set(id, merged)
    this.chart.overrideOverlay({
      id,
      styles: this.overlayStyleToKline(merged, this.overlayName(id)),
    } as Parameters<Chart['overrideOverlay']>[0])
    // Mirror the new colour/width/dash onto any sub-pane span segments.
    this.scheduleVerticalLineSpanSync()
    if (this.isSyncing || !this.overlaySyncCallback) return
    const syncId = this.localToSyncId.get(id)
    if (syncId) {
      this.overlaySyncCallback({ action: 'update', syncId, styles: merged })
    }
  }

  /**
   * Replace a live overlay's per-instance `extendData` and publish a sync
   * `update` so it persists and reaches peer charts. The edit path for state
   * only the owning overlay template understands — a Fib's per-level colour /
   * visibility map today.
   *
   * REPLACES rather than merges, unlike `overrideOverlayStyle`. Style is a flat
   * bag of independent fields where a partial patch is the natural unit;
   * extendData is an opaque blob whose shape only the template knows, so this
   * layer cannot merge it safely — clearing a level override would be
   * indistinguishable from omitting it. Callers send the full next value.
   */
  overrideOverlayExtendData(id: string, extendData: unknown): void {
    this.chart.overrideOverlay({
      id,
      extendData,
    } as Parameters<Chart['overrideOverlay']>[0])
    if (this.isSyncing || !this.overlaySyncCallback) return
    const syncId = this.localToSyncId.get(id)
    if (syncId) {
      this.overlaySyncCallback({ action: 'update', syncId, extendData })
    }
  }

  /**
   * Move a live overlay's points to new coordinate VALUES and publish a sync
   * `update`. The Coordinates-tab edit path for Phase 2's settings panel.
   */
  overrideOverlayPoints(id: string, points: Array<{ timestamp: number; value: number }>): void {
    this.chart.overrideOverlay({
      id,
      points,
    } as Parameters<Chart['overrideOverlay']>[0])
    this.scheduleVerticalLineSpanSync()
    if (this.isSyncing || !this.overlaySyncCallback) return
    const syncId = this.localToSyncId.get(id)
    if (syncId) {
      this.overlaySyncCallback({
        action: 'update',
        syncId,
        points: points.map((p) => ({ timestamp: p.timestamp, value: p.value })),
      })
    }
  }

  /* ---- Overlay sync ---- */

  onOverlaySyncEvent(callback: ((event: OverlaySyncEvent) => void) | null): void {
    this.overlaySyncCallback = callback
  }

  /**
   * Force a just-restored overlay out of klinecharts' "still drawing" state.
   *
   * klinecharts decides whether an overlay is finished PURELY from its point
   * count: `setPoints` marks it FINISHED only when
   * `points.length >= totalStep - 1` (dist/index.esm.js:1312). That rule cannot
   * hold for VARIABLE-POINT tools — `polygon` and `polyline` register
   * `totalStep: MAX_FREE_POINTS + 1` (31) because the user ends them with a
   * double-click, not by reaching a fixed count. So a saved 4-point polygon
   * restores with `currentStep = 5`, `isDrawing() === true`, and klinecharts
   * parks it as the pane's PROGRESS instance — the chart reloads armed, with
   * the polygon rubber-banding from the cursor.
   *
   * Live drawing escapes this because the double-click handler calls
   * `forceComplete()` then `progressInstanceComplete()`
   * (dist/index.esm.js:8479-8481). Completeness is therefore never encoded in
   * the persisted payload — only points are — so the restore path has to assert
   * it. This replays exactly that pair.
   *
   * It also prevents silent data loss: a pane has ONE progress slot, and
   * `addInstances` overwrites it WITHOUT pushing the previous occupant into the
   * instance list, so restoring two polygons would drop the first.
   *
   * `getChartStore`/`getOverlayStore` are internal APIs absent from klinecharts'
   * type declarations and from mocked charts in tests — guarded throughout, so a
   * version that drops them degrades to the old behaviour instead of throwing.
   */
  private completeRestoredOverlay(id: string): void {
    const overlay = this.chart.getOverlayById(id) as unknown as {
      isDrawing?: () => boolean
      forceComplete?: () => void
    } | null
    if (typeof overlay?.isDrawing !== 'function' || !overlay.isDrawing()) return
    overlay.forceComplete?.()
    // Move it from the pane's single progress slot into the normal instance
    // list; `progressInstanceComplete` is a no-op unless the instance has
    // actually stopped drawing, which `forceComplete` above guarantees.
    ;(
      this.chart as unknown as {
        getChartStore?: () => {
          getOverlayStore?: () => { progressInstanceComplete?: () => void }
        }
      }
    )
      .getChartStore?.()
      ?.getOverlayStore?.()
      ?.progressInstanceComplete?.()
  }

  applyOverlaySync(event: OverlaySyncEvent): void {
    this.isSyncing = true
    try {
      if (event.action === 'create' && event.name && event.points) {
        // Idempotent: a create for a syncId already mapped to a live overlay is
        // a no-op. Without this, a persistence/restore echo of a locally-created
        // overlay (e.g. a hotkey-placed line on a chart that started empty)
        // spawns a duplicate.
        if (this.syncToLocalId.has(event.syncId)) return
        const localId = this.chart.createOverlay({
          name: event.name,
          points: event.points,
          // Restore persisted style / visibility / lock (all optional — legacy
          // drawings omit them and load with the overlay's default styling).
          ...(event.styles ? { styles: this.overlayStyleToKline(event.styles) } : {}),
          ...(event.visible !== undefined ? { visible: event.visible } : {}),
          ...(event.lock !== undefined ? { lock: event.lock } : {}),
          ...(event.extendData !== undefined ? { extendData: event.extendData } : {}),
          ...this.overlayDrawCallbacks(),
        } as Parameters<Chart['createOverlay']>[0])
        if (localId) {
          this.localToSyncId.set(localId as string, event.syncId)
          this.syncToLocalId.set(event.syncId, localId as string)
          if (event.styles) this.overlayStyleById.set(localId as string, event.styles)
          // A persisted drawing is by definition finished; klinecharts cannot
          // infer that for variable-point tools and would restore them armed.
          this.completeRestoredOverlay(localId as string)
          if (this.overlaysHidden) {
            this.chart.overrideOverlay({ id: localId, visible: false } as Parameters<
              Chart['overrideOverlay']
            >[0])
          }
        }
      } else if (event.action === 'update') {
        const localId = this.syncToLocalId.get(event.syncId)
        // An update may carry points (move/coordinate edit) and/or style /
        // visibility / lock (style edit). Apply whichever fields are present.
        const hasFields =
          event.points !== undefined ||
          event.styles !== undefined ||
          event.visible !== undefined ||
          event.lock !== undefined ||
          // Per-instance state (a Fib's level overrides, an edited note body)
          // changes AFTER creation, so an update carrying only extendData has
          // to be actionable — it used to fall through this gate and vanish.
          event.extendData !== undefined
        if (localId && hasFields) {
          this.chart.overrideOverlay({
            id: localId,
            ...(event.points !== undefined ? { points: event.points } : {}),
            ...(event.styles
              ? { styles: this.overlayStyleToKline(event.styles, this.overlayName(localId)) }
              : {}),
            ...(event.extendData !== undefined ? { extendData: event.extendData } : {}),
            ...(event.visible !== undefined ? { visible: event.visible } : {}),
            ...(event.lock !== undefined ? { lock: event.lock } : {}),
          } as Parameters<Chart['overrideOverlay']>[0])
        }
      } else if (event.action === 'remove') {
        const localId = this.syncToLocalId.get(event.syncId)
        if (localId) {
          // An undo can take away the drawing that is selected; its floating
          // toolbar would otherwise stay up over nothing.
          if (this.selectedOverlayId === localId) {
            this.deselectOverlay()
            // klinecharts reports the deselect through onDeselected; if it did
            // not (nothing was click-selected in its store), say so ourselves.
            if (this.selectedOverlayId === localId) {
              this.selectedOverlayId = null
              this.eventBus.emit('overlay:deselected', undefined)
            }
          }
          this.chart.removeOverlay(localId)
          this.localToSyncId.delete(localId)
          this.syncToLocalId.delete(event.syncId)
          this.overlayStyleById.delete(localId)
        }
      }
    } finally {
      this.isSyncing = false
    }
    // Restored / peer-synced vertical lines need their sub-pane segments too;
    // the reconcile derives them from whatever primaries now exist.
    this.scheduleVerticalLineSpanSync()
    // Restored / peer-synced horizontal lines feed the sizing readout the same
    // way (the snapshot no-ops when POSITION_SIZE is not mounted).
    this.schedulePositionSizeLevelSync()
  }

  clearOverlaySyncMappings(): void {
    this.localToSyncId.clear()
    this.syncToLocalId.clear()
  }

  /**
   * Register a callback fired when a drawing tool stops being armed — when any
   * drawing's draw completes, or a half-drawn/one-shot overlay is removed — so
   * the toolbar can drop the active-tool highlight (the tool
   * deactivates once the drawing is placed).
   */
  setToolDeactivatedCallback(cb: (() => void) | null): void {
    this.toolDeactivatedCallback = cb
  }

  /**
   * Route a document/window listener through the shared hub instead of
   * installing it directly — one real listener per (target, type, phase)
   * app-wide, no matter how many charts are mounted. The unregister is queued
   * for destroy(), which replaces the per-listener removeEventListener calls.
   */
  private addGlobalListener<E extends Event>(
    target: 'document' | 'window',
    type: string,
    handler: (e: E) => void,
    options?: { capture?: boolean },
  ): void {
    ;(this.globalListenerUnsubs ??= []).push(hubAdd(target, type, handler, options))
  }

  /**
   * Alt(Option)-hold history mode. While Alt is held on a daily chart, the
   * candle under the cursor and the vertical crosshair both turn yellow, cueing
   * that an Alt+click drills into that day. Alt state is tracked globally so
   * "hold Alt, then move onto the chart" still arms it; the highlight only
   * applies while the pointer is over us. Alt+click emits `intradayDrill:requested`
   * — the host owns the actual jump (date-picker state + panel remount).
   */
  /**
   * Enable/disable the Alt-hold day-drill (yellow candle highlight + Alt+click
   * → `intradayDrill:requested`). The host calls this from whether it wired an
   * `onIntradayDrill` handler, so the highlight only appears where the gesture
   * actually does something. Disabling clears any active highlight immediately.
   */
  setIntradayDrillEnabled(enabled: boolean): void {
    if (this.intradayDrillEnabled === enabled) return
    this.intradayDrillEnabled = enabled
    if (!enabled) this.clearBarHighlight()
  }

  private setupAltHistoryMode(): void {
    registerBarHighlightOverlay()
    this.altKeyDownHandler = (e: KeyboardEvent) => {
      // Ctrl/Cmd+Alt is the AI companion's hold-to-talk chord, not history mode.
      if (e.key !== 'Alt' || e.ctrlKey || e.metaKey || this.altHeld) return
      this.altHeld = true
      if (this.isPointerOver) this.updateBarHighlight()
    }
    this.altKeyUpHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Alt') return
      this.altHeld = false
      this.altChordConsumed = false
      this.clearBarHighlight()
    }
    // Releasing Alt while the window is blurred (alt-tab) never fires keyup,
    // so reset on blur to avoid a stuck highlight.
    this.altBlurHandler = () => {
      this.altHeld = false
      this.altChordConsumed = false
      this.clearBarHighlight()
    }
    this.addGlobalListener('document', 'keydown', this.altKeyDownHandler)
    this.addGlobalListener('document', 'keyup', this.altKeyUpHandler)
    this.addGlobalListener('window', 'blur', this.altBlurHandler)

    this.altClickHandler = (e: MouseEvent) => {
      if (!this.intradayDrillEnabled) return
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      if (this.period.timespan !== 'day') return
      const cursor = this.lastCursor
      if (!cursor?.timestamp) return
      e.preventDefault()
      const dayTimestamp = alignTimestamp(cursor.timestamp, PERIODS['1D'])
      this.eventBus.emit('intradayDrill:requested', { dayTimestamp })
    }
    this.container.addEventListener('click', this.altClickHandler)
  }

  /**
   * Drawing chords placed at the cursor on the chart under the pointer, all
   * behind Option/Alt so bare letters stay free for ticker entry:
   *   Option+H = horizontal line, Option+V = vertical line, Option+R = ray,
   *   Option+Shift+R = horizontal ray, Option+F = Fibonacci,
   *   Option+P = position plan.
   * Option+S toggles show/hide of all indicators + drawings (host owns that
   * state). Chords match on e.code (physical key): macOS Option+letter
   * rewrites e.key to a special character, so the code is the stable signal.
   * Shift alone still arms the measure (ruler) tool (emits
   * `measure:requested`); releasing Shift before the first click disarms it,
   * so Shift cannot latch the tool on - see `cancelPendingMeasure`. A bare
   * letter typed while Shift is held is ticker entry, not measuring, and
   * disarms the pending ruler.
   * All are gated to the hovered chart and suppressed while typing in a field.
   */
  private setupDrawingHotkeys(): void {
    // ── Shift arms the measure (ruler) tool: Shift+click plants the first
    // point, then the gesture stands on its own — release Shift and place the
    // second point whenever. Releasing BEFORE that first click disarms, so
    // Shift gates activation instead of latching the tool on indefinitely. We
    // emit an event so the host (EdgeChart) arms the same overlay path the
    // drawing-bar button uses, keeping its selected-state highlight in sync.
    this.shiftMeasureHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Shift' || this.shiftHeld) return
      this.shiftHeld = true
      if (!this.isPointerOver) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return
      }
      this.eventBus.emit('measure:requested', undefined)
    }
    this.shiftMeasureUpHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Shift') return
      this.shiftHeld = false
      // Shift gates ACTIVATION only — this is a no-op once the first click has
      // planted a point. See `cancelPendingMeasure`.
      this.cancelPendingMeasure()
    }
    this.addGlobalListener('document', 'keydown', this.shiftMeasureHandler)
    this.addGlobalListener('document', 'keyup', this.shiftMeasureUpHandler)

    // A bare letter while Shift is held is shifted ticker typing (capital
    // symbols), not a measure gesture: disarm the pending ruler so it never
    // flashes mid-entry. No preventDefault: the letter still reaches the
    // ticker-entry layer.
    this.measureTypingCancelHandler = (e: KeyboardEvent) => {
      if (!this.shiftHeld || e.altKey || e.metaKey || e.ctrlKey) return
      if (!/^Key[A-Z]$/.test(e.code)) return
      this.cancelPendingMeasure()
    }
    this.addGlobalListener('document', 'keydown', this.measureTypingCancelHandler)

    this.hotkeyHandler = (e: KeyboardEvent) => {
      // Drawing chords require Option/Alt and never combine with Cmd/Ctrl.
      if (!e.altKey || e.ctrlKey || e.metaKey) return
      const code = e.code
      if (
        code !== 'KeyH' &&
        code !== 'KeyV' &&
        code !== 'KeyR' &&
        code !== 'KeyF' &&
        code !== 'KeyS' &&
        code !== 'KeyP'
      )
        return
      // Only the chart under the pointer responds, and never while typing.
      if (!this.isPointerOver) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return
      }
      // A fired chord clears the Alt-hold bar highlight for the rest of this
      // Alt hold (see altChordConsumed): the highlight is a click cue, and a
      // chord means the hold was a chord, not a drill gesture.
      const consumeAltHold = () => {
        this.altChordConsumed = true
        this.clearBarHighlight()
      }
      // Option+S toggles show/hide of ALL indicators + drawings: the host
      // owns that state (the DrawingBar eye button), so emit and let
      // EdgeChart drive its existing handler. No cursor needed (global
      // toggle, not a placement).
      if (code === 'KeyS' && !e.shiftKey) {
        e.preventDefault()
        consumeAltHold()
        this.eventBus.emit('visibility:toggleAll', undefined)
        return
      }
      // Shift distinguishes the horizontal-ray chords: Option+Shift+H is the
      // labeled binding (the shifted sibling of Option+H's horizontal line)
      // and Option+Shift+R stays as a silent alias. The Loom extension's
      // default record shortcut is Alt+Shift+R and consumes the physical
      // chord before the page sees it (verified 2026-08-30), so R cannot be
      // the labeled binding. Every other shifted chord is unbound.
      if (e.shiftKey && code !== 'KeyR' && code !== 'KeyH') return
      const cursor = this.lastCursor
      if (!cursor) return
      e.preventDefault()
      consumeAltHold()
      // Holding Shift has already armed the ruler. A shifted DRAWING chord
      // supersedes it: without this both would occupy klinecharts' single
      // progress slot and `addInstances` would silently orphan the measure.
      if (e.shiftKey) this.cancelPendingMeasure()
      if (code === 'KeyH') {
        this.placeOverlayAtCursor(
          e.shiftKey ? 'horizontalRayLine' : 'horizontalStraightLine',
          cursor,
        )
      } else if (code === 'KeyV') {
        this.placeOverlayAtCursor('verticalStraightLine', cursor)
      } else if (code === 'KeyF') {
        // Option+F: Fibonacci retracement. Multi-point like the ray: anchor
        // the first point at the cursor and let klinecharts' progressive draw
        // wait for the second click, so the levels span the swing the user
        // picks.
        this.startOverlayAtCursor('fibonacciLine', cursor)
      } else if (code === 'KeyP') {
        // Option+P: position plan. Two-click tool: the entry anchors at the
        // cursor and the next click places the stop.
        this.startOverlayAtCursor(POSITION_PLAN_NAME, cursor)
      } else if (e.shiftKey) {
        // Option+Shift+R: horizontal ray. Our `horizontalRayLine` template is
        // a genuine single-click tool (one anchor, extends right), so place it
        // directly like the H/V chords. See `registerHorizontalRayOverlay`.
        this.placeOverlayAtCursor('horizontalRayLine', cursor)
      } else {
        // Option+R: ray line is multi-point. Anchor the first point at the
        // cursor and let klinecharts' progressive draw wait for the second
        // click.
        this.startOverlayAtCursor('rayLine', cursor)
      }
    }
    this.addGlobalListener('document', 'keydown', this.hotkeyHandler)
  }

  /**
   * Escape cancels an in-progress drawing (armed tool whose points are still
   * being placed). Registered on the CAPTURE phase: cancelling must consume
   * the press before the workspace's bubble-phase Escape handlers (fullscreen
   * minimize, drill close) see it — Escape mid-draw must never also
   * unmaximize the panel. Inert when nothing is being drawn.
   */
  private setupEscapeDrawCancel(): void {
    this.escapeDrawCancelHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (!this.cancelPendingDraw()) return
      e.preventDefault()
      e.stopPropagation()
    }
    this.addGlobalListener('document', 'keydown', this.escapeDrawCancelHandler, { capture: true })
  }

  /**
   * Subscribe to the COMMITTED points of a drawing in progress on this chart.
   * Used to let a drawing started here be finished on a sibling chart showing
   * the same ticker.
   */
  setDrawProgressCallback(
    cb: ((points: Array<{ timestamp?: number; value?: number }>) => void) | null,
  ): void {
    this.drawProgressCallback = cb
  }

  /**
   * Broadcast the committed prefix of an in-progress drawing.
   *
   * klinecharts fires `onDrawing` for BOTH the rubber-band mouse move and the
   * click that commits a point (dist/index.esm.js:8435 and :8458) — with
   * identical arguments, and before `nextStep()` — so the event alone cannot
   * tell them apart. What separates them is the overlay's own step: at
   * `currentStep = N`, `points[0..N-2]` are committed and `points[N-1]` is the
   * live preview. Slicing to that prefix and skipping unchanged emissions
   * yields exactly one broadcast per click rather than one per mouse move.
   */
  private emitDrawProgress(overlay: {
    points?: Array<{ timestamp?: number; value?: number }>
  }): void {
    if (!this.drawProgressCallback) return
    const step = (overlay as { currentStep?: number }).currentStep
    if (typeof step !== 'number') return
    const committed = (overlay.points ?? [])
      .slice(0, Math.max(0, step - 1))
      .map((p) => ({ timestamp: p.timestamp, value: p.value }))
    if (committed.length === 0) return
    const key = JSON.stringify(committed)
    if (key === this.lastDrawProgress) return
    this.lastDrawProgress = key
    this.drawProgressCallback(committed)
  }

  /**
   * Adopt the committed points of a drawing being made on a SIBLING chart, so a
   * drawing started on one chart can be finished on another.
   *
   * Applies only PARTIAL point sets. A complete one would flip this overlay to
   * FINISHED inside `setPoints`, but `overrideOverlay` never calls
   * `progressInstanceComplete`, so it would be stranded in the pane's single
   * progress slot — the same trap behind restored polygons coming back armed.
   * Completion therefore stays with whichever chart receives the final click,
   * and nothing here persists: `overrideOverlay` fires no draw/move-end
   * callback, so an unfinished drawing is never saved.
   */
  applyPendingPoints(points: Array<{ timestamp?: number; value?: number }>): void {
    if (this.pendingDrawId === null || points.length === 0) return
    const overlay = this.chart.getOverlayById(this.pendingDrawId) as unknown as {
      totalStep?: number
    } | null
    const totalStep = overlay?.totalStep
    // Would complete the overlay — leave it for the click.
    if (typeof totalStep === 'number' && points.length >= totalStep - 1) return
    this.chart.overrideOverlay({
      id: this.pendingDrawId,
      points,
    } as Parameters<Chart['overrideOverlay']>[0])
  }

  /**
   * Drop a measurement that Shift armed but the user never STARTED.
   *
   * Shift gates activation only: Shift+click plants the first point, and from
   * then on the gesture is its own — release Shift and place the second point
   * whenever. Holding a modifier down across a two-click drag is awkward, and
   * nothing about the second click is ambiguous once the first has landed.
   *
   * What this does prevent is Shift LATCHING the tool on. Before this, tapping
   * Shift armed the ruler indefinitely, so the next stray click anywhere on the
   * chart silently began a measurement the user never asked for.
   *
   * Three things are therefore deliberately spared:
   *
   *  - **A measurement already in progress** (`isStart()` false — the first
   *    click has landed). This is the whole point above.
   *  - **A tool armed from the drawing bar.** Shift is also the modifier for
   *    the horizontal-ray hotkey, so an incidental press must not cancel an
   *    unrelated overlay out from under the user.
   *  - **A COMPLETED measurement.** It stays readable until the next click
   *    tears it down (`armMeasureTeardown`) — reading it is the point.
   */
  private cancelPendingMeasure(): boolean {
    if (this.pendingDrawId === null) return false
    const overlay = this.chart.getOverlayById(this.pendingDrawId) as unknown as {
      name?: string
      isStart?: () => boolean
    } | null
    if (overlay?.name !== 'measure') return false
    // Already under way — the first point is planted, so Shift has done its job.
    // Absent `isStart` (mocked charts, a future klinecharts) means we cannot
    // tell, so leave the drawing alone rather than destroy work in progress.
    if (typeof overlay.isStart !== 'function' || !overlay.isStart()) return false
    return this.cancelPendingDraw()
  }

  /**
   * Show the Pre/Post price mark at the session's extended-hours price, or
   * clear it with null. Drawn only on a live (non-anchored) daily chart; a
   * live premarket tick from the chart's own stream takes precedence.
   */
  setExtendedSessionPrice(mark: { label: ExtendedSessionLabel; price: number } | null): void {
    this.extendedHostMark = mark
    this.syncExtendedSessionMark()
  }

  /** Settings eye for the Pre/Post mark; hides stream-fed Pre prices too. */
  setExtendedSessionMarkVisible(visible: boolean): void {
    this.extendedMarkVisible = visible
    this.syncExtendedSessionMark()
  }

  private setStreamPrePrice(price: number | null): void {
    this.streamPrePrice = price
    this.syncExtendedSessionMark()
  }

  private syncExtendedSessionMark(): void {
    const dailyLive =
      this.extendedMarkVisible &&
      this.period?.timespan === 'day' &&
      this.period.multiplier === 1 &&
      this.dataLayer?.getAnchorTimestamp() == null
    const mark = !dailyLive
      ? null
      : this.streamPrePrice != null
        ? { label: 'Pre' as const, price: this.streamPrePrice }
        : this.extendedHostMark
    if (!mark || !Number.isFinite(mark.price)) {
      if (this.extendedMarkId) {
        this.chart.removeOverlay(this.extendedMarkId)
        this.extendedMarkId = null
      }
      return
    }
    const points = [{ value: mark.price }]
    // The last close rides along so the pill can stack clear of the native
    // last-price pill instead of covering it.
    const data = this.chart.getDataList?.() as Array<{ close?: number }> | undefined
    const lastClose = data?.[data.length - 1]?.close
    const extendData = {
      label: mark.label,
      color: EXTENDED_SESSION_COLORS[mark.label],
      ...(typeof lastClose === 'number' ? { lastPrice: lastClose } : {}),
    }
    if (this.extendedMarkId) {
      this.chart.overrideOverlay({ id: this.extendedMarkId, points, extendData } as Parameters<
        Chart['overrideOverlay']
      >[0])
      return
    }
    this.extendedMarkId =
      (this.chart.createOverlay({
        name: EXTENDED_SESSION_PRICE_MARK_NAME,
        points,
        extendData,
        lock: true,
      } as Parameters<Chart['createOverlay']>[0]) as string | null) ?? null
  }

  /** A drawing tool is armed or a drawing is half placed: the "+" by the crosshair label hides. */
  isDrawingPending(): boolean {
    return this.pendingDrawId != null
  }

  /** Cancel the drawing currently being placed, if any; true when one was. */
  cancelPendingDraw(): boolean {
    if (this.pendingDrawId === null) return false
    const id = this.pendingDrawId
    this.pendingDrawId = null
    this.lastDrawProgress = null
    this.chart.removeOverlay(id)
    // The first click mid-draw selects the in-progress overlay, which opens the
    // host's quick-edit bar. klinecharts fires no onDeselected when an overlay
    // is removed, so say so here or the bar outlives the cancelled drawing.
    if (id === this.selectedOverlayId) {
      this.selectedOverlayId = null
      this.eventBus.emit('overlay:deselected', undefined)
    }
    // A cancelled loop-range selection disarms the replay strip's loop button.
    if (id === this.replayLoopOverlayId) {
      this.replayLoopOverlayId = null
      this.eventBus.emit('replay:loop-ended', undefined)
    }
    // The armed tool goes with it — drop the DrawingBar highlight.
    this.toolDeactivatedCallback?.()
    return true
  }

  private handleOverlayDrawEnd(event: OverlayEvent): void {
    const { id, name, points } = event.overlay
    // Non-geometric per-instance state the template may have stashed while
    // drawing (e.g. a Text tool's string). Opaque here — just carried.
    const extendData = (event.overlay as { extendData?: unknown }).extendData
    // Completed — no longer cancellable via Escape.
    if (this.pendingDrawId === id) this.pendingDrawId = null
    this.lastDrawProgress = null
    // Any completed drawing deactivates its tool, so the toolbar button stops
    // reading as armed/selected.
    this.toolDeactivatedCallback?.()
    // The measure/ruler tool is ephemeral: never persisted or synced, and torn
    // down on the user's next click or Escape.
    if (name === 'measure') {
      this.armMeasureTeardown(id)
      return
    }
    this.persistOverlayCreate(id, name, points, extendData)
    if (name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
    if (MANUAL_LEVEL_OVERLAY_NAMES.has(name)) this.schedulePositionSizeLevelSync()
  }

  /**
   * Arm a one-shot teardown for the just-finished measure overlay: the next
   * pointer-press anywhere (or Escape) removes it. The press that completed the
   * draw has already fired by the time `onDrawEnd` runs, so the listener only
   * catches the NEXT interaction. Replaces any prior armed teardown so a fresh
   * measurement supersedes a stale one.
   */
  private armMeasureTeardown(id: string): void {
    this.measureTeardownCleanup?.()
    const onDown = () => remove()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') remove()
    }
    const cleanup = () => {
      this.measureTeardownCleanup = null
      this.container.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
    const remove = () => {
      cleanup()
      this.chart.removeOverlay(id)
    }
    this.measureTeardownCleanup = cleanup
    this.container.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey, true)
  }

  /**
   * Whether klinecharts still considers this overlay to be mid-draw.
   *
   * `isDrawing` is absent from klinecharts' type declarations and from the
   * mocked charts in tests, so an unavailable check reports "not drawing" —
   * failing OPEN, since treating every overlay as unfinished would silently
   * stop all persistence.
   */
  private isOverlayDrawing(id: string): boolean {
    const overlay = this.chart.getOverlayById(id) as unknown as {
      isDrawing?: () => boolean
    } | null
    return typeof overlay?.isDrawing === 'function' ? overlay.isDrawing() : false
  }

  /** A live overlay's template name, or undefined when it is gone. */
  private overlayName(id: string): string | undefined {
    return (this.chart.getOverlayById(id) as { name?: string } | null)?.name
  }

  /**
   * Register a freshly-created overlay for persistence + cross-chart sync.
   * Idempotent: skips an overlay already mapped, so a hotkey-placed overlay
   * that also happens to fire `onDrawEnd` isn't published twice.
   */
  private persistOverlayCreate(
    id: string,
    name: string,
    points: Array<{ timestamp?: number; value?: number }>,
    extendData?: unknown,
  ): void {
    if (this.isSyncing || !this.overlaySyncCallback) return
    if (this.localToSyncId.has(id)) return
    // NEVER save a half-drawn overlay. klinecharts keeps an overlay in its
    // drawing state until `points.length >= totalStep - 1`, so a caller that
    // places fewer points than its tool needs would otherwise persist an
    // incomplete record — and because this method is idempotent per id, the
    // real `onDrawEnd` persist that follows would be DROPPED, freezing the
    // half-finished version as the saved one. Skipping here defers persistence
    // to `onDrawEnd`, which is exactly when the drawing is actually complete.
    if (this.isOverlayDrawing(id)) return
    const syncId = generateSyncId()
    this.localToSyncId.set(id, syncId)
    this.syncToLocalId.set(syncId, id)
    // Carry the per-tool default style (if the create path armed one) so the
    // drawing persists + broadcasts styled, matching what's painted on screen.
    const styles = this.pendingCreateStyles.get(id)
    this.pendingCreateStyles.delete(id)
    if (styles) this.overlayStyleById.set(id, styles)
    this.overlaySyncCallback({
      action: 'create',
      syncId,
      name,
      points: points.map((p) => ({ timestamp: p.timestamp, value: p.value })),
      ...(styles ? { styles } : {}),
      ...(extendData !== undefined ? { extendData } : {}),
    })
  }

  /**
   * A click on the position plan's "+ Add entry" pill appends a new entry
   * line (midpoint seed, weights reset to an equal split — appendPlanEntry)
   * and publishes both the points and the config so the change persists and
   * reaches peer charts.
   */
  private handlePlanAddEntryClick(event: OverlayEvent): void {
    const { overlay } = event
    if (overlay.name !== POSITION_PLAN_NAME) return
    const figureKey = (event as { figureKey?: string }).figureKey
    const points = ((overlay.points ?? []) as Array<{ timestamp?: number; value?: number }>).map(
      (p) => ({ timestamp: p.timestamp ?? 0, value: p.value ?? 0 }),
    )
    const config = parsePlanConfig((overlay as { extendData?: unknown }).extendData)

    if (figureKey === 'plan_add_entry') {
      const next = appendPlanEntry(points, config)
      if (!next) return
      this.overrideOverlayPoints(overlay.id, next.points)
      this.overrideOverlayExtendData(overlay.id, next.config)
      // The pill click just SELECTED the plan (any figure press does), which
      // would arm whole-plan drag — but the user's next move is grabbing the
      // fresh line to place it. Deselect (deferred so klinecharts finishes its
      // own click processing first) so that drag moves only the new line.
      queueMicrotask(() => this.deselectOverlay())
      return
    }

    // A click on an entry's ✕ removes that leg. Two key schemes: unselected
    // pills ride the anchor prefix (so drags move their line), selected ones
    // use the plain key (whole-plan drag).
    const removeMatch = figureKey?.match(/(?:^plan_remove_|__remove_)(\d+)$/)
    if (removeMatch) {
      this.removePlanEntry(overlay.id, Number(removeMatch[1]))
      queueMicrotask(() => this.deselectOverlay())
      return
    }

    // A click on an entry's label pill opens the on-chart % editor — the
    // React layer renders the menu; the payload carries the full config so
    // the selection can write back a complete extendData.
    const weightMatch = figureKey?.match(/(?:^plan_weight_|__weight_)(\d+)$/)
    if (weightMatch) {
      const entryIndex = Number(weightMatch[1])
      const indices = entryPointIndices(points.length)
      const entryPrice = points[indices[entryIndex]]?.value
      if (!isFinite(entryPrice)) return
      // Display numbering rides along so the menu can enforce the cascade's
      // remaining budget without re-deriving plan geometry.
      const displayNumbers = entryDisplayNumbers(
        indices.map((i) => points[i]?.value ?? NaN),
        points[1]?.value ?? NaN,
      )
      this.eventBus.emit('plan:weightEdit', {
        overlayId: overlay.id,
        entryIndex,
        entryCount: indices.length,
        config,
        entryPrice,
        displayNumbers,
        // Click x (widget coords) so the menu can anchor under the pill —
        // labels ride the plan's left edge now, not the pane's left.
        x: (event as { x?: number }).x,
      })
      // Same as Add entry: a % pill click must not leave the plan armed for
      // whole-plan drag.
      queueMicrotask(() => this.deselectOverlay())
    }
  }

  /**
   * Remove one entry leg from a position plan (invoked by the % menu's
   * "Remove entry"). Reads the LIVE overlay so a drag that happened after the
   * menu opened is not clobbered; publishes both points and config.
   */
  removePlanEntry(overlayId: string, entryIndex: number): void {
    const overlay = this.chart.getOverlayById(overlayId) as {
      id: string
      name: string
      points?: Array<{ timestamp?: number; value?: number }>
      extendData?: unknown
    } | null
    if (!overlay || overlay.name !== POSITION_PLAN_NAME) return
    const points = ((overlay.points ?? []) as Array<{ timestamp?: number; value?: number }>).map(
      (p) => ({ timestamp: p.timestamp ?? 0, value: p.value ?? 0 }),
    )
    const next = removePlanEntry(points, parsePlanConfig(overlay.extendData), entryIndex)
    if (!next) return
    this.overrideOverlayPoints(overlayId, next.points)
    this.overrideOverlayExtendData(overlayId, next.config)
  }

  private handleOverlayMoveEnd(event: OverlayEvent): void {
    // Snap the sub-pane segments to the line's released position. Runs even
    // when sync is off (it's local rendering, not persistence).
    if (event.overlay.name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
    if (MANUAL_LEVEL_OVERLAY_NAMES.has(event.overlay.name)) this.schedulePositionSizeLevelSync()
    if (this.isSyncing || !this.overlaySyncCallback) return
    const { id, points } = event.overlay
    const syncId = this.localToSyncId.get(id)
    if (syncId) {
      this.overlaySyncCallback({
        action: 'update',
        syncId,
        points: points.map((p) => ({ timestamp: p.timestamp, value: p.value })),
      })
    }
  }

  private handleOverlayRemoved(event: OverlayEvent): void {
    // Removed while still being drawn (Escape cancel or any external removal)
    // — nothing pending anymore.
    if (this.pendingDrawId === event.overlay.id) this.pendingDrawId = null
    // A measure overlay being removed (ephemeral teardown OR a cancelled
    // half-drawn measurement) also means the tool is no longer armed.
    if (event.overlay.name === 'measure') this.toolDeactivatedCallback?.()
    // A removed vertical line must take its sub-pane segments with it. The
    // deferred reconcile sees the store AFTER klinecharts drops the primary,
    // so the orphaned segments fall out of the desired set and are cleaned up.
    if (event.overlay.name === VERTICAL_LINE_NAME) this.scheduleVerticalLineSpanSync()
    // A deleted horizontal line/ray must fall out of the sizing readout —
    // deferred for the same store-settling reason.
    if (MANUAL_LEVEL_OVERLAY_NAMES.has(event.overlay.name)) this.schedulePositionSizeLevelSync()
    if (this.isSyncing || !this.overlaySyncCallback) return
    const { id } = event.overlay
    const syncId = this.localToSyncId.get(id)
    if (syncId) {
      this.localToSyncId.delete(id)
      this.syncToLocalId.delete(syncId)
      this.overlaySyncCallback({ action: 'remove', syncId })
    }
  }

  /* ---- Vertical-line cross-pane span ---- */

  /**
   * Queue a span reconcile for the end of the current microtask (coalesced).
   * Callers just signal "vertical-line state may have changed" — draw-end,
   * move, style/visibility edits, removal, restore, and pane add/remove — and
   * let `syncVerticalLineSpans` derive the correct set of segments from scratch.
   */
  private scheduleVerticalLineSpanSync(): void {
    if (this.verticalSpanSyncScheduled) return
    this.verticalSpanSyncScheduled = true
    queueMicrotask(() => {
      this.verticalSpanSyncScheduled = false
      this.syncVerticalLineSpans()
    })
  }

  /* ---- Position-size manual levels ---- */

  /**
   * Queue a manual-level snapshot for the POSITION_SIZE indicator (coalesced,
   * same microtask deferral as the vertical-line span sync). Callers just
   * signal "horizontal line/ray state may have changed" — draw-end, drag,
   * removal, visibility, restore — and the snapshot re-derives from scratch.
   */
  private schedulePositionSizeLevelSync(): void {
    if (this.positionSizeSyncScheduled) return
    this.positionSizeSyncScheduled = true
    queueMicrotask(() => {
      this.positionSizeSyncScheduled = false
      this.syncPositionSizeLevels()
    })
  }

  /**
   * Stable callback the sizing indicator's calc invokes with each fresh
   * readout (rides in via extendData). Relayed onto the event bus so the
   * React pill (PositionSizeReadout) can render it — the indicator draws
   * nothing on canvas.
   */
  private readonly positionSizeReadoutRelay = (payload: PositionSizeReadoutPayload): void => {
    this.eventBus.emit('positionSize:readout', payload)
  }

  /**
   * Push the user's horizontal lines / rays into the POSITION_SIZE indicator's
   * extendData so its manual mode can auto-bind to them (indicators cannot see
   * the overlay store themselves). Setting extendData flips klinecharts'
   * calcFlag, so the readout re-derives immediately. No-op when the indicator
   * is not mounted. The readout relay rides along on every push so the calc
   * can always reach the React pill.
   */
  private syncPositionSizeLevels(): void {
    if (!this.getIndicatorState(CANDLE_PANE_ID, POSITION_SIZE_NAME)) return
    const extendData: PositionSizeExtendData = {
      manualLevels: manualLevelsFromOverlays(this.liveOverlayInstances()),
      onReadout: this.positionSizeReadoutRelay,
    }
    this.updateIndicator(CANDLE_PANE_ID, POSITION_SIZE_NAME, { extendData })
  }

  /**
   * Read klinecharts' live overlay instances across every pane. Internal API
   * (absent from the type declarations and from mocked charts in tests), so a
   * version that drops it — or a test double — degrades to "no overlays" and
   * the reconcile becomes a no-op rather than throwing.
   */
  private liveOverlayInstances(): Array<{
    id: string
    name: string
    paneId: string
    groupId: string
    visible?: boolean
    points?: Array<{ timestamp?: number; value?: number }>
  }> {
    const store = (
      this.chart as unknown as {
        getChartStore?: () => {
          getOverlayStore?: () => {
            getInstances?: () => Array<{
              id: string
              name: string
              paneId: string
              groupId: string
              visible?: boolean
              points?: Array<{ timestamp?: number; value?: number }>
            }>
          }
        }
      }
    )
      .getChartStore?.()
      ?.getOverlayStore?.()
    return store?.getInstances?.() ?? []
  }

  /**
   * Make every vertical line span ALL panes. klinecharts clips an overlay to
   * its own pane's canvas, so a single overlay cannot cross panes; instead each
   * primary `verticalStraightLine` (candle pane) gets a mirror `verticalLineSpan`
   * segment on every sub-pane, drawing that pane's own full-height slice — the
   * same one-segment-per-pane trick the built-in crosshair uses.
   *
   * Fully idempotent and derived from current state: it diffs the desired set
   * (live primaries × current sub-panes) against the live segments and
   * creates/updates/removes to converge. That means one entry point covers
   * create, drag, style, visibility, removal, reload, and indicator add/remove
   * without per-event bookkeeping. Segments are non-persisted, locked, and
   * event-ignoring, so they never enter selection, persistence, or the
   * quick-edit bar — the primary owns all of that.
   */
  private syncVerticalLineSpans(): void {
    const all = this.liveOverlayInstances()
    if (all.length === 0 && this.subPaneIds.size === 0) return
    const primaries = all.filter((o) => o.name === VERTICAL_LINE_NAME)
    const segments = all.filter((o) => o.name === VERTICAL_LINE_SPAN_NAME)
    const subPaneIds = new Set(this.subPaneIds.values())

    // Index existing segments by their primary (groupId) then pane.
    const byPrimary = new Map<string, Map<string, string>>()
    for (const seg of segments) {
      let panes = byPrimary.get(seg.groupId)
      if (!panes) {
        panes = new Map()
        byPrimary.set(seg.groupId, panes)
      }
      panes.set(seg.paneId, seg.id)
    }

    const keep = new Set<string>()
    for (const primary of primaries) {
      const timestamp = primary.points?.[0]?.timestamp
      if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) continue
      const style = this.overlayStyleById.get(primary.id)
      const styleFields = style ? { styles: this.overlayStyleToKline(style) } : {}
      const visible = primary.visible !== false
      const existingPanes = byPrimary.get(primary.id)
      for (const paneId of subPaneIds) {
        const existingId = existingPanes?.get(paneId)
        if (existingId) {
          keep.add(existingId)
          this.chart.overrideOverlay({
            id: existingId,
            points: [{ timestamp, value: 0 }],
            visible,
            lock: true,
            ...styleFields,
          } as Parameters<Chart['overrideOverlay']>[0])
        } else {
          const newId = this.chart.createOverlay(
            {
              name: VERTICAL_LINE_SPAN_NAME,
              // Shared groupId ties the segment back to its primary so this
              // reconcile can re-pair them after a reload with no local state.
              groupId: primary.id,
              points: [{ timestamp, value: 0 }],
              lock: true,
              visible,
              ...styleFields,
              // Deliberately NO overlayDrawCallbacks(): segments must stay out
              // of persistence, selection, and the quick-edit bar.
            } as Parameters<Chart['createOverlay']>[0],
            paneId,
          )
          if (typeof newId === 'string') keep.add(newId)
        }
      }
    }

    // Drop orphans: primary gone, pane gone, or a stale duplicate.
    for (const seg of segments) {
      if (!keep.has(seg.id)) this.chart.removeOverlay(seg.id)
    }
  }

  /* ---- Crosshair sync ---- */

  /**
   * rAF-throttled like `subscribeViewport`: OnCrosshairChange fires per raw
   * mousemove (60–120 Hz) and each publish makes every synced peer repaint
   * its overlay canvas, so coalescing to one publish per frame bounds the
   * fan-out cost in large sync groups. `null` (cursor left the chart) is a
   * meaningful payload, hence the explicit `hasPending` flag.
   */
  subscribeCrosshair(callback: (data: CrosshairSyncData | null) => void): void {
    let rafId: number | null = null
    let pending: CrosshairSyncData | null = null
    let hasPending = false

    const handler = (raw: Crosshair) => {
      // Empty payload = cursor left the chart (kline fires OnCrosshairChange
      // with no position fields). Signal the absence with null so peer charts
      // in a sync group can clear their broadcast crosshair line instead of
      // keeping the last hovered position painted.
      if (raw.dataIndex == null || raw.x == null || raw.y == null || !raw.paneId) {
        pending = null
        hasPending = true
      } else {
        // Convert pixel y to price value in data space. Kept here (not in the
        // rAF) so the deferred callback never touches a possibly-disposed chart.
        const point = this.chart.convertFromPixel([{ x: raw.x, y: raw.y }], { paneId: raw.paneId })
        const pt = Array.isArray(point) ? point[0] : point
        if (pt?.dataIndex == null || pt?.value == null) return
        pending = {
          dataIndex: raw.dataIndex,
          timestamp: raw.kLineData?.timestamp ?? 0,
          value: pt.value,
          paneId: raw.paneId,
        }
        hasPending = true
      }
      if (rafId != null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        if (hasPending) {
          const payload = pending
          hasPending = false
          pending = null
          callback(payload)
        }
      })
    }
    // Remember the wrapped handler so we can unsubscribe this exact callback later.
    this.crosshairHandlers.set(callback, handler)
    this.chart.subscribeAction(ActionType.OnCrosshairChange, handler)
  }

  unsubscribeCrosshair(callback?: (data: CrosshairSyncData | null) => void): void {
    if (callback) {
      const raw = this.crosshairHandlers.get(callback)
      this.chart.unsubscribeAction(ActionType.OnCrosshairChange, raw)
      this.crosshairHandlers.delete(callback)
    } else {
      this.chart.unsubscribeAction(ActionType.OnCrosshairChange)
    }
  }

  /**
   * Remove any lingering crosshair line from this chart's canvas.
   *
   * kline has no public API for "remove crosshair". The obvious path —
   * `executeAction(OnCrosshairChange, {})` — pins the line to the last bar:
   * the action handler fills in a default `paneId='candle'`, and the line-
   * draw guard (`isString(crosshair.paneId)`) then permits a render at
   * `realX = dataIndexToCoordinate(dataList.length - 1)`.
   *
   * The internal `tooltipStore.setCrosshair()` (no args) is the same call
   * kline makes on its own `mouseLeaveEvent`. It clears `paneId` to undefined
   * so the line-draw guard fails and the overlay re-render skips drawing.
   * That's the only API path that actually removes the line, so we reach
   * through the internal store rather than firing a misbehaving action.
   */
  clearCrosshair(): void {
    const internal = this.chart as unknown as {
      getChartStore?: () => {
        getTooltipStore: () => { setCrosshair: (data?: unknown, opts?: unknown) => void }
      }
    }
    internal.getChartStore?.().getTooltipStore().setCrosshair()
    // Remote crosshair gone → readouts (OHLC header / indicator legend) snap
    // back to the latest bar.
    this.eventBus.emit('crosshair:remote', null)
  }

  /* ---- Viewport (date-range) sync ---- */

  /**
   * Subscribe to visible-range changes (scroll + zoom). The kline action
   * gives us data-index range; we convert to timestamps so peers with
   * different bar alignments can still locate the equivalent bars.
   *
   * rAF-throttled: scroll/zoom fires many times per frame; coalescing to
   * one publish per frame keeps peer renders sane without sacrificing
   * perceived sync responsiveness.
   */
  subscribeViewport(callback: (data: ViewportSyncData) => void): void {
    let rafId: number | null = null
    let pending: ViewportSyncData | null = null

    const handler = (range: { from: number; to: number }) => {
      const dataList = this.chart.getDataList()
      if (dataList.length === 0) return
      const fromIdx = Math.max(0, range.from)
      const toIdx = Math.min(dataList.length - 1, range.to)
      const fromTs = dataList[fromIdx]?.timestamp
      const toTs = dataList[toIdx]?.timestamp
      if (fromTs == null || toTs == null) return
      pending = { from: fromTs, to: toTs }
      if (rafId != null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        if (pending) {
          const payload = pending
          pending = null
          callback(payload)
        }
      })
    }
    this.viewportHandlers.set(callback, handler)
    this.chart.subscribeAction(ActionType.OnVisibleRangeChange, handler)
  }

  /**
   * Open times of the leftmost and rightmost visible bars, or null before bars load.
   * klinecharts' `getVisibleRange().to` is exclusive (one past the rightmost visible bar).
   */
  getVisibleTimeRange(): { from: number; to: number } | null {
    const data = this.chart.getDataList()
    if (data.length === 0) return null
    const range = this.chart.getVisibleRange()
    const last = data.length - 1
    const from = data[Math.min(last, Math.max(0, range.from))]?.timestamp
    const to = data[Math.min(last, Math.max(0, range.to - 1))]?.timestamp
    return from == null || to == null ? null : { from, to }
  }

  unsubscribeViewport(callback?: (data: ViewportSyncData) => void): void {
    if (callback) {
      const raw = this.viewportHandlers.get(callback)
      this.chart.unsubscribeAction(ActionType.OnVisibleRangeChange, raw)
      this.viewportHandlers.delete(callback)
    } else {
      this.chart.unsubscribeAction(ActionType.OnVisibleRangeChange)
    }
  }

  /**
   * Apply a broadcast date range to this chart. Delegates to
   * `setVisibleDateRange`, which clamps to the chart's actual data and
   * snaps to the nearest bar when no bars fall inside the window — the
   * "1–2 visible bars" state is the intentional "no data here" signal.
   */
  executeViewport(data: ViewportSyncData): void {
    this.setVisibleDateRange(data.from, data.to)
  }

  executeCrosshair(data: CrosshairSyncData): void {
    // Find the correct bar by timestamp (dataIndex from the source chart is
    // meaningless here — an intraday chart may have thousands of bars while
    // a daily chart has far fewer, so the same index points to a different date).
    const dataList = this.chart.getDataList()
    if (dataList.length === 0) return

    const ts = data.timestamp
    let lo = 0,
      hi = dataList.length - 1
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1
      if (dataList[mid].timestamp <= ts) lo = mid
      else hi = mid - 1
    }
    // lo = last bar with timestamp <= ts.  This is the bar that *contains*
    // the source timestamp (e.g. a 5m bar at 13:20 contains 13:20–13:24).
    // Do NOT snap to the next bar even if its timestamp is numerically
    // closer — that bar represents a different time period.

    const coord = this.chart.convertToPixel(
      { dataIndex: lo, value: data.value },
      { paneId: data.paneId },
    ) as Partial<Coordinate>
    if (coord?.x == null || coord?.y == null) return
    this.chart.executeAction(ActionType.OnCrosshairChange, {
      x: coord.x,
      y: coord.y,
      paneId: data.paneId,
    })

    // Drive this chart's readouts (OHLC header / indicator legend) from the
    // remote position. A bar only "covers" the source timestamp if it falls
    // within the bar's span — when the timestamp lands in a gap (e.g. this
    // ticker didn't trade that day) the readouts BLANK OUT (dataIndex: null)
    // rather than showing the nearest earlier bar. Emitted AFTER the
    // executeAction above so this wins over any latest-bar recompute the
    // action subscribers themselves trigger.
    const bar = dataList[lo]
    const covered = bar.timestamp <= ts && ts < bar.timestamp + (this.period.ms ?? Infinity)
    this.eventBus.emit('crosshair:remote', { dataIndex: covered ? lo : null })
  }

  exportImage(type = 'png'): string {
    return this.chart.getConvertPictureUrl(true, type)
  }

  /**
   * Choose where mouse-wheel zoom is anchored:
   *   'cursor'  — klinecharts default; the bar under the pointer stays fixed.
   *   'lastBar' — the most recent bar stays fixed ("scale on last
   *               value" feel).
   *
   * klinecharts has no config for this, so for 'lastBar' we intercept the wheel
   * in the CAPTURE phase on the container (an ancestor of klinecharts' canvas):
   * stopping propagation there prevents klinecharts' own bubble-phase wheel
   * handler from firing, and we re-issue the zoom anchored at the last data
   * index instead of the cursor. See `syncWheelInterception` for how this
   * combines with `setWheelAction`'s policy into one listener.
   */
  setZoomAnchor(anchor: 'cursor' | 'lastBar'): void {
    this.zoomAnchorState = anchor
    // Flag read by the klinecharts x-axis drag patch (see
    // patches/klinecharts@9.8.12.patch): when true, dragging the time axis pins
    // the zoom to the last visible bar instead of the click point — matching the
    // wheel anchor below so both gestures behave consistently.
    ;(this.chart as unknown as { __edgeZoomAnchorLastBar?: boolean }).__edgeZoomAnchorLastBar =
      anchor === 'lastBar'
    this.syncWheelInterception()
  }

  /**
   * Choose what a plain mouse wheel does over the chart body:
   *   'zoom' — klinecharts default (today's behavior, honoring `zoomAnchor`).
   *   'pan'  — Thinkorswim-style: wheel scrolls the time axis instead
   *            (wheel-up scrolls left / back in time).
   *
   * Ctrl/Cmd+wheel always performs the OTHER action — the escape hatch works
   * from both directions. See `syncWheelInterception`.
   */
  setWheelAction(action: 'zoom' | 'pan'): void {
    this.wheelActionState = action
    this.syncWheelInterception()
  }

  /**
   * Choose whether a ticker swap resets the horizontal zoom:
   *   false: today's behavior, the in-place symbol swap preserves zoom.
   *   true:  the swap lands on exactly what a double-click shows
   *          (`resetTimeScale`, so the reset-zoom policy for the timeframe
   *          class), on live and anchored (History) charts alike.
   * Read inside `setSymbol`, so toggling it takes effect on the next swap with
   * no listener churn. Turning it off also drops a reset still waiting for bars.
   */
  setResetZoomOnSymbolChange(reset: boolean): void {
    this.resetZoomOnSymbolChange = reset
    if (!reset) this.pendingSymbolZoomReset = false
  }

  /**
   * Choose what "Reset zoom" shows per timeframe class (spec D3/D4/D6). Read
   * live inside `resetTimeScale`, so a settings change takes effect on the next
   * reset gesture. `undefined` (no stored policy) means every class keeps
   * today's fixed reset.
   */
  setResetZoomPolicy(policy: ResetZoomPolicy | undefined): void {
    this.resetZoomPolicy = policy ?? {}
  }

  /**
   * Turns smooth motion on or off (see the `smoothMotion` field): a gliding
   * wheel zoom and Go to latest, with the price scale easing during both.
   * Turning it off stops anything still moving where it is.
   */
  setSmoothMotion(enabled: boolean): void {
    this.smoothMotion = enabled
    if (!enabled) this.stopMotion()
    this.syncWheelInterception()
    if (enabled) this.installMotionPress()
    else this.removeMotionPress()
  }

  /**
   * Installs or removes the single capture-phase wheel listener that owns
   * ALL wheel policy (zoom-anchor re-anchoring AND wheel-action routing) —
   * one handler, not two competing ones. Interception is needed whenever
   * either setting departs from the klinecharts-native default (cursor-
   * anchored zoom on plain wheel): `zoomAnchorState === 'lastBar'` or
   * `wheelActionState === 'pan'`, or when smooth motion is on (the native
   * zoom jumps). The handler itself reads `this.zoomAnchorState`
   * / `this.wheelActionState` live, so toggling either setting while already
   * intercepting needs no reinstall — only the install/remove transition does.
   */
  private syncWheelInterception(): void {
    const needed =
      this.smoothMotion || this.zoomAnchorState === 'lastBar' || this.wheelActionState === 'pan'
    if (needed && !this.wheelHandler) {
      this.wheelHandler = (e: WheelEvent) => {
        // Any wheel gesture takes over from a Go to latest glide.
        if (this.scrollGlide) this.scrollGlide = null
        // Horizontal-wheel / shift-scroll pans the time axis — leave it alone.
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
        const dataList = this.chart.getDataList()
        if (!dataList || dataList.length === 0) return
        // Ctrl/Cmd behavior is asymmetric by explicit decision (spec D6,
        // 2026-07-31): in 'pan' mode it flips to zoom (respecting the current
        // zoomAnchor); in 'zoom' mode it keeps the PRE-EXISTING cursor-anchored
        // zoom override (temporarily anchors at the pointer even when
        // zoomAnchor is 'lastBar') rather than panning — an earlier draft had
        // it pan for symmetry with the 'pan'-mode case; the user chose to
        // preserve the existing override instead.
        const ctrl = e.metaKey || e.ctrlKey
        e.preventDefault()
        e.stopImmediatePropagation()
        if (this.wheelActionState === 'pan') {
          if (ctrl) {
            this.zoomFromWheel(e, dataList)
          } else {
            this.panFromWheel(e)
          }
        } else if (ctrl) {
          this.zoomFromWheel(e, dataList, 'cursor')
        } else {
          this.zoomFromWheel(e, dataList)
        }
      }
      this.container.addEventListener('wheel', this.wheelHandler, {
        capture: true,
        passive: false,
      })
    } else if (!needed && this.wheelHandler) {
      this.container.removeEventListener('wheel', this.wheelHandler, { capture: true })
      this.wheelHandler = null
    }
  }

  /**
   * Zoom path used for: plain wheel in 'zoom' mode (respecting `zoomAnchorState`);
   * Ctrl/Cmd+wheel in 'pan' mode (also respecting `zoomAnchorState` — D6); and
   * Ctrl/Cmd+wheel in 'zoom' mode via `anchorOverride: 'cursor'` (the
   * pre-existing cursor-anchor override, which ignores `zoomAnchorState`
   * entirely — see the call site's comment). Math unchanged from the original
   * lastBar-only implementation.
   */
  private zoomFromWheel(e: WheelEvent, dataList: unknown[], anchorOverride?: 'cursor'): void {
    // Mirror klinecharts' own wheel→scale mapping (SyntheticEvent handler).
    let deltaY = -(e.deltaY / 100)
    if (e.deltaMode === 1)
      deltaY *= 32 // DOM_DELTA_LINE
    else if (e.deltaMode === 2) deltaY *= 120 // DOM_DELTA_PAGE
    const scale = Math.sign(deltaY) * Math.min(1, Math.abs(deltaY))
    if (scale === 0) return
    // Smooth motion zooms in proportion to wheel travel (a notch is a large
    // step, a trackpad stream adds up); off, it keeps klinecharts' capped step.
    const factor = this.smoothMotion
      ? wheelZoomFactor(wheelDeltaPx(e.deltaY, e.deltaMode))
      : 1 + scale / 10
    if (anchorOverride === 'cursor' || this.zoomAnchorState === 'cursor') {
      // Re-issue the zoom anchored at the pointer's x in the main pane
      // (mirrors klinecharts' native mouseWheelVertEvent).
      const rect = this.container.getBoundingClientRect()
      this.zoomBy(factor, e.clientX - rect.left)
      return
    }
    // Pin to the most recent bar — but only while it's actually on screen.
    // Once you've scrolled back so the latest bar is off the right edge,
    // anchoring there would zoom toward a point you can't see, so fall back
    // to the rightmost *visible* bar. `visibleRange.to` is EXCLUSIVE
    // (klinecharts adjustVisibleRange: one past the rightmost visible bar,
    // slice-style; it only equals a real index because it's clamped to the
    // data length at the live edge) — anchoring at `to` itself pinned an
    // off-screen bar and dragged the view rightward on every zoom step.
    const lastIndex = dataList.length - 1
    const visibleTo = this.chart.getVisibleRange().to
    const anchorIndex = Math.min(lastIndex, visibleTo - 1)
    // Anchor on the bar's RIGHT edge, not its center (which is what
    // zoomAtDataIndex uses): the newest bar's end, and the empty right margin
    // after it, stay put while the bars scale.
    const center = (
      this.chart.convertToPixel({ dataIndex: anchorIndex }, { paneId: CANDLE_PANE_ID }) as {
        x?: number
      }
    ).x
    if (center == null || !Number.isFinite(center)) return
    // zoomAtCoordinate takes a bar-space *multiplier*; klinecharts' internal
    // wheel zoom is barSpace*(1 + scale/10).
    this.zoomBy(factor, center + this.chart.getBarSpace() / 2)
  }

  /**
   * Pan path used for plain wheel in 'pan' mode (Ctrl/Cmd+wheel in 'pan' mode
   * goes through `zoomFromWheel` instead — see the dispatch above). Wheel-up
   * (negative `deltaY`) must scroll LEFT / back in time (spec D6). The sign
   * that achieves that is EMPIRICAL, not derived from klinecharts' source: a
   * live Chrome repro (2026-07-31) showed `chart.scrollByDistance(deltaY)`
   * un-negated panned FORWARD in time on wheel-up (the opposite of D6) — i.e.
   * `scrollByDistance`'s negative-distance direction is "forward in time",
   * not "back" as the ArrowLeft handler's `scroll(-3 * barSpace)` call
   * suggested by (incorrect) analogy. Negating `deltaY` here is what actually
   * produces the required direction; do not "fix" this back without a fresh
   * live check.
   */
  private panFromWheel(e: WheelEvent): void {
    let deltaY = e.deltaY
    if (e.deltaMode === 1)
      deltaY *= 32 // DOM_DELTA_LINE
    else if (e.deltaMode === 2) deltaY *= 120 // DOM_DELTA_PAGE
    if (deltaY === 0) return
    this.chart.scrollByDistance(-deltaY)
  }

  /**
   * Applies one wheel-zoom step pinned at pixel `anchorX`: instantly, or, with
   * smooth motion on, by retargeting the glide.
   *
   * The new target starts from the live bar space, so reversing direction
   * reverses on the next frame instead of first unwinding a target that ran
   * ahead. Events that land before a frame has run (`fresh`) build on the
   * pending target instead, so a burst inside one frame is not lost.
   */
  private zoomBy(factor: number, anchorX: number): void {
    if (!this.smoothMotion) {
      this.chart.zoomAtCoordinate(factor, { x: anchorX, y: 0 })
      return
    }
    const glide = this.zoomGlide
    const from = glide?.fresh ? glide.target : this.chart.getBarSpace()
    // klinecharts' setBarSpace silently ignores anything outside its limits.
    const target = clamp(from * factor, NATIVE_MIN_BAR_SPACE, NATIVE_MAX_BAR_SPACE)
    this.zoomGlide = { target, anchorX, fresh: true }
    this.ensureMotionLoop().start()
  }

  private ensureMotionLoop(): MotionLoop {
    this.motionLoop ??= new MotionLoop(
      (dt) => this.stepMotion(dt),
      this.motionClock ?? browserFrameClock,
    )
    return this.motionLoop
  }

  /** One frame of smooth motion, painted in this frame; true while anything
   *  is still moving. */
  private stepMotion(dtMs: number): boolean {
    let moving = false
    this.paintInThisFrame(() => {
      moving = this.advanceMotion(dtMs)
    })
    return moving
  }

  /**
   * Runs `apply` and paints what it changed in this frame. klinecharts
   * schedules every repaint with requestAnimationFrame, and from inside our own
   * frame callback that lands a frame late, so each glide step would show one
   * frame behind the gesture. The callbacks `apply` schedules are collected and
   * run before this returns; that includes our own frame-coalesced subscribers
   * (viewport sync, scroll tracking), which then run in the glide frame.
   */
  private paintInThisFrame(apply: () => void): void {
    const win = window as unknown as {
      requestAnimationFrame: (cb: FrameRequestCallback) => number
      cancelAnimationFrame: (id: number) => void
    }
    const realRequest = win.requestAnimationFrame
    const realCancel = win.cancelAnimationFrame
    const queued = new Map<number, FrameRequestCallback>()
    // klinecharts reads -1 as "nothing pending", so collected ids start below it.
    let nextId = -1
    win.requestAnimationFrame = (cb) => {
      nextId -= 1
      queued.set(nextId, cb)
      return nextId
    }
    win.cancelAnimationFrame = (id) => {
      if (!queued.delete(id)) realCancel?.call(window, id)
    }
    try {
      apply()
    } finally {
      // Restored first, so anything the callbacks schedule goes to a real frame.
      win.requestAnimationFrame = realRequest
      win.cancelAnimationFrame = realCancel
    }
    const now = performance.now()
    for (const cb of queued.values()) {
      // Every one must run: a skipped klinecharts repaint keeps its canvas
      // marked pending and that canvas never repaints again. Errors surface
      // outside the frame instead of stopping the glide.
      try {
        cb(now)
      } catch (err) {
        queueMicrotask(() => {
          throw err
        })
      }
    }
  }

  /** Applies one frame of every glide in progress. */
  private advanceMotion(dtMs: number): boolean {
    let moving = false
    // Read the price ranges before the view moves: klinecharts re-fits an
    // auto pane synchronously inside the zoom/scroll call below.
    const shown = this.capturePriceRanges()
    const glide = this.zoomGlide
    if (glide) {
      glide.fresh = false
      const from = this.chart.getBarSpace()
      let to = easeToward(from, glide.target, dtMs, ZOOM_GLIDE_MS)
      const landed = Math.abs(glide.target - to) <= glide.target * ZOOM_SETTLE_RATIO
      if (landed) to = glide.target
      this.chart.zoomAtCoordinate(to / from, { x: glide.anchorX, y: 0 })
      // klinecharts silently ignores a bar space it won't take; give up
      // rather than retry it every frame.
      const refused = this.chart.getBarSpace() === from
      if (landed || refused) this.zoomGlide = null
      else moving = true
    }
    const scroll = this.scrollGlide
    if (scroll) {
      const next = easeToward(scroll.remaining, 0, dtMs, SCROLL_GLIDE_MS)
      if (Math.abs(next) < 0.5) {
        // klinecharts' own call makes the landing exact, even if bars arrived
        // during the glide.
        this.scrollGlide = null
        this.chart.scrollToRealTime()
      } else {
        this.chart.scrollByDistance(scroll.remaining - next)
        scroll.remaining = next
        moving = true
      }
    }
    if (this.easePriceRanges(shown, dtMs)) moving = true
    return moving
  }

  /**
   * The price range each easable pane shows right now: auto-fitting panes plus
   * panes already being eased. A pane the user scaled by hand is left out.
   */
  private capturePriceRanges(): Map<string, YAxisRange> {
    const shown = new Map<string, YAxisRange>()
    for (const pid of this.allPaneIds()) {
      const axis = this.getYAxis(pid)
      if (!axis?.calcRange) continue
      const ours = this.priceGlides?.has(pid) ?? false
      if (!ours && axis.getAutoCalcTickFlag?.() !== true) continue
      const range = axis.getRange()
      if (range) shown.set(pid, { ...range })
    }
    return shown
  }

  /**
   * Moves each pane's shown range part of the way toward its auto-fit for the
   * current view. A pane whose fit didn't move is left on auto; one that is
   * being eased goes back to auto once it lands. Re-measures once per frame.
   */
  private easePriceRanges(shown: Map<string, YAxisRange>, dtMs: number): boolean {
    let moving = false
    let changed = false
    for (const [pid, from] of shown) {
      const axis = this.getYAxis(pid)
      const fit = axis?.calcRange?.()
      if (!axis || !fit) continue
      const span = Math.abs(fit.to - fit.from) || 1
      const nextFrom = easeToward(from.from, fit.from, dtMs, PRICE_GLIDE_MS)
      const nextTo = easeToward(from.to, fit.to, dtMs, PRICE_GLIDE_MS)
      const settled =
        Math.abs(nextFrom - fit.from) <= span * PRICE_SETTLE_RATIO &&
        Math.abs(nextTo - fit.to) <= span * PRICE_SETTLE_RATIO
      const glides = (this.priceGlides ??= new Set())
      if (settled) {
        if (glides.delete(pid)) {
          axis.setAutoCalcTickFlag?.(true)
          changed = true
        }
        continue
      }
      const realFrom = axis.convertToRealValue(nextFrom)
      const realTo = axis.convertToRealValue(nextTo)
      axis.setRange({
        from: nextFrom,
        to: nextTo,
        range: nextTo - nextFrom,
        realFrom,
        realTo,
        realRange: realTo - realFrom,
      })
      glides.add(pid)
      changed = true
      moving = true
    }
    // A pane that left the chart mid-ease, or whose range can't be read, would
    // otherwise stay pinned as manually scaled once the loop stops.
    for (const pid of this.priceGlides ?? []) {
      if (shown.has(pid)) continue
      this.getYAxis(pid)?.setAutoCalcTickFlag?.(true)
      this.priceGlides?.delete(pid)
      changed = true
    }
    if (changed) this.reMeasureViewport()
    return moving
  }

  /** Hands every eased pane straight back to auto-fit. */
  private releasePriceGlides(): void {
    const glides = this.priceGlides
    if (!glides || glides.size === 0) return
    for (const pid of glides) this.getYAxis(pid)?.setAutoCalcTickFlag?.(true)
    glides.clear()
    this.reMeasureViewport()
  }

  /** Stops any glide where it is and hands eased price scales back to
   *  auto-fit: a new press, a programmatic jump, a data swap, or smooth motion
   *  being turned off. */
  private stopMotion(): void {
    this.zoomGlide = null
    this.scrollGlide = null
    this.motionLoop?.stop()
    this.releasePriceGlides()
  }

  /** klinecharts' own scrollToRealTime distance (px, scrollByDistance's sign),
   *  read through its time-scale store; null when that isn't reachable. */
  private distanceToRealTime(): number | null {
    const store = this.internals.getChartStore?.()?.getTimeScaleStore?.()
    const diff = store?.getLastBarRightSideDiffBarCount?.()
    const resting = store?.getInitialOffsetRightDistance?.()
    if (diff == null || resting == null) return null
    const barSpace = this.chart.getBarSpace()
    return (diff - resting / barSpace) * barSpace
  }

  /** Any press stops what is gliding (see the `smoothMotion` field). */
  private installMotionPress(): void {
    if (this.motionPressHandler) return
    this.motionPressHandler = () => this.stopMotion()
    this.container.addEventListener('pointerdown', this.motionPressHandler, {
      capture: true,
      passive: true,
    })
  }

  private removeMotionPress(): void {
    if (!this.motionPressHandler) return
    this.container.removeEventListener('pointerdown', this.motionPressHandler, { capture: true })
    this.motionPressHandler = null
  }

  /**
   * Choose what a click-drag on the chart body does:
   *   'pan'        — klinecharts default (today's behavior).
   *   'zoomRegion' — Thinkorswim-style rubber-band: drag selects a time range,
   *                  releasing zooms into it. See `dragPhase`'s doc comment
   *                  above for the full gesture/interception design.
   */
  setDragAction(action: 'pan' | 'zoomRegion'): void {
    this.dragActionState = action
    this.syncDragInterception()
    // Switching to pan mustn't leave a half-drawn zoomRegion selection behind.
    // (The mousedown listener now stays installed for the ⌘+drag hotkey, so the
    // old syncDragInterception teardown-on-flip no longer fires.)
    if (action === 'pan' && this.dragPhase !== 'idle') this.teardownDragPress()
  }

  /** Installs/removes the always-on capture-phase mousedown listener that
   *  arms a drag-zoom press. Mirrors `syncWheelInterception`'s install-only-
   *  when-needed shape. */
  private syncDragInterception(): void {
    // Always installed: the mousedown handler also arms the ⌘+drag region-zoom
    // hotkey, which must work even when drag = pan. destroy() removes it directly.
    const needed = true
    if (needed && !this.dragMouseDownHandler) {
      this.dragMouseDownHandler = (e: MouseEvent) => this.handleDragMouseDown(e)
      this.container.addEventListener('mousedown', this.dragMouseDownHandler, { capture: true })
    } else if (!needed && this.dragMouseDownHandler) {
      this.container.removeEventListener('mousedown', this.dragMouseDownHandler, {
        capture: true,
      })
      this.dragMouseDownHandler = null
      // Setting flipped away mid-drag — don't leave a half-armed press behind.
      this.teardownDragPress()
    }
  }

  private handleDragMouseDown(e: MouseEvent): void {
    if (this.dragPhase !== 'idle') return
    // Region-zoom arms in zoomRegion drag-mode OR on a ⌘ (Mac) / ⌃ (PC) drag in
    // ANY mode — the global hotkey, the SAME modifier that drives ⌘/⌃+wheel
    // zoom. Alt/Shift are left to pan. Everything else pans (klinecharts owns it).
    const modZoom = (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey
    if (this.dragActionState !== 'zoomRegion' && !modZoom) return
    if (this.dragStartBlocked(e)) return
    // Swallow starting HERE, not at the 5px threshold — see the `dragPhase`
    // doc comment for why. No preventDefault(): the native click synthesis
    // for a sub-threshold press stays intact.
    e.stopImmediatePropagation()
    const rect = this.container.getBoundingClientRect()
    this.dragStartX = e.clientX - rect.left
    this.dragPhase = 'armed'
    this.dragMouseMoveHandler = (ev: MouseEvent) => this.handleDragMouseMove(ev)
    this.dragMouseUpHandler = (ev: MouseEvent) => this.handleDragMouseUp(ev)
    this.dragEscapeHandler = (ev: KeyboardEvent) => this.handleDragEscape(ev)
    window.addEventListener('mousemove', this.dragMouseMoveHandler, true)
    window.addEventListener('mouseup', this.dragMouseUpHandler, true)
    document.addEventListener('keydown', this.dragEscapeHandler, true)
  }

  private handleDragMouseMove(e: MouseEvent): void {
    e.stopImmediatePropagation()
    const rect = this.container.getBoundingClientRect()
    const x = e.clientX - rect.left
    if (this.dragPhase === 'armed') {
      if (Math.abs(x - this.dragStartX) < MIN_DRAG_PX) return
      this.dragPhase = 'selecting'
      this.showDragSelection()
    }
    this.updateDragSelection(x)
  }

  private handleDragMouseUp(e: MouseEvent): void {
    e.stopImmediatePropagation()
    const rect = this.container.getBoundingClientRect()
    const x = e.clientX - rect.left
    const wasSelecting = this.dragPhase === 'selecting'
    const startX = this.dragStartX
    this.teardownDragPress()
    if (wasSelecting) this.applyDragZoom(startX, x)
  }

  private handleDragEscape(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return
    e.preventDefault()
    this.teardownDragPress()
  }

  /** Ends the current press (any phase): removes the window/document
   *  listeners, hides the selection div, and resets to 'idle'. Safe to call
   *  when nothing is in progress. */
  private teardownDragPress(): void {
    if (this.dragMouseMoveHandler) {
      window.removeEventListener('mousemove', this.dragMouseMoveHandler, true)
      this.dragMouseMoveHandler = null
    }
    if (this.dragMouseUpHandler) {
      window.removeEventListener('mouseup', this.dragMouseUpHandler, true)
      this.dragMouseUpHandler = null
    }
    if (this.dragEscapeHandler) {
      document.removeEventListener('keydown', this.dragEscapeHandler, true)
      this.dragEscapeHandler = null
    }
    this.hideDragSelection()
    this.dragPhase = 'idle'
  }

  /**
   * Guards evaluated at mousedown — returning true means "fall through
   * untouched" (no interception; klinecharts' native pan/click/overlay
   * handling proceeds exactly as if zoomRegion weren't active).
   */
  private dragStartBlocked(e: MouseEvent): boolean {
    // A drawing tool is armed — its own draw-start handling owns this press.
    if (this.pendingDrawId !== null) return true
    // Left button only; alt/shift block. ⌘/⌃ are ALLOWED — they're the
    // region-zoom hotkey (handleDragMouseDown decides when the modifier arms).
    if (e.button !== 0 || e.altKey || e.shiftKey) return true
    // Over an overlay's hit-target, or an overlay drag is already in
    // progress — the overlay owns the gesture (move/resize/select), not us.
    if (this.isOverlayInteractionActive()) return true
    // Outside the candle pane's OWN plot area (a y-axis strip, the x-axis
    // band, or a sub-pane) — region-zoom only starts from the primary price
    // pane; axis drags keep their native scale behavior.
    const rect = this.container.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    if (!this.isInCandlePanePlotArea(x, y)) return true
    return false
  }

  /** True while the pointer is over an overlay's hit-target (hover) or an
   *  overlay is mid-drag (pressed) — the same overlay-store reads
   *  `deselectOverlay`/the label-visibility resolver above already use. */
  private isOverlayInteractionActive(): boolean {
    const store = (
      this.chart as unknown as {
        getChartStore?: () => {
          getOverlayStore?: () => {
            getHoverInstanceInfo: () => { instance: { id: string } | null; figureType: number }
            getPressedInstanceInfo: () => { instance: { id: string } | null }
          }
        }
      }
    )
      .getChartStore?.()
      ?.getOverlayStore?.()
    if (!store) return false
    const hover = store.getHoverInstanceInfo()
    if (hover.instance !== null && hover.figureType !== 0) return true
    return store.getPressedInstanceInfo().instance !== null
  }

  /** Container-relative point is within the CANDLE pane's own Main bounding
   *  box (not a y-axis strip, not a sub-pane). Mirrors `paneIdAtYAxis`'s
   *  bounding-box style below. */
  private isInCandlePanePlotArea(x: number, y: number): boolean {
    const b = this.chart.getSize(CANDLE_PANE_ID, DomPosition.Main)
    if (!b) return false
    return x >= b.left && x <= b.left + b.width && y >= b.top && y <= b.top + b.height
  }

  private ensureDragSelectionEl(): HTMLDivElement {
    if (!this.dragSelectionEl) {
      const el = document.createElement('div')
      el.setAttribute('data-testid', 'rubber-band-selection')
      el.style.position = 'absolute'
      el.style.top = '0'
      el.style.bottom = '0'
      el.style.pointerEvents = 'none'
      el.style.zIndex = '5'
      el.style.background = 'hsl(var(--primary) / 0.12)'
      this.dragSelectionEl = el
    }
    return this.dragSelectionEl
  }

  private showDragSelection(): void {
    // The selection div positions absolutely against `this.container` — make
    // sure it's actually a positioning context (it isn't, by default; only
    // klinecharts' own inner container is).
    const computedPosition = getComputedStyle(this.container).position
    if (computedPosition === 'static' || computedPosition === '') {
      this.container.style.position = 'relative'
    }
    const el = this.ensureDragSelectionEl()
    // `parentNode`, not `isConnected` — `isConnected` requires attachment all
    // the way up to the live `document`, which is an unrelated concern from
    // "is this div parented under our container" (and false in some detached
    // test/portal setups even when the append below already succeeded once).
    if (el.parentNode !== this.container) this.container.appendChild(el)
    this.updateDragSelection(this.dragStartX)
  }

  private updateDragSelection(currentX: number): void {
    const el = this.dragSelectionEl
    if (!el) return
    const left = Math.min(this.dragStartX, currentX)
    const width = Math.abs(currentX - this.dragStartX)
    el.style.left = `${left}px`
    el.style.width = `${width}px`
  }

  private hideDragSelection(): void {
    if (this.dragSelectionEl?.parentNode) this.dragSelectionEl.remove()
  }

  /**
   * Apply the rubber-band zoom on a valid release. `x0`/`x1` are the
   * container-relative selection edges (unordered).
   */
  private applyDragZoom(x0: number, x1: number): void {
    const pane = this.chart.getSize(CANDLE_PANE_ID, DomPosition.Main)
    if (!pane) return
    const currentBarSpace = this.chart.getBarSpace()
    const result = computeRubberBandZoom({
      x0,
      x1,
      paneWidth: pane.width,
      currentBarSpace,
      minBarSpace: NATIVE_MIN_BAR_SPACE,
      maxBarSpace: NATIVE_MAX_BAR_SPACE,
    })
    if (!result.valid) return
    // Capture the RIGHT edge as a timestamp BEFORE zooming: convertFromPixel
    // resolves against the CURRENT bar space/visible range, which
    // zoomAtCoordinate is about to change — converting after the fact would
    // resolve the wrong bar.
    const rightEdgeX = Math.max(x0, x1)
    const converted = this.chart.convertFromPixel([{ x: rightEdgeX }], {
      paneId: CANDLE_PANE_ID,
    }) as Array<{ timestamp?: number }>
    let t1 = converted[0]?.timestamp
    if (t1 == null) {
      // The right edge landed in the empty gap PAST the last bar (klinecharts'
      // offsetRightDistance), so convertFromPixel resolves an index with no data
      // → null. This happens EVERY time the drag reaches the chart's end. Pin the
      // LAST real bar flush to the right edge (matching a drag that stops just
      // before the end) instead of skipping the scroll below — skipping strands
      // the newest bars ~offsetRightDistance in from the right (the "~30 bars off
      // the right" drop).
      const dataList = this.chart.getDataList()
      t1 = dataList[dataList.length - 1]?.timestamp
    }
    this.chart.zoomAtCoordinate(result.scale, { x: result.anchorX, y: 0 }, 0)
    // Still null only when the dataset is genuinely empty — nothing to pin to.
    if (t1 == null) return
    // Raw klinecharts call, NOT the `scrollToTimestamp` wrapper below — that
    // wrapper's 5-bar anchor nudge exists for anchored history/focus-date
    // loads. A rubber-band release always wants the selection's right edge
    // pinned EXACTLY at the pane's right edge, which `scrollToTimestamp`
    // alone already does (traced through klinecharts' `scrollToDataIndex` →
    // `scroll`: the resulting `lastBarRightSideDiffBarCount` places t1's bar
    // exactly at the pane's right edge, zero margin — flush).
    //
    // Do NOT also call `setOffsetRightDistance(0)` here (a real bug, caught
    // live 2026-07-31): its `0` is NOT "zero offset from where we just
    // scrolled to" — the public API sets the offset relative to the LATEST
    // bar in the dataset, unconditionally, discarding whatever
    // `scrollToTimestamp` just did. That's what caused the live-verified
    // "window snaps to the most recent data instead of the dragged range" bug.
    this.chart.scrollToTimestamp(t1, 0)
  }

  /**
   * Control what the candle pane's price (y) axis auto-fit includes.
   *   - 'indicators' (default): candles + every on-pane indicator figure
   *     (klinecharts' native behavior).
   *   - 'candles': candle high/low only — a far-off overlay (distant EMA, a
   *     pivot level) no longer squashes the scale.
   *
   * Sets a flag read by the klinecharts YAxisImp.calcRange patch (see
   * patches/klinecharts@9.8.12.patch) which, for the CANDLE pane only, skips the
   * indicator-figure min/max contribution when the flag is true. Sub-panes are
   * indicator-only and always fit their own indicators, so they're untouched.
   * After flipping the flag we release the candle axis back to auto-fit and
   * force a viewport re-measure so the new range applies immediately.
   */
  /** klinecharts' internal (non-public-typed) chart API surface — the single
   *  `as unknown as ChartInternals` cast shared by the price-axis helpers. */
  private get internals(): ChartInternals {
    return this.chart as unknown as ChartInternals
  }

  /** The klinecharts y-axis component for a pane (via internal APIs), or
   *  undefined before the pane exists. Wraps the draw-pane lookup shared by the
   *  price-axis scale / reset / auto methods. */
  private getYAxis(paneId: string): YAxisComponent | undefined {
    return this.internals.getDrawPaneById?.(paneId)?.getAxisComponent?.()
  }

  /** Force klinecharts to re-measure pane viewports and re-fit the y-axis — the
   *  `adjustPaneViewport(false, true, true, true)` call shared after a scale
   *  change. */
  private reMeasureViewport(): void {
    this.internals.adjustPaneViewport?.(false, true, true, true)
  }

  /** Every pane id (candle + unique sub-panes), deduped — a sub-pane can host
   *  several indicators, so `subPaneIds.values()` may repeat. */
  private allPaneIds(): string[] {
    return [CANDLE_PANE_ID, ...new Set(this.subPaneIds.values())]
  }

  setPriceAxisScope(scope: 'candles' | 'indicators'): void {
    ;(
      this.chart as unknown as { __edgePriceAxisCandlesOnly?: boolean }
    ).__edgePriceAxisCandlesOnly = scope === 'candles'
    // Release any manual scale so the new scope's auto-fit range takes effect
    // (toggling the setting is a deliberate re-fit), then re-measure.
    this.priceGlides?.delete(CANDLE_PANE_ID)
    this.getYAxis(CANDLE_PANE_ID)?.setAutoCalcTickFlag?.(true)
    this.reMeasureViewport()
  }

  /**
   * Price (y) axis scale style: type (normal / percentage / log), inversion,
   * and which side it renders on. Maps straight to klinecharts `yAxis`. Log
   * requires positive values — equities are, so no guard needed.
   */
  setPriceScaleStyle(opts: {
    type: 'normal' | 'percentage' | 'log'
    reverse: boolean
    position: 'left' | 'right'
  }): void {
    this.chart.setStyles({
      yAxis: { type: opts.type, position: opts.position, reverse: opts.reverse },
    } as Parameters<typeof this.chart.setStyles>[0])
  }

  /** The candle-pane price at a viewport y, rounded to the chart's price precision, for the
   *  "Add alert at $X" menu rows. Null off the candle pane or at a price of 0 or below. */
  alertPriceAt(clientY: number): { price: number; text: string } | null {
    const pane = this.chart.getSize(CANDLE_PANE_ID, DomPosition.Main)
    const y = clientY - this.container.getBoundingClientRect().top
    if (!pane || y < pane.top || y > pane.top + pane.height) return null
    const point = this.chart.convertFromPixel([{ y }], { paneId: CANDLE_PANE_ID, absolute: true })
    const pt = Array.isArray(point) ? point[0] : point
    return alertPrice(pt?.value, this.chart.getPriceVolumePrecision().price)
  }

  /** The pane whose price-axis strip a VIEWPORT point (clientX/Y) is over, or
   *  null — public wrapper over `paneIdAtYAxis` for a right-click handler. */
  priceAxisPaneAt(clientX: number, clientY: number): string | null {
    const rect = this.container.getBoundingClientRect()
    return this.paneIdAtYAxis(clientX - rect.left, clientY - rect.top)
  }

  /** Release a pane's price axis back to auto-fit — the "Auto" menu action. */
  resetPriceAxis(paneId = CANDLE_PANE_ID): void {
    this.resetYAxis(paneId)
  }

  scrollToRealTime(): void {
    this.stopMotion()
    this.chart.scrollToRealTime()
  }

  /**
   * The scroll-to-latest button: back to the current bar AND the current price.
   * Scrolling alone kept any manual price-axis drag or wheel scale, so the
   * latest bar could land off-screen vertically and the user had to hunt for
   * the price. Releases every pane to auto-fit (as the body double-click does)
   * but keeps the zoom level; rescaling time stays with double-click / Reset
   * zoom.
   *
   * With smooth motion on it glides there instead of jumping, and every pane,
   * including one scaled by hand, eases back to auto-fit on the way.
   */
  goToLatest(): void {
    this.stopMotion()
    const distance = this.smoothMotion ? this.distanceToRealTime() : null
    if (distance == null) {
      for (const pid of this.allPaneIds()) this.resetYAxis(pid)
      this.chart.scrollToRealTime()
      return
    }
    for (const pid of this.allPaneIds()) {
      if (this.getYAxis(pid)?.getAutoCalcTickFlag?.() === false) {
        ;(this.priceGlides ??= new Set()).add(pid)
      }
    }
    if (Math.abs(distance) >= 1) this.scrollGlide = { remaining: distance }
    else this.chart.scrollToRealTime()
    this.ensureMotionLoop().start()
  }

  /** Timestamp of the bar currently under the cursor (from the tracked
   *  crosshair), or null when the cursor isn't over a bar. The body context menu
   *  aligns this to the trading day for its "…this day" actions — the SAME source
   *  the Alt+click intraday drill reads (so both resolve the identical day). */
  cursorBarTimestamp(): number | null {
    return this.lastCursor?.timestamp ?? null
  }

  /** The pane whose price (y) axis strip the container-relative point is over,
   *  or null. Checks every pane's y-axis bounding (right-hand strip).
   *
   *  NB: klinecharts only populates `top/height/left/width` on a widget's
   *  bounding — `right`/`bottom` stay 0 — so the far edges are derived here. */
  private paneIdAtYAxis(x: number, y: number): string | null {
    for (const pid of this.allPaneIds()) {
      const b = this.chart.getSize(pid, DomPosition.YAxis)
      if (b && x >= b.left && x <= b.left + b.width && y >= b.top && y <= b.top + b.height) {
        return pid
      }
    }
    return null
  }

  /** Zoom `paneId`'s price axis by `factor` (<1 zoom in / >1 zoom out), anchored
   *  around the axis center. Mirrors klinecharts' own y-axis drag-scale math via
   *  its internal pane axis API. Returns false when the axis can't be scaled
   *  (missing API, scroll-zoom disabled, or no current range). */
  private scaleYAxis(paneId: string, factor: number): boolean {
    // The user takes the scale over from an ease in progress.
    this.priceGlides?.delete(paneId)
    const yAxis = this.getYAxis(paneId)
    if (!yAxis || typeof yAxis.getRange !== 'function' || typeof yAxis.setRange !== 'function') {
      return false
    }
    if (yAxis.getScrollZoomEnabled && !yAxis.getScrollZoomEnabled()) return false
    const range = yAxis.getRange()
    if (!range) return false
    const span = range.to - range.from
    if (!Number.isFinite(span) || span === 0) return false
    const difRange = (span * factor - span) / 2
    const newFrom = range.from - difRange
    const newTo = range.to + difRange
    const realFrom = yAxis.convertToRealValue(newFrom)
    const realTo = yAxis.convertToRealValue(newTo)
    yAxis.setRange({
      from: newFrom,
      to: newTo,
      range: newTo - newFrom,
      realFrom,
      realTo,
      realRange: realTo - realFrom,
    })
    this.reMeasureViewport()
    // `setRange` above already flips klinecharts' auto-calc flag to false, so the
    // "Auto" menu reads not-auto with no extra bookkeeping here.
    // A wheel zoom that brings an alert into view re-tiers at once. Charts without alert
    // lines keep their tier until the next data update, as before.
    if (this.priceAlertOverlayIds?.size) this.updateAxisWidth()
    return true
  }

  /** Release `paneId`'s price axis from a manual scale back to auto-fit. */
  private resetYAxis(paneId: string): void {
    this.priceGlides?.delete(paneId)
    const yAxis = this.getYAxis(paneId)
    if (yAxis?.setAutoCalcTickFlag) {
      yAxis.setAutoCalcTickFlag(true)
      this.reMeasureViewport()
    }
  }

  /** Whether the price (candle-pane) axis is currently auto-fitting (vs a manual
   *  drag/wheel scale) — the right-click "Auto" active state. Reads klinecharts'
   *  real auto-calc flag so it reflects EVERY path that pins the axis: the native
   *  y-axis drag (setRange → flag false), our wheel zoom, "Auto" reset, and the
   *  axis double-click. A local mirror drifted because the native drag bypasses
   *  our code entirely — the menu then read stale "auto" after a drag. */
  isPriceAxisAuto(paneId = CANDLE_PANE_ID): boolean {
    // A pane being eased is pinned only for the glide; it is still auto-fit.
    if (this.priceGlides?.has(paneId)) return true
    const yAxis = this.getYAxis(paneId)
    if (yAxis?.getAutoCalcTickFlag) return yAxis.getAutoCalcTickFlag()
    return true // no axis yet → default to auto
  }

  /** True when `containerY` (px from the chart's top edge) is in the time-axis
   *  strip below every pane's content. */
  private isInTimeAxisBand(containerY: number): boolean {
    let panesBottom = 0
    for (const pid of this.allPaneIds()) {
      const b = this.chart.getSize(pid, DomPosition.Main)
      // `bottom` is never populated by klinecharts (stays 0) — derive the pane's
      // bottom edge from top + height, or the time-axis band is never detected.
      if (b) panesBottom = Math.max(panesBottom, b.top + b.height)
    }
    return panesBottom > 0 && containerY >= panesBottom
  }

  /**
   * Reset the horizontal zoom per the user's reset-zoom policy for this
   * period's timeframe class. A range rule (anchor / daysBack / barsBack)
   * frames the resolved window with the latest loaded bar on the right edge.
   * A level rule, or no rule at all, restores the default bar space scaled by
   * the level and scrolls back to the latest bar. That level path is exactly
   * today's reset when the rule is absent or `standard`, and it is also the
   * fallback for a range rule with no loaded bars.
   */
  resetTimeScale(): void {
    this.stopMotion()
    const rule = this.resetZoomPolicy[zoomCategoryOf(this.period)]
    if (rule && rule.kind !== 'level') {
      const range = resolveResetRange(this.chart.getDataList(), rule)
      if (range) {
        this.setVisibleDateRange(range.from, range.to, false)
        this.markChosenBarSpace()
        return
      }
    }
    const level = rule?.kind === 'level' ? rule.level : 'standard'
    const barSpace = this.defaultBarSpace * ZOOM_LEVEL_MULTIPLIER[level]
    this.chart.setBarSpace(barSpace)
    this.markChosenBarSpace()
    this.chart.setOffsetRightDistance(this.rightMarginBars * barSpace)
    this.chart.scrollToRealTime()
  }

  /**
   * "Reset zoom" — the body context menu's escape hatch from a rubber-band
   * zoom (drag-to-zoom removes the obvious way back out; this is the way
   * back). Identical to the existing time-axis double-click reset above —
   * kept as a single behavior under two public names (one entry point per
   * gesture) rather than duplicating the restore logic.
   */
  resetZoom(): void {
    this.resetTimeScale()
  }

  scrollToTimestamp(timestamp: number, animationDuration?: number): void {
    this.stopMotion()
    const dataList = this.chart.getDataList()
    if (!dataList || dataList.length === 0) return
    this.chart.scrollToTimestamp(timestamp, animationDuration)
    // Push the anchor bar 5 bars from the right edge so it's clearly visible
    if (this.dataLayer.getAnchorTimestamp() != null) {
      const barSpace = this.chart.getBarSpace()
      this.chart.scrollByDistance(-5 * barSpace, animationDuration)
    }
  }

  getBarSpace(): number {
    return this.chart.getBarSpace()
  }

  setPaneHeight(paneId: string, height: number, minHeight?: number): void {
    const rounded = Math.round(height)
    const options: { id: string; height: number; minHeight?: number } = {
      id: paneId,
      height: rounded,
    }
    if (minHeight !== undefined) options.minHeight = Math.round(minHeight)
    this.chart.setPaneOptions(options)
    // An explicit pane height is a height-only relayout, see notifyLayoutChanged.
    this.notifyLayoutChanged()
    // Record the new ratio so this height survives resize(): resize() re-applies
    // every sub-pane height from subPaneRatios, so without this an explicit
    // setPaneHeight (e.g. collapsing a disabled debug pane to legend height) is
    // clobbered back to its stale pre-set ratio on the next container resize —
    // which is exactly what a grid-layout change triggers.
    const total = this.container.clientHeight
    if (paneId !== CANDLE_PANE_ID && total > 0) {
      this.subPaneRatios.set(paneId, rounded / total)
    }
  }

  resize(): void {
    // A mosaic drag transiently collapses the container to a sliver; letting
    // klinecharts lay out at that size freezes the y-axis at a garbage range
    // computed from the ~1 visible bar (and clamps away the right-edge gap).
    // Skip layout entirely while collapsed: the chart carries its last sane
    // axis through the drag and lays out ONCE at the drop size — preventing
    // the corruption beats healing it (no post-drop correction jump).
    // Threshold: comfortably below any usable chart, above drag slivers.
    if (
      this.container.clientWidth < MIN_LAYOUT_SIZE_PX ||
      this.container.clientHeight < MIN_LAYOUT_SIZE_PX
    ) {
      return
    }
    // Set sub-pane heights before resize so klinecharts factors them
    // into its layout calculation (mirrors how constructor works)
    const newHeight = this.container.clientHeight
    if (newHeight > 0 && this.subPaneRatios.size > 0) {
      for (const [paneId, ratio] of this.subPaneRatios) {
        this.chart.setPaneOptions({
          id: paneId,
          height: Math.round(newHeight * ratio),
        })
      }
    }
    this.chart.resize()
    this.notifyLayoutChanged()
  }

  /**
   * Post-storm settle pass, fired by `useChartResize`'s trailing debounce
   * once a resize storm (mosaic drag / splitter drag / window resize) goes
   * quiet. Two heals, both verified against a live repro (2026-07-26):
   *
   * 1. A mosaic drag transiently COLLAPSES the container; klinecharts
   *    recomputes the y-axis while ~1 bar is visible, and the final
   *    expand-to-drop-size redraw does NOT re-run the axis calcRange — the
   *    price axis stays frozen at the collapsed-state range (candles clipped
   *    to a sliver). One more `resize()` at the settled size recomputes it.
   * 2. The collapsed clamp can consume the right-edge gap
   *    (`lastBarRightSideDiffBarCount` 3 → ≤0). When the chart still sits at
   *    the live right edge with its gap eaten, re-assert the resting offset.
   *    A chart the user scrolled back into history is left alone — resetting
   *    the offset there would yank them to the latest bar.
   */
  settleResize(): void {
    this.resize()
    const len = this.chart.getDataList().length
    if (len === 0) return
    const barSpace = this.chart.getBarSpace()
    const resting = this.rightMarginBars * barSpace
    // getOffsetRightDistance exists on klinecharts 9.x but not on our Chart
    // type surface — access defensively so a lib bump can't crash the settle.
    const getOffset = (this.chart as unknown as { getOffsetRightDistance?: () => number })
      .getOffsetRightDistance
    const offset = typeof getOffset === 'function' ? getOffset.call(this.chart) : null
    const { to } = this.chart.getVisibleRange()
    const atLiveEdge = to >= len - 1
    if (atLiveEdge && offset != null && offset < resting) {
      this.chart.setOffsetRightDistance(resting)
    }
  }

  destroy(): void {
    this.barCountdownId = null
    this.priceAlertOverlayIds = null
    if (this.autoAnalysisTimer) {
      clearTimeout(this.autoAnalysisTimer)
      this.autoAnalysisTimer = null
    }
    this.measureTeardownCleanup?.()
    // All document/window listeners (outside-deselect, Alt/Shift trackers,
    // drawing hotkeys, Escape draw-cancel) route through the shared hub — one
    // unregister apiece here replaces the old removeEventListener calls.
    this.globalListenerUnsubs?.splice(0).forEach((unsub) => unsub())
    this.outsideDeselectHandler = null
    this.altKeyDownHandler = null
    this.altKeyUpHandler = null
    this.altBlurHandler = null
    this.shiftMeasureHandler = null
    this.shiftMeasureUpHandler = null
    this.measureTypingCancelHandler = null
    this.hotkeyHandler = null
    this.escapeDrawCancelHandler = null
    if (this.keydownHandler) {
      this.container.removeEventListener('keydown', this.keydownHandler)
      this.keydownHandler = null
    }
    if (this.wheelHandler) {
      this.container.removeEventListener('wheel', this.wheelHandler, { capture: true })
      this.wheelHandler = null
    }
    if (this.dragMouseDownHandler) {
      this.container.removeEventListener('mousedown', this.dragMouseDownHandler, {
        capture: true,
      })
      this.dragMouseDownHandler = null
    }
    this.teardownDragPress()
    this.stopMotion()
    this.removeMotionPress()
    if (this.yAxisWheelHandler) {
      this.container.removeEventListener('wheel', this.yAxisWheelHandler, { capture: true })
      this.yAxisWheelHandler = null
    }
    if (this.dblclickHandler) {
      this.container.removeEventListener('dblclick', this.dblclickHandler)
      this.dblclickHandler = null
    }
    if (this.axisAlertPressHandler) {
      this.container.removeEventListener('mousedown', this.axisAlertPressHandler, {
        capture: true,
      })
      this.axisAlertPressHandler = null
    }
    this.axisAlertPress = null
    if (this.pointerEnterHandler) {
      this.container.removeEventListener('pointerenter', this.pointerEnterHandler)
      this.pointerEnterHandler = null
    }
    if (this.pointerLeaveHandler) {
      this.container.removeEventListener('pointerleave', this.pointerLeaveHandler)
      this.pointerLeaveHandler = null
    }
    if (this.cursorTrackHandler) {
      this.chart.unsubscribeAction(ActionType.OnCrosshairChange, this.cursorTrackHandler)
      this.cursorTrackHandler = null
    }
    if (this.altClickHandler) {
      this.container.removeEventListener('click', this.altClickHandler)
      this.altClickHandler = null
    }
    this.clearBarHighlight()
    this.lastCursor = null
    if (this.unregisterMeasureVolumeResolver) {
      this.unregisterMeasureVolumeResolver()
      this.unregisterMeasureVolumeResolver = null
    }
    if (this.cursorMoveHandler) {
      this.container.removeEventListener('mousemove', this.cursorMoveHandler)
      this.container.removeEventListener('mouseleave', this.cursorMoveHandler)
      this.cursorMoveHandler = null
      if (this.cursorUpHandler) {
        this.container.removeEventListener('mouseup', this.cursorUpHandler)
        this.cursorUpHandler = null
      }
      this.applyDrawingCursor(null)
    }
    if (this.unregisterLabelResolver) {
      this.unregisterLabelResolver()
      this.unregisterLabelResolver = null
    }
    if (this.unregisterPlanSelectionResolver) {
      this.unregisterPlanSelectionResolver()
      this.unregisterPlanSelectionResolver = null
    }
    this.selectedOverlayId = null
    this.overlaySyncCallback = null
    this.localToSyncId.clear()
    this.syncToLocalId.clear()
    this.replay?.destroy()
    this.replay = null
    // Optional: harnesses that build a manager via Object.create skip fields.
    this.htfSeries?.dispose()
    this.mtfSessionInstances?.clear()
    this.dataLayer.destroy()
    this.eventBus.emit('chart:destroyed', undefined)
    this.eventBus.clear()
    dispose(this.chart)
  }
}
